import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type CSSProperties, type Dispatch, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type Ref, type SetStateAction } from "react";
import { create } from "zustand";
import {
  boundsForRect,
  boundsForItems,
  fitBounds,
  ItemSpatialIndex,
  panBy,
  screenToWorld,
  viewportWorldBounds,
  worldToScreen,
  zoomAt,
  type Point,
  type Viewport
} from "@bedrock/canvas";
import {
  isLockedFolder,
  isLockedItem,
  isSealedItem,
  makeId,
  markdownToPlainText,
  sealLockedProps,
  tickClock,
  type AccountStorageUsage,
  type CanvasItem,
  type DateEventColor,
  type DocumentColor,
  type DesktopUpdateInfo,
  type DesktopUpdateState,
  type FolderColor,
  type HybridClock,
  type ItemProps,
  type ItemType,
  type NoteLock,
  type Space,
  type StickyColor,
  type SyncOperation,
  type SyncTransportError,
  type WorkingSession
} from "@bedrock/domain";
import { EDITOR_TOOLBAR_SCROLL_CLEARANCE, MarkdownEditor, MarkdownPreview, type MarkdownEditorHandle, type MarkdownEditorShortcutAction, type MarkdownEditorShortcuts } from "@bedrock/editor";
import { initialSyncStatus, normalizeSearchText, SyncRequestError, type LocalStore, type RecoveredVersion, type SearchResult, type SyncStatus } from "@bedrock/local-store";
import { fixtureItems, fixtureSpaces } from "@bedrock/test-fixtures";
import { Button, FloatingPanel, Icon, IconButton, Modal, StrataMark, Tooltip, type IconName } from "@bedrock/ui";
import { AssetCopyButton } from "./asset-copy-button";
import { downloadTextFile, textDownloadForItem } from "./text-download";
import { canStartCanvasItemDrag, canvasDragItems, canvasFolderDropIntent, includeFrameMembershipChanges, moveCanvasDragItems, reconcileFrameMembership, type CanvasFolderDropIntent } from "./canvas-drag";
import { ARROW_COLORS, arrowColorLabel, FRAME_BORDER_COLORS, FRAME_COLORS, frameBorderColorLabel, frameColorLabel } from "./canvas-element-appearance";
import { arrowGeometry, arrowHeadPoints, CanvasArrow, CanvasFrame, CanvasFrameLabel } from "./canvas-elements";
import { canvasRotationPointerAngle, rotateCanvasFrameItems, updateCanvasRotation } from "./canvas-rotation";
import { canvasAssetId, withoutCanvasAssetUrl, withoutStaleCanvasObjectUrl, withCanvasAssetUrl } from "./asset-hydration";
import { ASSET_FILE_ACCEPT, canvasAssetPropsForFile, IMPORTABLE_FILE_ACCEPT } from "./canvas-asset-file";
import { canvasSizeForImageFile } from "./canvas-image-size";
import { CanvasMedia } from "./canvas-media";
import { loadCanvasControlScheme, saveCanvasControlScheme, type CanvasControlScheme } from "./canvas-controls";
import { hasSameCanvasLayout } from "./canvas-preview";
import { PdfViewer } from "./pdf-viewer";
import { SpreadsheetViewer } from "./spreadsheet-viewer";
import { defaultCanvasItemSize, fitCanvasFrameToContents, resetCanvasItemSize, resizeCanvasItemFromScreenDelta } from "./canvas-resize";
import {
  CANVAS_SHORTCUT_ACTIONS,
  SYSTEM_CLIPBOARD_PASTE_ACTION,
  canvasShortcutAccelerators,
  canvasShortcutAria,
  canvasShortcutBindingFromEvent,
  canvasShortcutConflict,
  canvasShortcutForEvent,
  canvasShortcutLabel,
  canvasShortcutMenuProps,
  defaultCanvasShortcutBindings,
  isSystemClipboardPasteBinding,
  isSystemClipboardPasteShortcut,
  loadCanvasShortcutBindings,
  saveCanvasShortcutBindings,
  systemClipboardPasteShortcutAria,
  systemClipboardPasteShortcutLabel,
  type CanvasShortcutAccelerators,
  type CanvasShortcutBinding,
  type CanvasShortcutBindings,
  type CanvasShortcutId
} from "./canvas-shortcuts";
import { ContextMenu, ContextMenuDivider, ContextMenuHeader, ContextMenuItem } from "./context-menu";
import { classifyClipboardPaste, classifySystemClipboardContents, type ClipboardPastePayload, type SystemClipboardContents } from "./clipboard-paste";
import { CanvasDateEvent, DateEventForm } from "./date-event";
import { DATE_EVENT_COLORS, dateEventColorLabel } from "./date-event-appearance";
import { exportLibrary } from "./export";
import { lockNote, relockNote, unlockNote, type UnlockedNote } from "./note-lock";
import { useFolderLocks, WRONG_PIN_MESSAGE, type FolderLocks } from "./folder-locks";
import { NoteLockDialog } from "./note-lock-dialog";
import { useExitAnimation } from "./exit-animation";
import { DOCUMENT_COLORS, documentColorLabel } from "./document-appearance";
import { loadDocumentColorInEditor, saveDocumentColorInEditor } from "./editor-preferences";
import { FOLDER_COLORS, folderColorLabel } from "./folder-appearance";
import { clampImagePreviewOffset, imagePreviewZoomFromWheel, type ImagePreviewOffset } from "./image-preview-zoom";
import { folderStatusText } from "./folder-status";
import { isStackFocusTarget, registerStackFocusDismissal, viewportForStackFocus } from "./stack-focus";
import { arrangeStackChildren } from "./stack-layout";
import { STACK_COLLAPSE_DURATION_MS, STACK_REVEAL_DURATION_MS, orderedStackChildren, stackCollapseDelay, stackCollapseTotalDuration, stackRevealDelay, stackRevealOrigin, stackRevealTotalDuration } from "./stack-reveal";
import { STICKY_COLORS, stickyColorLabel } from "./sticky-appearance";
import { itemTransitionName, runViewTransition } from "./view-transitions";
import { formatVideoTime, VideoPlayer } from "./video-player";
import { CanvasWebClip, WebClipForm } from "./web-clip";
import { MediaPalette } from "./media-palette";
import { loadStartupMode, saveStartupMode, useWorkingSession, type StartupMode, type WorkingSessionCloud } from "./working-session";
import type { CopyFileToClipboard } from "./asset-clipboard";
import "@bedrock/ui/styles.css";
import "./styles.css";

type Screen = { kind: "library" } | { kind: "space"; spaceId: string };

interface ShellState {
  screen: Screen;
  startupMode: StartupMode;
  setStartupMode: (mode: StartupMode) => void;
  selectedIds: string[];
  theme: "light" | "dark";
  controlScheme: CanvasControlScheme;
  documentColorInEditor: boolean;
  searchOpen: boolean;
  settingsOpen: boolean;
  addOpen: boolean;
  editorItemId: string | null;
  selectEditorTitle: boolean;
  searchTargetItemId: string | null;
  syncState: SyncStatus;
  shortcutBindings: CanvasShortcutBindings;
  setScreen: (screen: Screen) => void;
  setSelectedIds: (ids: string[]) => void;
  setTheme: (theme: "light" | "dark") => void;
  setControlScheme: (scheme: CanvasControlScheme) => void;
  setDocumentColorInEditor: (enabled: boolean) => void;
  setSearchOpen: (open: boolean) => void;
  setSettingsOpen: (open: boolean) => void;
  setAddOpen: (open: boolean) => void;
  setEditorItemId: (id: string | null, selectTitle?: boolean) => void;
  setSearchTargetItemId: (id: string | null) => void;
  setSyncState: (state: SyncStatus) => void;
  setShortcutBinding: (id: CanvasShortcutId, binding: CanvasShortcutBinding) => void;
  resetShortcutBindings: () => void;
}

const initialTheme = (): "light" | "dark" => {
  if (typeof localStorage !== "undefined") {
    const saved = localStorage.getItem("bedrock-theme");
    if (saved === "light" || saved === "dark") return saved;
  }
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const useShell = create<ShellState>((set) => ({
  screen: { kind: "library" },
  startupMode: loadStartupMode(),
  setStartupMode: (startupMode) => { saveStartupMode(startupMode); set({ startupMode }); },
  selectedIds: [],
  theme: initialTheme(),
  controlScheme: loadCanvasControlScheme(),
  documentColorInEditor: loadDocumentColorInEditor(),
  searchOpen: false,
  settingsOpen: false,
  addOpen: false,
  editorItemId: null,
  selectEditorTitle: false,
  searchTargetItemId: null,
  syncState: initialSyncStatus,
  shortcutBindings: loadCanvasShortcutBindings(),
  setScreen: (screen) => set({ screen, selectedIds: [], addOpen: false, editorItemId: null, selectEditorTitle: false }),
  setSelectedIds: (selectedIds) => set({ selectedIds }),
  setTheme: (theme) => set({ theme }),
  setControlScheme: (controlScheme) => {
    saveCanvasControlScheme(controlScheme);
    set({ controlScheme });
  },
  setDocumentColorInEditor: (documentColorInEditor) => {
    saveDocumentColorInEditor(documentColorInEditor);
    set({ documentColorInEditor });
  },
  setSearchOpen: (searchOpen) => set({ searchOpen }),
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),
  setAddOpen: (addOpen) => set({ addOpen }),
  setEditorItemId: (editorItemId, selectEditorTitle = false) => set({ editorItemId, selectEditorTitle }),
  setSearchTargetItemId: (searchTargetItemId) => set({ searchTargetItemId }),
  setSyncState: (syncState) => set({ syncState }),
  setShortcutBinding: (id, binding) => set((state) => ({ shortcutBindings: { ...state.shortcutBindings, [id]: binding } })),
  resetShortcutBindings: () => set({ shortcutBindings: defaultCanvasShortcutBindings() })
}));

let memoryClock: HybridClock | undefined;

function getDeviceId(): string {
  const existing = localStorage.getItem("bedrock-device-id");
  if (existing) return existing;
  const created = makeId("device");
  localStorage.setItem("bedrock-device-id", created);
  return created;
}

function nextClock(): HybridClock {
  if (!memoryClock) {
    try {
      const saved = JSON.parse(localStorage.getItem("bedrock-hybrid-clock") ?? "null") as Partial<HybridClock> | null;
      if (saved && typeof saved.wallTime === "number" && typeof saved.counter === "number" && saved.deviceId === getDeviceId()) memoryClock = saved as HybridClock;
    } catch { /* a malformed local clock is replaced on the next tick */ }
  }
  memoryClock = tickClock(getDeviceId(), memoryClock);
  localStorage.setItem("bedrock-hybrid-clock", JSON.stringify(memoryClock));
  return memoryClock;
}

function operation(entityType: SyncOperation["entityType"], entityId: string, patch: Record<string, unknown>, clock: HybridClock, baseRevision: number | null): SyncOperation {
  return { opId: makeId("op"), deviceId: getDeviceId(), entityType, entityId, baseRevision, clock, patch };
}

function syncSafeProps(props: ItemProps): ItemProps {
  if (props.kind === "image" || props.kind === "video" || props.kind === "pdf" || props.kind === "spreadsheet") {
    const { localUrl: _localUrl, ...syncProps } = props;
    return syncProps;
  }
  if (props.kind === "web-clip") {
    const { previewUrl: _previewUrl, ...syncProps } = props;
    return syncProps;
  }
  return props;
}

export interface BedrockAppProps {
  store: LocalStore;
  sessionScope?: string;
  workingSessionCloud?: WorkingSessionCloud | undefined;
  platform?: "web" | "desktop";
  authMode?: "local" | "authenticated";
  seedDemo?: boolean;
  enqueueClip?: ((input: { clipId: string; itemId: string; url: string }) => Promise<void>) | undefined;
  uploadAsset?: ((input: { assetId: string; filename: string; mimeType: string; blob: Blob }) => Promise<void>) | undefined;
  downloadAsset?: ((assetId: string) => Promise<Blob>) | undefined;
  pasteSystemClipboard?: (() => Promise<void>) | undefined;
  readSystemClipboard?: (() => Promise<SystemClipboardContents>) | undefined;
  copyFileToClipboard?: CopyFileToClipboard | undefined;
  onShortcutsChange?: ((accelerators: CanvasShortcutAccelerators) => void) | undefined;
  onHistoryAvailabilityChange?: ((availability: HistoryAvailability) => void) | undefined;
  getAccountStorageUsage?: (() => Promise<AccountStorageUsage>) | undefined;
  updateState?: DesktopUpdateState | undefined;
  onInstallUpdate?: (() => Promise<void> | void) | undefined;
  onCheckUpdate?: (() => Promise<void> | void) | undefined;
  onSignIn?: (() => Promise<void> | void) | undefined;
  onSignOut?: (() => Promise<void> | void) | undefined;
}

export interface HistoryAvailability {
  canUndo: boolean;
  canRedo: boolean;
}

export type { SystemClipboardContents } from "./clipboard-paste";
export type { ClipboardFileInput, CopyFileToClipboard } from "./asset-clipboard";

interface LocalData {
  spaces: Space[];
  itemsBySpace: Map<string, CanvasItem[]>;
  loading: boolean;
}

export function useLocalData(store: LocalStore, seedDemo: boolean, downloadAsset?: (assetId: string) => Promise<Blob>): LocalData {
  const [data, setData] = useState<LocalData>({ spaces: [], itemsBySpace: new Map(), loading: true });
  const assetUrls = useRef(new Map<string, string>());
  const assetRequests = useRef(new Map<string, Promise<string | undefined>>());
  const assetRetryAfter = useRef(new Map<string, number>());
  const refreshVersion = useRef(0);

  const resolveAssetUrl = useCallback((assetId: string): Promise<string | undefined> => {
    const existing = assetUrls.current.get(assetId);
    if (existing) return Promise.resolve(existing);
    if ((assetRetryAfter.current.get(assetId) ?? 0) > Date.now()) return Promise.resolve(undefined);
    const inFlight = assetRequests.current.get(assetId);
    if (inFlight) return inFlight;
    const request = (async () => {
      const cached = await store.getCachedAsset(assetId);
      let blob = cached?.blob;
      if (!blob && downloadAsset) {
        blob = await downloadAsset(assetId);
        await store.putCachedAsset({ assetId, blob, byteLength: blob.size, lastAccessedAt: Date.now(), pinned: false });
      }
      if (!blob) return undefined;
      const localUrl = URL.createObjectURL(blob);
      assetUrls.current.set(assetId, localUrl);
      assetRetryAfter.current.delete(assetId);
      return localUrl;
    })().catch(() => {
      assetRetryAfter.current.set(assetId, Date.now() + 30_000);
      return undefined;
    }).finally(() => assetRequests.current.delete(assetId));
    assetRequests.current.set(assetId, request);
    return request;
  }, [downloadAsset, store]);

  const refresh = useCallback(async () => {
    const version = ++refreshVersion.current;
    const spaces = await store.listSpaces({ includeDeleted: true });
    const itemPairs = await Promise.all(spaces.map(async (space) => {
      const items = await store.listItems(space.id, { includeDeleted: true });
      const hydrated = await Promise.all(items.map(async (item) => {
        const hydratableItem = withoutStaleCanvasObjectUrl(item);
        const assetId = canvasAssetId(hydratableItem);
        if (!assetId) return hydratableItem;
        const localUrl = await resolveAssetUrl(assetId);
        return localUrl ? withCanvasAssetUrl(hydratableItem, localUrl) : hydratableItem;
      }));
      return [space.id, hydrated] as const;
    }));
    if (version !== refreshVersion.current) return;
    setData({ spaces, itemsBySpace: new Map(itemPairs), loading: false });
  }, [resolveAssetUrl, store]);

  useEffect(() => {
    const initialize = async () => {
      await store.initialize();
      const spaces = await store.listSpaces({ includeDeleted: true });
      if (spaces.length === 0 && seedDemo) {
        for (const space of fixtureSpaces) {
          const clock = nextClock();
          await store.saveSpace(space, operation("space", space.id, { entity: space }, clock, null));
        }
        for (const item of fixtureItems) {
          const clock = nextClock();
          await store.saveItem(item, operation("item", item.id, { entity: item }, clock, null));
        }
      }
      await refresh();
    };
    void initialize();
    const unsubscribe = store.subscribe(() => void refresh());
    return unsubscribe;
  }, [refresh, seedDemo, store]);

  useEffect(() => {
    const urls = assetUrls.current;
    return () => {
      for (const url of urls.values()) URL.revokeObjectURL(url);
      urls.clear();
    };
  }, []);

  return data;
}

export function BedrockApp({ store, sessionScope = "local", workingSessionCloud, platform = "web", authMode = "local", seedDemo = authMode === "local", enqueueClip, uploadAsset, downloadAsset, pasteSystemClipboard, readSystemClipboard, copyFileToClipboard, onShortcutsChange, onHistoryAvailabilityChange, getAccountStorageUsage, updateState, onInstallUpdate, onCheckUpdate, onSignIn, onSignOut }: BedrockAppProps) {
  const working = useWorkingSession(sessionScope, workingSessionCloud);
  const startupHandled = useRef(false);
  const startupChoice = useRef(loadStartupMode());
  useEffect(() => { useShell.setState({ screen: { kind: "library" }, editorItemId: null, settingsOpen: false, startupMode: loadStartupMode(), syncState: initialSyncStatus }); }, []);
  const data = useLocalData(store, seedDemo, downloadAsset);
  const { screen, theme, editorItemId, selectEditorTitle, settingsOpen, searchOpen, shortcutBindings } = useShell();
  const setTheme = useShell((state) => state.setTheme);
  const setSearchOpen = useShell((state) => state.setSearchOpen);
  const uploadsInFlight = useRef(new Set<string>());
  const clipsInFlight = useRef(new Set<string>());
  const enqueuedClips = useRef(new Set<string>());
  const transferAttempts = useRef(new Map<string, number>());
  const transferTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const itemSaveQueues = useRef(new Map<string, Promise<void>>());
  const resealRef = useRef<FolderLocks["resealForSave"] | null>(null);
  const metadataSyncStatus = useRef<SyncStatus>({ ...initialSyncStatus });
  const captureHandled = useRef(false);
  const [uploadEpoch, setUploadEpoch] = useState(0);
  const [storageRefreshVersion, setStorageRefreshVersion] = useState(0);
  const [createSpaceOpen, setCreateSpaceOpen] = useState(false);
  const editingItem = editorItemId ? [...data.itemsBySpace.values()].flat().find((item) => item.id === editorItemId) : undefined;
  const resumeSpace = data.spaces.find((space) => space.id === working.session?.spaceId && space.deletedAt === null);
  const resumeItem = resumeSpace ? data.itemsBySpace.get(resumeSpace.id)?.find((item) => item.id === working.session?.itemId && item.deletedAt === null && item.props.kind === "document" && !isLockedItem(item)) : undefined;
  const continueWorking = useCallback(() => {
    if (!resumeSpace) return;
    startupHandled.current = true;
    useShell.getState().setScreen({ kind: "space", spaceId: resumeSpace.id });
    if (resumeItem) useShell.getState().setEditorItemId(resumeItem.id);
  }, [resumeItem, resumeSpace]);
  useEffect(() => {
    if (data.loading || !working.ready || startupHandled.current) return;
    if (screen.kind !== "library") { startupHandled.current = true; return; }
    if (startupChoice.current === "library") { startupHandled.current = true; return; }
    if (resumeSpace) continueWorking();
    // A new device may receive the session before the space has finished syncing.
  }, [continueWorking, data.loading, resumeSpace, screen.kind, working.ready]);
  const rememberPosition = useCallback((item: CanvasItem, cursor: number, scrollTop: number) => {
    working.remember({ spaceId: item.spaceId, itemId: item.id, cursor, scrollTop }, getDeviceId());
  }, [working.remember]);
  useEffect(() => {
    if (data.loading || screen.kind !== "space") return;
    const space = data.spaces.find((entry) => entry.id === screen.spaceId && entry.deletedAt === null);
    if (!space) return;
    const itemId = editingItem?.props.kind === "document" ? editingItem.id : null;
    if (working.session?.spaceId === space.id && working.session.itemId === itemId) return;
    working.remember({ spaceId: space.id, itemId }, getDeviceId());
  }, [data.loading, data.spaces, editingItem, screen, working.remember, working.session]);
  const [activity, setActivity] = useState<SyncActivity>({ pendingEdits: 0, entries: [], loading: true });
  useEffect(() => {
    if (data.loading) return;
    let active = true;
    let version = 0;
    const refresh = async () => {
      const request = ++version;
      const [outbox, issues, pendingEdits] = await Promise.all([store.getOutbox?.(10000) ?? [], store.listSyncIssues?.() ?? [], store.getPendingOperationCount?.() ?? 0]);
      if (!active || request !== version) return;
      const items = [...data.itemsBySpace.values()].flat().filter((item) => item.deletedAt === null);
      const pendingIds = new Set(outbox.map((op) => op.entityId));
      const entries: SyncActivity["entries"] = [];
      for (const item of items) {
        const props = item.props;
        if (props.kind === "image" || props.kind === "video" || props.kind === "pdf" || props.kind === "spreadsheet" || props.kind === "web-clip") {
          if (props.status === "uploading" || props.status === "pending" || props.status === "failed" || pendingIds.has(item.id)) {
            entries.push({ id: item.id, name: "filename" in props ? props.filename : props.title || props.url, status: props.status === "failed" ? "Needs attention" : props.status === "ready" ? "Saving to cloud" : props.kind === "web-clip" ? "Capturing preview" : "Uploading", failed: props.status === "failed" });
          }
        }
      }
      for (const issue of issues) {
        const item = items.find((entry) => entry.id === issue.operation.entityId);
        const space = data.spaces.find((entry) => entry.id === issue.operation.entityId);
        entries.push({ id: issue.id, name: item ? itemDisplayName(item) : space?.title ?? "Unsynced change", status: issue.message, failed: true });
      }
      setActivity({ pendingEdits, entries });
    };
    void refresh().catch(() => { if (active) setActivity((current) => ({ ...current, loading: false, error: true })); });
    const unsubscribe = store.subscribe(() => void refresh().catch(() => { if (active) setActivity((current) => ({ ...current, loading: false, error: true })); }));
    return () => { active = false; unsubscribe(); };
  }, [data.itemsBySpace, data.loading, data.spaces, store]);
  const syncActivity = { ...activity, loading: activity.loading || !working.ready, sessionPending: working.pending, sessionError: working.error, retrySession: working.retry };
  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if (event.isComposing || event.defaultPrevented || event.key.toLocaleLowerCase() !== "k" || (!event.metaKey && !event.ctrlKey)) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable) return;
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, [setSearchOpen]);
  const scheduleTransferRetry = useCallback((key: string, retryAt?: number | null) => {
    if (transferTimers.current.has(key)) return;
    const delays = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000];
    const attempt = transferAttempts.current.get(key) ?? 0;
    transferAttempts.current.set(key, attempt + 1);
    const base = delays[Math.min(attempt, delays.length - 1)]!;
    const delay = retryAt ? Math.max(0, retryAt - Date.now()) : Math.round(base * (.8 + Math.random() * .4));
    const timer = setTimeout(() => {
      transferTimers.current.delete(key);
      setUploadEpoch((value) => value + 1);
    }, delay);
    transferTimers.current.set(key, timer);
  }, []);

  const finishTransfer = useCallback((key: string) => {
    transferAttempts.current.delete(key);
    const timer = transferTimers.current.get(key);
    if (timer) clearTimeout(timer);
    transferTimers.current.delete(key);
  }, []);

  useEffect(() => () => {
    for (const timer of transferTimers.current.values()) clearTimeout(timer);
    transferTimers.current.clear();
  }, []);

  useEffect(() => {
    document.documentElement.dataset["theme"] = theme;
    localStorage.setItem("bedrock-theme", theme);
  }, [theme]);

  useEffect(() => {
    useShell.setState({ shortcutBindings: loadCanvasShortcutBindings() });
  }, []);

  useEffect(() => {
    useShell.setState({ controlScheme: loadCanvasControlScheme() });
  }, []);

  useEffect(() => {
    saveCanvasShortcutBindings(shortcutBindings);
    onShortcutsChange?.(canvasShortcutAccelerators(shortcutBindings));
  }, [onShortcutsChange, shortcutBindings]);

  useEffect(() => {
    if (data.loading) return;
    const items = [...data.itemsBySpace.values()].flat();
    const pendingAssets = items.filter((item) => (item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" || item.props.kind === "spreadsheet") && item.props.status === "uploading").length;
    const pendingClips = items.filter((item) => item.props.kind === "web-clip" && item.props.status === "pending").length;
    const current = useShell.getState().syncState;
    if (current.pendingAssets !== pendingAssets || current.pendingClips !== pendingClips) {
      const metadata = metadataSyncStatus.current;
      useShell.getState().setSyncState({ ...metadata, phase: metadata.phase === "up-to-date" && pendingAssets + pendingClips > 0 ? "syncing" : metadata.phase, pendingAssets, pendingClips });
    }
  }, [data.itemsBySpace, data.loading]);

  useEffect(() => {
    const listener = (event: Event) => {
      const current = useShell.getState().syncState;
      const metadata = (event as CustomEvent<SyncStatus>).detail;
      metadataSyncStatus.current = metadata;
      useShell.getState().setSyncState({ ...metadata, phase: metadata.phase === "up-to-date" && current.pendingAssets + current.pendingClips > 0 ? "syncing" : metadata.phase, pendingAssets: current.pendingAssets, pendingClips: current.pendingClips });
    };
    const observeClock = (event: Event) => {
      memoryClock = tickClock(getDeviceId(), memoryClock, (event as CustomEvent<HybridClock>).detail);
      localStorage.setItem("bedrock-hybrid-clock", JSON.stringify(memoryClock));
    };
    window.addEventListener("bedrock:sync-state", listener);
    window.addEventListener("bedrock:remote-clock", observeClock);
    return () => { window.removeEventListener("bedrock:sync-state", listener); window.removeEventListener("bedrock:remote-clock", observeClock); };
  }, []);

  useEffect(() => {
    const listener = (event: Event) => {
      const command = (event as CustomEvent<string>).detail;
      if (command === "export") void downloadLibraryExport(store);
      if (command === "new-space") setCreateSpaceOpen(true);
    };
    window.addEventListener("bedrock:command", listener);
    return () => window.removeEventListener("bedrock:command", listener);
  }, [store]);

  useEffect(() => {
    const onOnline = () => setUploadEpoch((value) => value + 1);
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const saveItemChanges = useCallback((item: CanvasItem, patch: Partial<Pick<CanvasItem, "x" | "y" | "width" | "height" | "rotation" | "zIndex" | "parentStackId" | "props" | "deletedAt">>, options?: SaveItemOptions): Promise<void> => {
    const previousSave = itemSaveQueues.current.get(item.id) ?? Promise.resolve();
    const pendingSave = previousSave.catch(() => undefined).then(async () => {
      const current = await store.getItem?.(item.id) ?? item;
      // Edits to something decrypted from an open folder are encrypted again before they are stored.
      if (patch.props && isSealedItem(current) && !options?.removeLock) {
        const resealed = await resealRef.current?.(current, patch.props);
        if (resealed) patch = { ...patch, props: resealed };
      }
      // A folder only looks expanded on this device; its stored state stays as it was. Locking is the
      // one edit that closes it for good.
      if (patch.props?.kind === "stack" && current.props.kind === "stack" && !options?.removeLock && (isLockedFolder(current) || !isLockedFolder({ props: patch.props }))) {
        patch = { ...patch, props: { ...patch.props, collapsed: current.props.collapsed } };
      }
      // Stale editors and undo history still hold the readable body; only an explicit unlock may restore it.
      if (patch.props && isLockedItem(current) && !isLockedItem({ props: patch.props }) && !options?.removeLock) {
        const { props: _staleProps, ...rest } = patch;
        patch = rest;
        if (Object.keys(patch).length === 0) return;
      }
      const clock = nextClock();
      const fieldClocks = { ...current.fieldClocks };
      for (const key of Object.keys(patch)) fieldClocks[key] = clock;
      if (patch.props) patch = { ...patch, props: sealLockedProps(patch.props) };
      const next: CanvasItem = { ...current, ...patch, fieldClocks, updatedAt: clock.wallTime };
      const syncPatch = { ...patch } as Record<string, unknown>;
      if (patch.props) syncPatch["props"] = syncSafeProps(patch.props);
      await store.saveItem(withoutCanvasAssetUrl(next), operation("item", item.id, syncPatch, clock, current.updatedAt));
    });
    itemSaveQueues.current.set(item.id, pendingSave);
    const clearPendingSave = () => {
      if (itemSaveQueues.current.get(item.id) === pendingSave) itemSaveQueues.current.delete(item.id);
    };
    void pendingSave.then(clearPendingSave, clearPendingSave);
    return pendingSave;
  }, [store]);

  const storedItems = useMemo(() => [...data.itemsBySpace.values()].flat(), [data.itemsBySpace]);
  const folderLocks = useFolderLocks({ store, items: storedItems, saveItemChanges, uploadAsset, downloadAsset });
  resealRef.current = folderLocks.resealForSave;
  const displayItemsBySpace = useMemo(() => new Map([...data.itemsBySpace].map(([spaceId, items]) => [spaceId, items.map(folderLocks.display)])), [data.itemsBySpace, folderLocks.display]);
  const openFolderIds = folderLocks.openFolderIds;
  const closeFolder = folderLocks.closeFolder;
  useEffect(() => {
    // Leaving a space closes its open folders, so their keys never outlive the canvas that showed them.
    const spaceId = screen.kind === "space" ? screen.spaceId : null;
    for (const folderId of openFolderIds) {
      if (storedItems.find((item) => item.id === folderId)?.spaceId !== spaceId) void closeFolder(folderId);
    }
  }, [closeFolder, openFolderIds, screen, storedItems]);

  const createItem = useCallback(async (spaceId: string, props: ItemProps, position?: Point, preferredSize?: { width: number; height: number }) => {
    const clock = nextClock();
    const type = props.kind;
    const dimensions = preferredSize ?? defaultCanvasItemSize(type);
    const items = data.itemsBySpace.get(spaceId) ?? [];
    const item: CanvasItem = {
      id: makeId("item"), spaceId, type,
      x: position?.x ?? 0, y: position?.y ?? 0,
      width: dimensions.width, height: dimensions.height,
      rotation: 0, zIndex: type === "frame"
        ? Math.min(0, ...items.map((current) => current.zIndex)) - 1
        : Math.max(0, ...items.map((current) => current.zIndex)) + 1,
      parentStackId: null, props,
      fieldClocks: { props: clock, x: clock, y: clock, width: clock, height: clock, rotation: clock, zIndex: clock, parentStackId: clock, deletedAt: clock },
      createdAt: clock.wallTime, updatedAt: clock.wallTime, deletedAt: null
    };
    const persistedItem = withoutCanvasAssetUrl(item);
    await store.saveItem(persistedItem, operation("item", item.id, { entity: persistedItem }, clock, null));
    if (type === "frame") {
      const reconciled = reconcileFrameMembership(item, items.filter((current) => current.deletedAt === null));
      for (const next of reconciled) {
        const current = items.find((candidate) => candidate.id === next.id);
        if (!current || (current.parentStackId === next.parentStackId && current.zIndex === next.zIndex)) continue;
        await saveItemChanges(current, { parentStackId: next.parentStackId, zIndex: next.zIndex });
      }
    }
    if (type === "sticky-note" || type === "document" || type === "label") {
      useShell.getState().setSelectedIds([]);
      useShell.getState().setEditorItemId(item.id, type === "document");
    } else useShell.getState().setSelectedIds([item.id]);
    return item;
  }, [data.itemsBySpace, saveItemChanges, store]);

  useEffect(() => {
    if (!uploadAsset || data.loading || (typeof navigator !== "undefined" && !navigator.onLine)) return;
    for (const item of [...data.itemsBySpace.values()].flat()) {
      const assetProps = item.props;
      if ((assetProps.kind !== "image" && assetProps.kind !== "video" && assetProps.kind !== "pdf" && assetProps.kind !== "spreadsheet") || assetProps.status !== "uploading" || uploadsInFlight.current.has(assetProps.assetId)) continue;
      uploadsInFlight.current.add(assetProps.assetId);
      void store.getCachedAsset(assetProps.assetId).then(async (cached) => {
        if (!cached) throw new SyncRequestError({ retryable: false, status: null, code: "missing_local_asset", message: "The local asset cache no longer contains this upload", correlationId: null, retryAt: null });
        await uploadAsset({ assetId: assetProps.assetId, filename: assetProps.filename, mimeType: assetProps.mimeType, blob: cached.blob });
        await store.putCachedAsset({ ...cached, pinned: false, lastAccessedAt: Date.now() });
        await saveItemChanges(item, { props: { ...assetProps, status: "ready" } });
        setStorageRefreshVersion((version) => version + 1);
        finishTransfer(`asset:${assetProps.assetId}`);
      }).catch((error: unknown) => {
        if (error instanceof SyncRequestError && !error.detail.retryable) void saveItemChanges(item, { props: { ...assetProps, status: "failed" } });
        else scheduleTransferRetry(`asset:${assetProps.assetId}`, error instanceof SyncRequestError ? error.detail.retryAt : null);
      }).finally(() => uploadsInFlight.current.delete(assetProps.assetId));
    }
  }, [data.itemsBySpace, data.loading, finishTransfer, saveItemChanges, scheduleTransferRetry, store, uploadAsset, uploadEpoch]);

  useEffect(() => {
    if (!enqueueClip || data.loading || (typeof navigator !== "undefined" && !navigator.onLine)) return;
    for (const item of [...data.itemsBySpace.values()].flat()) {
      if (item.props.kind !== "web-clip" || item.props.status !== "pending" || enqueuedClips.current.has(item.props.clipId) || clipsInFlight.current.has(item.props.clipId)) continue;
      const props = item.props;
      clipsInFlight.current.add(props.clipId);
      void enqueueClip({ clipId: props.clipId, itemId: item.id, url: props.url }).then(() => {
        enqueuedClips.current.add(props.clipId);
        finishTransfer(`clip:${props.clipId}`);
      }).catch((error: unknown) => {
        if (error instanceof SyncRequestError && !error.detail.retryable) void saveItemChanges(item, { props: { ...props, status: "failed", description: "Preview capture could not be started." } });
        else scheduleTransferRetry(`clip:${props.clipId}`, error instanceof SyncRequestError ? error.detail.retryAt : null);
      }).finally(() => clipsInFlight.current.delete(props.clipId));
    }
  }, [data.itemsBySpace, data.loading, enqueueClip, finishTransfer, saveItemChanges, scheduleTransferRetry, uploadEpoch]);

  useEffect(() => {
    if (platform !== "web" || data.loading || captureHandled.current || new URLSearchParams(window.location.search).get("capture") !== "note") return;
    const inbox = data.spaces.find((space) => space.title === "Inbox" && space.deletedAt === null) ?? data.spaces.find((space) => space.deletedAt === null);
    if (!inbox) return;
    captureHandled.current = true;
    history.replaceState(null, "", window.location.pathname);
    useShell.getState().setScreen({ kind: "space", spaceId: inbox.id });
    void createItem(inbox.id, { kind: "sticky-note", markdown: "", plainText: "", accent: "paper" });
  }, [createItem, data.loading, data.spaces, platform]);

  const saveSpaceChanges = useCallback(async (space: Space, patch: Partial<Pick<Space, "title" | "theme" | "starred" | "deletedAt">>) => {
    const clock = nextClock();
    const fieldClocks = { ...space.fieldClocks };
    for (const key of Object.keys(patch)) fieldClocks[key] = clock;
    const next: Space = { ...space, ...patch, fieldClocks, updatedAt: clock.wallTime };
    await store.saveSpace(next, operation("space", space.id, patch, clock, space.updatedAt));
  }, [store]);

  const permanentlyDelete = useCallback(async (entityType: "space" | "item", entityId: string, baseRevision: number) => {
    if (entityType === "item") await (itemSaveQueues.current.get(entityId) ?? Promise.resolve()).catch(() => undefined);
    const clock = nextClock();
    await store.permanentlyDelete(entityType, entityId, operation(entityType, entityId, { permanentDelete: true }, clock, baseRevision));
  }, [store]);

  const displayActiveItems = screen.kind === "space" ? displayItemsBySpace.get(screen.spaceId) ?? [] : [];
  const displayedEditingItem = editingItem ? folderLocks.display(editingItem) : undefined;
  // Something still sealed (its folder closed meanwhile) has nothing readable to edit.
  const shownEditingItem = displayedEditingItem && !isSealedItem(displayedEditingItem) ? displayedEditingItem : undefined;

  if (data.loading) return <LoadingScreen />;

  return <main className="bedrock-app" data-platform={platform} spellCheck={false} autoCorrect="off">
    {screen.kind === "library" && <LibraryScreen spaces={data.spaces} itemsBySpace={data.itemsBySpace} store={store} authMode={authMode} activity={syncActivity} updateState={updateState} onInstallUpdate={onInstallUpdate} onCheckUpdate={onCheckUpdate} onSignIn={onSignIn} saveSpaceChanges={saveSpaceChanges} onCreateSpace={() => setCreateSpaceOpen(true)}/>}
    {screen.kind === "space" && <SpaceScreen key={screen.spaceId} space={data.spaces.find((space) => space.id === screen.spaceId)} syncBadge={<SyncBadge state={authMode === "local" ? { ...initialSyncStatus, phase: "local-only" } : useShell.getState().syncState} activity={syncActivity} onSignIn={onSignIn}/>} updateState={updateState} onInstallUpdate={onInstallUpdate} onCheckUpdate={onCheckUpdate} items={displayActiveItems} saveItemChanges={saveItemChanges} createItem={createItem} uploadAsset={uploadAsset} pasteSystemClipboard={pasteSystemClipboard} readSystemClipboard={readSystemClipboard} cacheAsset={(assetId, blob) => store.putCachedAsset({ assetId, blob, byteLength: blob.size, lastAccessedAt: Date.now(), pinned: true })} onHistoryAvailabilityChange={onHistoryAvailabilityChange} folderLocks={folderLocks}/>}
    {searchOpen && <SearchDialog store={store}/>}
    {settingsOpen && <SettingsDialog authMode={authMode} store={store} spaces={data.spaces} itemsBySpace={data.itemsBySpace} saveSpaceChanges={saveSpaceChanges} saveItemChanges={saveItemChanges} permanentlyDelete={permanentlyDelete} getAccountStorageUsage={getAccountStorageUsage} storageRefreshVersion={storageRefreshVersion} onSignOut={onSignOut}/>}
    {createSpaceOpen && <CreateSpaceDialog store={store} onClose={() => setCreateSpaceOpen(false)}/>}
    {shownEditingItem?.type === "document" && !isLockedItem(shownEditingItem) && <EditorDialog key={shownEditingItem.id} item={shownEditingItem} position={working.session?.itemId === shownEditingItem.id ? working.session : null} onPositionChange={rememberPosition} store={store} saveItemChanges={saveItemChanges} selectTitle={selectEditorTitle}/>}
    {shownEditingItem && isLockedItem(shownEditingItem) && <LockedNoteEditor key={shownEditingItem.id} item={shownEditingItem} store={store} saveItemChanges={saveItemChanges}/>}
    {shownEditingItem?.type === "date-event" && <DateEventDialog item={shownEditingItem} saveItemChanges={saveItemChanges}/>}
    {shownEditingItem && (shownEditingItem.type === "image" || shownEditingItem.type === "video" || shownEditingItem.type === "pdf") && <MediaDialog item={shownEditingItem} saveItemChanges={saveItemChanges} copyFileToClipboard={copyFileToClipboard}/>}
    {shownEditingItem?.type === "spreadsheet" && <SpreadsheetDialog item={shownEditingItem} saveItemChanges={saveItemChanges} copyFileToClipboard={copyFileToClipboard}/>}
    {shownEditingItem?.type === "web-clip" && <WebClipDialog item={shownEditingItem}/>}

    <button className="bedrock-theme-sentinel" aria-hidden="true" tabIndex={-1} onClick={() => setTheme(theme === "light" ? "dark" : "light")}/>
  </main>;
}

function LoadingScreen() {
  return <div className="bedrock-loading"><StrataMark size={74}/><p>Settling your space…</p></div>;
}

function LibraryScreen({ spaces, itemsBySpace, store, authMode, updateState, onInstallUpdate, onCheckUpdate, onSignIn, saveSpaceChanges, onCreateSpace, activity }: {
  spaces: Space[];
  itemsBySpace: Map<string, CanvasItem[]>;
  store: LocalStore;
  authMode: "local" | "authenticated";
  updateState?: DesktopUpdateState | undefined;
  onInstallUpdate?: (() => Promise<void> | void) | undefined;
  onCheckUpdate?: (() => Promise<void> | void) | undefined;
  onSignIn?: (() => Promise<void> | void) | undefined;
  saveSpaceChanges: (space: Space, patch: Partial<Pick<Space, "title" | "theme" | "starred" | "deletedAt">>) => Promise<void>;
  onCreateSpace: () => void;
  activity: SyncActivity;
}) {
  const setScreen = useShell((state) => state.setScreen);
  const setSettingsOpen = useShell((state) => state.setSettingsOpen);
  const setSearchOpen = useShell((state) => state.setSearchOpen);
  const syncState = useShell((state) => state.syncState);
  const [query, setQuery] = useState("");
  const [contextMenu, setContextMenu] = useState<{ spaceId: string; anchor: Point } | null>(null);
  const librarySpaces = spaces.filter((space) => space.deletedAt === null);
  const spaceQueryTerms = normalizeSearchText(query).split(" ").filter(Boolean);
  const activeSpaces = librarySpaces
    .filter((space) => {
      const title = normalizeSearchText(space.title);
      return spaceQueryTerms.every((term) => title.includes(term));
    })
    .sort((left, right) => Number(right.starred) - Number(left.starred));
  const totalItemCount = librarySpaces.reduce((count, space) => count + (itemsBySpace.get(space.id) ?? []).filter((item) => item.deletedAt === null).length, 0);
  const menuSpace = contextMenu ? librarySpaces.find((space) => space.id === contextMenu.spaceId) : undefined;

  const duplicateSpace = async (source: Space) => {
    const spaceClock = nextClock();
    const duplicate: Space = {
      ...source,
      id: makeId("space"),
      title: `${source.title} copy`,
      starred: false,
      fieldClocks: { title: spaceClock, theme: spaceClock, starred: spaceClock, deletedAt: spaceClock },
      createdAt: spaceClock.wallTime,
      updatedAt: spaceClock.wallTime,
      deletedAt: null
    };
    await store.saveSpace(duplicate, operation("space", duplicate.id, { entity: duplicate }, spaceClock, null));

    const sourceItems = (itemsBySpace.get(source.id) ?? []).filter((item) => item.deletedAt === null);
    const duplicateIds = new Map(sourceItems.map((item) => [item.id, makeId("item")]));
    for (const item of sourceItems) {
      const itemClock = nextClock();
      const duplicateItem: CanvasItem = {
        ...structuredClone(item),
        id: duplicateIds.get(item.id) ?? makeId("item"),
        spaceId: duplicate.id,
        parentStackId: item.parentStackId ? duplicateIds.get(item.parentStackId) ?? null : null,
        fieldClocks: { props: itemClock, x: itemClock, y: itemClock, width: itemClock, height: itemClock, rotation: itemClock, zIndex: itemClock, parentStackId: itemClock, deletedAt: itemClock },
        createdAt: itemClock.wallTime,
        updatedAt: itemClock.wallTime,
        deletedAt: null
      };
      const persistedItem = withoutCanvasAssetUrl(duplicateItem);
      await store.saveItem(persistedItem, operation("item", duplicateItem.id, { entity: persistedItem }, itemClock, null));
    }
  };
  return <section className="library-screen">
    <header className="library-header">
      <div className="library-brand"><StrataMark size={42}/><div><h1>Bedrock</h1><p>Your private field of notes</p></div></div>
      <div className="library-actions">
        {updateState && onInstallUpdate && isActionableUpdateState(updateState) && <DashboardUpdateButton state={updateState} onInstall={onInstallUpdate}/>}
        {updateState?.phase === "error" && !updateState.update && onCheckUpdate && <button
          type="button"
          className="library-update-button library-update-button--error"
          aria-label="Retry update check"
          title={updateState.message}
          onClick={() => void Promise.resolve(onCheckUpdate())}
        ><Icon name="download"/><span>Retry update check</span></button>}
        <SyncBadge state={authMode === "local" ? { ...initialSyncStatus, phase: "local-only" } : syncState} activity={activity} onSignIn={onSignIn}/>
        <IconButton label="Search everything" tooltip="Search everything" tooltipShortcut="Ctrl/⌘ K" icon="search" onClick={() => setSearchOpen(true)}/>
        <IconButton label="Settings" icon="settings" onClick={() => setSettingsOpen(true)}/>
      </div>
    </header>

    <div className="library-layout">
      <aside className="library-intro">
        <div className="library-intro__copy">
          <p className="eyebrow">PRIVATE LIBRARY</p>
          <h2>Spaces</h2>
          <p className="library-intro__description">One room for each thread. Keep the shape of an idea without sorting everything first.</p>
        </div>
        <div className="library-overview" aria-label="Library overview">
          <span><strong>{librarySpaces.length}</strong><small>{librarySpaces.length === 1 ? "space" : "spaces"}</small></span>
          <span><strong>{totalItemCount}</strong><small>{totalItemCount === 1 ? "item" : "items"}</small></span>
        </div>
      </aside>

      <section className="library-collection" aria-label="Spaces">
        <div className="library-tools">
          <label className="library-search"><Icon name="search"/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a space" aria-label="Filter spaces"/></label>
          <span className="library-tools__summary">{query.trim() ? `${activeSpaces.length} found` : `${librarySpaces.length} total`}</span>
        </div>

        {activeSpaces.length > 0 ? <div className="space-grid">
          <button className="new-space-card new-space-card--wide" onClick={onCreateSpace}><span><Icon name="plus"/></span><span className="new-space-card__copy"><strong>Start a new space</strong><small>A blank surface for one thread of thought.</small></span><Icon name="arrow-up-right" className="new-space-card__arrow"/></button>
          {activeSpaces.map((space, index) => <SpaceCard key={space.id} space={space} items={itemsBySpace.get(space.id) ?? []} index={index} onOpen={() => runViewTransition(() => setScreen({ kind: "space", spaceId: space.id }))} onMenu={(anchor) => setContextMenu({ spaceId: space.id, anchor })}/>)}
        </div> : <div className="library-empty">
          <span className="library-empty__mark">{query ? <Icon name="search"/> : <StrataMark size={54}/>}</span>
          <p className="eyebrow">{query ? "NO MATCHES" : "YOUR LIBRARY IS QUIET"}</p>
          <h3>{query ? <>No space named “{query}”</> : "Make room for your first thread."}</h3>
          <p>{query ? "Try another name or return to the full library." : "Spaces keep related notes and references together without asking you to organize everything first."}</p>
          {query ? <Button onClick={() => setQuery("")}>Clear search</Button> : <Button tone="primary" onClick={onCreateSpace}><Icon name="plus"/> Create your first space</Button>}
        </div>}
      </section>
    </div>
    {menuSpace && contextMenu && <SpaceActionMenu
      anchor={contextMenu.anchor}
      space={menuSpace}
      onClose={() => setContextMenu(null)}
      onOpen={() => runViewTransition(() => setScreen({ kind: "space", spaceId: menuSpace.id }))}
      onRename={(title) => void saveSpaceChanges(menuSpace, { title })}
      onToggleStar={() => void saveSpaceChanges(menuSpace, { starred: !menuSpace.starred })}
      onDuplicate={() => void duplicateSpace(menuSpace)}
      onDelete={() => void saveSpaceChanges(menuSpace, { deletedAt: Date.now() })}
    />}
  </section>;
}

type ActionableDesktopUpdateState =
  | Extract<DesktopUpdateState, { phase: "available" | "ready" | "downloading" | "installing" | "installed" }>
  | (Extract<DesktopUpdateState, { phase: "error" }> & { update: DesktopUpdateInfo });

function isActionableUpdateState(state: DesktopUpdateState): state is ActionableDesktopUpdateState {
  return state.phase === "available" || state.phase === "ready" || state.phase === "downloading" || state.phase === "installing" || state.phase === "installed" || (state.phase === "error" && state.update !== undefined);
}

function DashboardUpdateButton({ state, onInstall, className = "" }: {
  state: ActionableDesktopUpdateState;
  onInstall: () => Promise<void> | void;
  className?: string;
}) {
  const progress = state.phase === "downloading" ? Math.min(100, Math.floor((state.receivedBytes / state.totalBytes) * 100)) : null;
  const label = state.phase === "downloading"
    ? `Downloading ${progress}%`
    : state.phase === "installing"
      ? "Installing Bedrock…"
      : state.phase === "installed"
        ? "Restart Bedrock"
        : state.phase === "ready"
          ? "Open installer"
          : state.phase === "error"
            ? "Retry update"
            : `Update to ${state.update.version}`;
  return <button
    type="button"
    className={`library-update-button ${className} library-update-button--${state.phase}`}
    disabled={state.phase === "downloading" || state.phase === "installing"}
    aria-label={label}
    title={state.phase === "error" ? state.message : state.update.target === "linux-x64" ? "Updates only Bedrock. Administrator authentication is required; restart after installation." : undefined}
    onClick={() => void Promise.resolve(onInstall())}
  >
    <Icon name="download"/>
    <span aria-live="polite">{label}</span>
  </button>;
}

function CreateSpaceDialog({ store, onClose }: { store: LocalStore; onClose: () => void }) {
  const setScreen = useShell((state) => state.setScreen);
  const nameInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const createSpace = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const title = name.trim();
    if (!title) {
      setError("Enter a name for your space.");
      nameInput.current?.focus();
      return;
    }

    setCreating(true);
    setError(null);
    const clock = nextClock();
    const space: Space = { id: makeId("space"), title, theme: "system", starred: false, fieldClocks: { title: clock, theme: clock, starred: clock, deletedAt: clock }, createdAt: clock.wallTime, updatedAt: clock.wallTime, deletedAt: null };
    try {
      await store.saveSpace(space, operation("space", space.id, { entity: space }, clock, null));
      runViewTransition(() => {
        onClose();
        setScreen({ kind: "space", spaceId: space.id });
      });
    } catch {
      setCreating(false);
      setError("This space could not be created. Try again.");
    }
  };

  const close = () => {
    if (!creating) onClose();
  };

  return <Modal title="Name your space" className="create-space-modal" onClose={close}>
    <form className="create-space-form" onSubmit={(event) => void createSpace(event)} noValidate>
      <div className="create-space-intro">
        <span className="create-space-intro__mark" aria-hidden="true"><StrataMark size={52}/></span>
        <div><strong>Start with something you will recognize.</strong><p>A clear name keeps each thread distinct in your library.</p></div>
      </div>
      <div className="create-space-fields">
        <label htmlFor="create-space-name">Space name</label>
        <input
          id="create-space-name"
          ref={nameInput}
          autoFocus
          autoComplete="off"
          maxLength={200}
          placeholder="Product direction"
          required
          value={name}
          aria-describedby={error ? "create-space-error" : "create-space-help"}
          aria-invalid={error ? true : undefined}
          onChange={(event) => { setName(event.target.value); if (error) setError(null); }}
        />
        {error
          ? <p id="create-space-error" className="create-space-error" role="alert">{error}</p>
          : <div id="create-space-help" className="create-space-help"><span>Use a short, memorable name.</span><span>{name.length}/200</span></div>}
      </div>
      <footer className="create-space-actions">
        <Button type="button" onClick={close} disabled={creating}>Cancel</Button>
        <Button type="submit" tone="primary" disabled={creating}>{creating ? "Creating…" : "Create space"}</Button>
      </footer>
    </form>
  </Modal>;
}

const SPACE_PREVIEW_ITEM_LIMIT = 50;

function spacePreviewViewBox(items: CanvasItem[]): string {
  const bounds = boundsForItems(items);
  const width = Math.max(1, bounds.maxX - bounds.minX);
  const height = Math.max(1, bounds.maxY - bounds.minY);
  const paddingX = Math.max(24, width * .1);
  const paddingY = Math.max(24, height * .1);
  return `${bounds.minX - paddingX} ${bounds.minY - paddingY} ${width + paddingX * 2} ${height + paddingY * 2}`;
}

function spacePreviewItemClassName(item: CanvasItem): string {
  const classes = ["mini-item", `mini-item--${item.type}`];
  if (item.props.kind === "sticky-note") classes.push(`mini-item--sticky-${item.props.accent ?? "paper"}`);
  if (item.props.kind === "document") classes.push(`document-color--${item.props.color ?? "paper"}`);
  if (item.props.kind === "date-event") classes.push(`date-event-color--${item.props.color ?? "lilac"}`);
  if (item.props.kind === "stack") classes.push(`mini-item--stack-${item.props.color ?? "lilac"}`);
  if (item.props.kind === "arrow") classes.push(`mini-item--arrow-${item.props.color ?? "ink"}`);
  if (item.props.kind === "frame") {
    classes.push(`mini-item--frame-${item.props.color ?? "clear"}`);
    classes.push(`mini-item--frame-background-${item.props.backgroundStyle ?? "soft"}`);
    classes.push(`mini-item--frame-border-${item.props.borderStyle ?? "solid"}`);
    classes.push(`mini-item--frame-border-color-${item.props.borderColor ?? "match"}`);
  }
  return classes.join(" ");
}

function SpacePreviewItem({ item }: { item: CanvasItem }) {
  const className = spacePreviewItemClassName(item);
  const centerX = item.x + item.width / 2;
  const centerY = item.y + item.height / 2;
  const rotation = item.rotation === 0 ? undefined : `rotate(${item.rotation} ${centerX} ${centerY})`;
  const radius = Math.min(18, Math.max(2, Math.min(item.width, item.height) * .04));

  if (item.props.kind === "arrow") {
    const geometry = arrowGeometry(item.width, item.height, item.props.path);
    const dashArray = item.props.stroke === "dashed" ? "14 10" : item.props.stroke === "dotted" ? "1 10" : undefined;
    return <g className={className} data-preview-item-id={item.id} transform={rotation}>
      <g transform={`translate(${item.x} ${item.y})`}>
        <path className="mini-item__arrow-line" d={geometry.path} pathLength={Math.max(item.width, item.height)} strokeDasharray={dashArray} strokeLinecap={item.props.stroke === "dotted" ? "round" : "square"} vectorEffect="non-scaling-stroke"/>
        {item.props.head !== "none" && <polygon className={`mini-item__arrow-head mini-item__arrow-head--${item.props.head}`} points={arrowHeadPoints(geometry)} vectorEffect="non-scaling-stroke"/>}
      </g>
    </g>;
  }

  if (item.props.kind === "stack") {
    const tabHeight = item.height * .18;
    const tabEnd = item.x + item.width * .43;
    return <path
      className={className}
      data-preview-item-id={item.id}
      d={`M ${item.x + radius} ${item.y + tabHeight} L ${item.x + radius} ${item.y} L ${tabEnd} ${item.y} L ${tabEnd + item.width * .1} ${item.y + tabHeight} L ${item.x + item.width - radius} ${item.y + tabHeight} Q ${item.x + item.width} ${item.y + tabHeight} ${item.x + item.width} ${item.y + tabHeight + radius} L ${item.x + item.width} ${item.y + item.height - radius} Q ${item.x + item.width} ${item.y + item.height} ${item.x + item.width - radius} ${item.y + item.height} L ${item.x + radius} ${item.y + item.height} Q ${item.x} ${item.y + item.height} ${item.x} ${item.y + item.height - radius} L ${item.x} ${item.y + tabHeight + radius} Q ${item.x} ${item.y + tabHeight} ${item.x + radius} ${item.y + tabHeight} Z`}
      transform={rotation}
      vectorEffect="non-scaling-stroke"
    />;
  }

  const frameDashArray = item.props.kind === "frame"
    ? item.props.borderStyle === "dashed" ? "7 5" : item.props.borderStyle === "dotted" ? "1 5" : undefined
    : undefined;
  return <rect
    className={className}
    data-preview-item-id={item.id}
    x={item.x}
    y={item.y}
    width={item.width}
    height={item.height}
    rx={radius}
    transform={rotation}
    strokeDasharray={frameDashArray}
    vectorEffect="non-scaling-stroke"
  />;
}

function SpaceCard({ space, items, index, onOpen, onMenu }: { space: Space; items: CanvasItem[]; index: number; onOpen: () => void; onMenu: (anchor: Point) => void }) {
  const active = items.filter((item) => item.deletedAt === null);
  const folderIds = new Set(active.filter((item) => item.props.kind === "stack").map((item) => item.id));
  const isBlank = active.length === 0;
  const previewItems = active.filter((item) => !item.parentStackId || !folderIds.has(item.parentStackId)).slice(0, SPACE_PREVIEW_ITEM_LIMIT);
  return <article className={`space-card-shell ${index === 0 ? "space-card-shell--feature" : ""}`} data-space-id={space.id} style={{ "--card-delay": `${Math.min(index, 8) * 45}ms` } as CSSProperties}>
    <button className={`space-card ${index === 0 ? "space-card--feature" : ""}`} aria-label={`Open ${space.title}, ${active.length} ${active.length === 1 ? "item" : "items"}`} onClick={onOpen} onContextMenu={(event) => { event.preventDefault(); onMenu({ x: event.clientX, y: event.clientY }); }}>
      <div className={`space-thumbnail space-thumbnail--${isBlank ? "blank" : "occupied"}`}>
        <span className="space-card__number">{String(index + 1).padStart(2, "0")}</span>
        {space.starred && <span className="space-card__star" aria-label="Starred"><Icon name="star"/></span>}
        {isBlank
          ? <div className="space-thumbnail__blank-state" aria-hidden="true"><div className="space-thumbnail__strata"><i/><i/><i/></div><span>Blank space</span></div>
          : <><svg className="space-thumbnail__silhouettes" viewBox={spacePreviewViewBox(previewItems)} preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">{previewItems.map((item) => <SpacePreviewItem key={item.id} item={item}/>)}</svg><div className="space-thumbnail__count" aria-hidden="true"><strong>{active.length}</strong><span>{active.length === 1 ? "item" : "items"}</span></div></>}
      </div>
      <div className="space-card__meta"><strong>{space.title}</strong><span className="space-card__arrow"><Icon name="arrow-up-right"/></span></div>
    </button>
    <IconButton className="space-card__menu-button" label={`Actions for ${space.title}`} icon="more" aria-haspopup="menu" onClick={(event) => { const bounds = event.currentTarget.getBoundingClientRect(); onMenu({ x: bounds.right, y: bounds.bottom + 6 }); }}/>
  </article>;
}

function SpaceActionMenu({ anchor, space, onClose, onOpen, onRename, onToggleStar, onDuplicate, onDelete }: {
  anchor: Point;
  space: Space;
  onClose: () => void;
  onOpen: () => void;
  onRename: (title: string) => void;
  onToggleStar: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  return <ContextMenu anchor={anchor} label="Space actions" className="space-context-menu" onClose={onClose}>{(close) => <>
    <ContextMenuHeader eyebrow="SPACE" title={space.title} closeLabel="Close space menu" onClose={close}/>
    <ContextMenuItem icon="arrow-up-right" label="Open space" description="Enter this canvas" onClick={() => close(onOpen)}/>
    <ContextMenuItem icon="star" label={space.starred ? "Remove from starred" : "Add to starred"} description={space.starred ? "Return to the regular space order" : "Keep this space at the top"} onClick={() => close(onToggleStar)}/>
    <ContextNameEditor inputId={`space-name-${space.id}`} label="Space name" title={space.title} onRename={onRename}/>
    <ContextMenuItem icon="copy" label="Duplicate" description="Copy this space and its items" onClick={() => close(onDuplicate)}/>
    <ContextMenuDivider/>
    <ContextMenuItem icon="trash" label="Move to Trash" description="Remove this space from the library" tone="danger" onClick={() => close(onDelete)}/>
  </>}
  </ContextMenu>;
}

function canvasItemIcon(item: CanvasItem): IconName {
  if (item.props.kind === "sticky-note") return "sticky-note";
  if (item.props.kind === "document") return "note";
  if (item.props.kind === "stack") return "folder";
  if (item.props.kind === "web-clip") return "browser";
  if (item.props.kind === "date-event") return "calendar";
  if (item.props.kind === "label") return "label";
  if (item.props.kind === "image") return "image";
  if (item.props.kind === "pdf") return "pdf";
  if (item.props.kind === "spreadsheet") return "spreadsheet";
  if (item.props.kind === "arrow") return "arrow";
  if (item.props.kind === "frame") return "frame";
  return "video";
}

function itemDisplayName(item: CanvasItem): string {
  if (isSealedItem(item)) return "Locked item";
  if ("title" in item.props) return item.props.title || "Untitled document";
  if ("filename" in item.props) return item.props.filename;
  return "Unsynced item";
}

interface SaveItemOptions {
  /** Allows readable props to replace a locked body. */
  removeLock?: boolean;
}

interface SyncActivity {
  pendingEdits: number;
  loading?: boolean;
  error?: boolean;
  entries: Array<{ id: string; name: string; status: string; failed: boolean }>;
  sessionPending?: boolean;
  sessionError?: SyncTransportError | null;
  retrySession?: () => void;
}

function workingSessionErrorMessage(error: SyncTransportError): string {
  if (error.status === 404) return "Cloud resume is not available on this server yet. The cloud service needs an update. Your place is saved on this device.";
  if (error.code === "invalid_cloud_response" || error.code === "protocol_incompatible") return "The app and cloud service are using incompatible versions. Your place is saved on this device.";
  return `Your place is saved on this device. ${error.message.replace(/[.!?]+$/, "")}.`;
}

function SyncBadge({ state, activity, onSignIn }: { state: SyncStatus; activity: SyncActivity; onSignIn?: (() => Promise<void> | void) | undefined }) {
  const [open, setOpen] = useState(false);
  const [openingSignIn, setOpeningSignIn] = useState(false);
  const [signInFailed, setSignInFailed] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const labels: Record<SyncStatus["phase"], string> = {
    "local-only": "Local only",
    offline: "Offline · saved locally",
    syncing: "Syncing",
    "up-to-date": "Up to date",
    retrying: "Retrying",
    attention: "Sync needs attention"
  };
  const signInRequired = [state.error, activity.sessionError].some((error) => error?.code === "sign_in_required" || error?.code === "unauthorized");
  const contentNeedsAttention = activity.entries.some((entry) => entry.failed) || activity.error;
  const needsAttention = contentNeedsAttention || activity.sessionError;
  const contentPending = activity.pendingEdits > 0 || state.pendingOperations > 0 || state.pendingAssets > 0 || state.pendingClips > 0 || activity.entries.length > 0;
  const hasPending = contentPending || activity.sessionPending;
  const readyToSwitch = state.phase === "up-to-date" && !hasPending && !needsAttention && !activity.loading;
  const onlyPositionFailed = state.phase === "up-to-date" && !contentPending && !contentNeedsAttention && !activity.loading && activity.sessionError;
  const label = signInRequired ? "Sign in to sync" : state.phase !== "local-only" && needsAttention ? "Sync needs attention" : state.phase === "up-to-date" && hasPending ? "Syncing" : labels[state.phase];
  const badgePhase = state.phase === "local-only" ? state.phase : needsAttention ? "attention" : state.phase === "up-to-date" && hasPending ? "syncing" : state.phase;
  const lastSync = state.lastSuccessfulSyncAt === null ? "Not yet" : new Date(state.lastSuccessfulSyncAt).toLocaleString();
  const retry = state.retryAt === null ? null : new Date(state.retryAt).toLocaleTimeString();
  return <div className="sync-badge-wrap" ref={root} onBlur={(event) => { if (!root.current?.contains(event.relatedTarget)) setOpen(false); }}>
    <button type="button" className={`sync-badge sync-badge--${badgePhase} ${signInRequired ? "sync-badge--sign-in-required" : ""}`} aria-label={label} aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((value) => !value)}><i/><span>{label}</span></button>
    {open && <section className="sync-popover" role="dialog" aria-label="Sync details">
      <strong>{signInRequired ? "Your session ended" : label}</strong>
      {signInRequired && <p className="sync-popover__recovery">Your work is saved on this device. Sign in again to resume cloud sync.</p>}
      <p className="sync-popover__readiness" role="status">{state.phase === "local-only" ? "Your work is saved on this device only." : readyToSwitch ? "Everything has reached the cloud. Ready to switch devices." : onlyPositionFailed ? "Your content is synced. Your working position is saved on this device only." : "Keep Bedrock open until your latest work reaches the cloud."}</p>
      {state.phase !== "local-only" && <dl>
        <div><dt>Pending edits</dt><dd>{Math.max(activity.pendingEdits, state.pendingOperations)}</dd></div>
        <div><dt>Media transfers</dt><dd>{state.pendingAssets}</dd></div>
        <div><dt>Web clips</dt><dd>{state.pendingClips}</dd></div>
        <div><dt>Last synced</dt><dd>{lastSync}</dd></div>
        {retry && <div><dt>Next retry</dt><dd>{retry}</dd></div>}
      </dl>}
      {state.phase !== "local-only" && activity.error && <p role="alert">Sync activity could not be checked. Reopen Bedrock to try again.</p>}
      {state.phase !== "local-only" && activity.sessionPending && !activity.sessionError && <p>Saving your place to the cloud…</p>}
      {state.phase !== "local-only" && activity.sessionError && <>
        <p role="alert">{workingSessionErrorMessage(activity.sessionError)}{activity.sessionError.retryable && " Cloud sync will retry automatically."}{activity.sessionError.correlationId && ` · Ref ${activity.sessionError.correlationId}`}</p>
        {!signInRequired && activity.retrySession && <button type="button" className="sync-popover__sign-in" onClick={activity.retrySession}>Retry saving your place<Icon name="undo"/></button>}
      </>}
      {state.phase !== "local-only" && activity.entries.length > 0 && <div className="sync-activity"><h4>Files &amp; activity</h4><ul>{activity.entries.map((entry) => <li key={entry.id} className={entry.failed ? "is-attention" : ""}><strong>{entry.name}</strong><span>{entry.status}</span></li>)}</ul></div>}
      {state.error && !signInRequired && <p>{state.error.message}{state.error.correlationId ? ` · Ref ${state.error.correlationId}` : ""}</p>}
      {signInRequired && onSignIn && <button type="button" className="sync-popover__sign-in" disabled={openingSignIn} onClick={() => {
        setOpeningSignIn(true);
        setSignInFailed(false);
        void Promise.resolve(onSignIn()).catch(() => setSignInFailed(true)).finally(() => setOpeningSignIn(false));
      }}>{openingSignIn ? "Opening browser…" : "Sign in again"}<Icon name="arrow-up-right"/></button>}
      {signInFailed && <p className="sync-popover__sign-in-error" role="alert">The browser could not be opened. Try again.</p>}
    </section>}
  </div>;
}

interface SpaceScreenProps {
  syncBadge?: ReactNode;
  updateState?: DesktopUpdateState | undefined;
  onInstallUpdate?: (() => Promise<void> | void) | undefined;
  onCheckUpdate?: (() => Promise<void> | void) | undefined;
  space: Space | undefined;
  items: CanvasItem[];
  saveItemChanges: (item: CanvasItem, patch: Partial<Pick<CanvasItem, "x" | "y" | "width" | "height" | "rotation" | "zIndex" | "parentStackId" | "props" | "deletedAt">>, options?: SaveItemOptions) => Promise<void>;
  createItem: (spaceId: string, props: ItemProps, position?: Point, preferredSize?: { width: number; height: number }) => Promise<CanvasItem>;
  uploadAsset?: ((input: { assetId: string; filename: string; mimeType: string; blob: Blob }) => Promise<void>) | undefined;
  pasteSystemClipboard?: (() => Promise<void>) | undefined;
  readSystemClipboard?: (() => Promise<SystemClipboardContents>) | undefined;
  cacheAsset: (assetId: string, blob: Blob) => Promise<void>;
  onHistoryAvailabilityChange?: ((availability: HistoryAvailability) => void) | undefined;
  folderLocks: FolderLocks;
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === "INPUT"
    || target.tagName === "TEXTAREA"
    || target.isContentEditable
    || target.closest('[contenteditable="true"], [contenteditable="plaintext-only"]') !== null;
}

function viewportForSearchTarget(item: CanvasItem, width: number, height: number): Viewport {
  const fitted = fitBounds(boundsForRect(item), width, height, Math.min(120, width * .16));
  const scale = Math.min(1.15, fitted.scale);
  return {
    scale,
    x: width / 2 - (item.x + item.width / 2) * scale,
    y: height / 2 - (item.y + item.height / 2) * scale
  };
}

const STACK_FOCUS_VIEWPORT_TRANSITION_MS = 420;

function SpaceScreen({ space, syncBadge, updateState, onInstallUpdate, onCheckUpdate, items, saveItemChanges, createItem, uploadAsset, pasteSystemClipboard, readSystemClipboard, cacheAsset, onHistoryAvailabilityChange, folderLocks }: SpaceScreenProps) {
  const setScreen = useShell((state) => state.setScreen);
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const setSearchOpen = useShell((state) => state.setSearchOpen);
  const setSettingsOpen = useShell((state) => state.setSettingsOpen);
  const addOpen = useShell((state) => state.addOpen);
  const setAddOpen = useShell((state) => state.setAddOpen);
  const editingItemId = useShell((state) => state.editorItemId);
  const selectedIds = useShell((state) => state.selectedIds);
  const setSelectedIds = useShell((state) => state.setSelectedIds);
  const searchTargetItemId = useShell((state) => state.searchTargetItemId);
  const setSearchTargetItemId = useShell((state) => state.setSearchTargetItemId);
  const shortcutBindings = useShell((state) => state.shortcutBindings);
  const controlScheme = useShell((state) => state.controlScheme);
  const [viewport, setViewport] = useState<Viewport>({ x: 0, y: 0, scale: 1 });
  const [ready, setReady] = useState(false);
  const [surfaceSize, setSurfaceSize] = useState({ width: 1, height: 1 });
  const [addMenuView, setAddMenuView] = useState<"menu" | "web-clip" | "date-event">("menu");
  const [contextMenu, setContextMenu] = useState<
    | { kind: "canvas"; anchor: Point }
    | { kind: "item"; anchor: Point; itemId: string }
    | null
  >(null);
  const [revealingStackId, setRevealingStackId] = useState<string | null>(null);
  const [closingStackId, setClosingStackId] = useState<string | null>(null);
  const [viewportTransitioning, setViewportTransitioning] = useState(false);
  const { exiting: addMenuExiting, requestExit: closeAddMenu } = useExitAnimation(() => setAddOpen(false));
  const surfaceRef = useRef<HTMLDivElement>(null);
  const systemClipboardPasteInFlight = useRef<Promise<void> | null>(null);
  const viewportTransitionFrame = useRef<number | null>(null);
  const viewportTransitionTimeout = useRef<number | null>(null);
  const stackFocusViewports = useRef(new Map<string, Viewport>());
  const history = useRef<Array<{ before: CanvasItem[]; after: CanvasItem[] }>>([]);
  const future = useRef<Array<{ before: CanvasItem[]; after: CanvasItem[] }>>([]);
  const [lockRequest, setLockRequest] = useState<{ itemId: string; mode: "lock" | "remove" | "unlock" } | null>(null);
  const { openFolderIds, closeFolder, expandFolder, storedItem } = folderLocks;
  const [historyAvailability, setHistoryAvailability] = useState<HistoryAvailability>({ canUndo: false, canRedo: false });
  const internalClipboard = useRef<CanvasItem[]>([]);
  const activeItems = items.filter((item) => item.deletedAt === null);
  const collapsedStacks = new Set(activeItems.filter((item) => item.props.kind === "stack" && item.props.collapsed).map((item) => item.id));
  const canvasItems = activeItems.filter((item) => item.parentStackId === null || !collapsedStacks.has(item.parentStackId));
  const focusedStack = canvasItems.find((item) => item.props.kind === "stack" && !item.props.collapsed);
  const selected = activeItems.filter((item) => selectedIds.includes(item.id));
  const menuItem = contextMenu?.kind === "item" ? activeItems.find((item) => item.id === contextMenu.itemId) : undefined;
  const lockRequestItem = lockRequest ? activeItems.find((item) => item.id === lockRequest.itemId) : undefined;
  const [lockNotice, setLockNotice] = useState<string | null>(null);
  const revealingStackCandidate = revealingStackId ? activeItems.find((item) => item.id === revealingStackId) : undefined;
  const revealingStack = revealingStackCandidate?.props.kind === "stack" && !revealingStackCandidate.props.collapsed ? revealingStackCandidate : undefined;
  const revealingChildCount = revealingStack ? activeItems.filter((item) => item.parentStackId === revealingStack.id).length : 0;
  const closingStackCandidate = closingStackId ? activeItems.find((item) => item.id === closingStackId) : undefined;
  const closingStack = closingStackCandidate?.props.kind === "stack" && !closingStackCandidate.props.collapsed ? closingStackCandidate : undefined;
  const closingChildCount = closingStack ? activeItems.filter((item) => item.parentStackId === closingStack.id).length : 0;

  const cancelViewportTransition = useCallback(() => {
    if (viewportTransitionFrame.current !== null) window.cancelAnimationFrame(viewportTransitionFrame.current);
    if (viewportTransitionTimeout.current !== null) window.clearTimeout(viewportTransitionTimeout.current);
    viewportTransitionFrame.current = null;
    viewportTransitionTimeout.current = null;
    setViewportTransitioning(false);
  }, []);

  const transitionViewport = useCallback((nextViewport: Viewport) => {
    cancelViewportTransition();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setViewport(nextViewport);
      return;
    }
    setViewportTransitioning(true);
    viewportTransitionFrame.current = window.requestAnimationFrame(() => {
      viewportTransitionFrame.current = null;
      setViewport(nextViewport);
      viewportTransitionTimeout.current = window.setTimeout(() => {
        viewportTransitionTimeout.current = null;
        setViewportTransitioning(false);
      }, STACK_FOCUS_VIEWPORT_TRANSITION_MS);
    });
  }, [cancelViewportTransition]);

  useEffect(() => () => {
    if (viewportTransitionFrame.current !== null) window.cancelAnimationFrame(viewportTransitionFrame.current);
    if (viewportTransitionTimeout.current !== null) window.clearTimeout(viewportTransitionTimeout.current);
  }, []);

  useEffect(() => {
    onHistoryAvailabilityChange?.(historyAvailability);
  }, [historyAvailability, onHistoryAvailabilityChange]);

  useEffect(() => () => {
    onHistoryAvailabilityChange?.({ canUndo: false, canRedo: false });
  }, [onHistoryAvailabilityChange]);

  useEffect(() => {
    if (!surfaceRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setSurfaceSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(surfaceRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!revealingStackId || !revealingStack) return;
    if (revealingChildCount === 0) {
      setRevealingStackId(null);
      return;
    }
    const timeout = window.setTimeout(() => setRevealingStackId(null), stackRevealTotalDuration(revealingChildCount));
    return () => window.clearTimeout(timeout);
  }, [revealingChildCount, revealingStack, revealingStackId]);

  useEffect(() => {
    if (!ready && !searchTargetItemId && surfaceSize.width > 10) {
      setViewport(fitBounds(boundsForItems(canvasItems), surfaceSize.width, surfaceSize.height, surfaceSize.width < 600 ? 36 : 96));
      setReady(true);
    }
  }, [canvasItems, ready, searchTargetItemId, surfaceSize]);

  const persistSet = useCallback(async (before: CanvasItem[], after: CanvasItem[], record = true) => {
    if (record) {
      history.current.push({ before, after });
      if (history.current.length > 80) history.current.shift();
      future.current = [];
      setHistoryAvailability({ canUndo: true, canRedo: false });
    }
    for (const candidate of after) {
      const previous = before.find((item) => item.id === candidate.id) ?? candidate;
      // Items dropped into a locked folder are sealed as they land, but nothing leaves a closed
      // one (as undo might try): outside their folder, sealed items couldn't be opened.
      const origin = candidate.parentStackId !== previous.parentStackId && previous.parentStackId ? items.find((item) => item.id === previous.parentStackId) : undefined;
      const next = origin && isClosedLockedFolder(origin) ? { ...candidate, parentStackId: previous.parentStackId } : candidate;
      const patch: Record<string, unknown> = {};
      for (const field of ["x", "y", "width", "height", "rotation", "zIndex", "parentStackId", "props", "deletedAt"] as const) {
        if (JSON.stringify(previous[field]) !== JSON.stringify(next[field])) patch[field] = next[field];
      }
      if (Object.keys(patch).length) await saveItemChanges(previous, patch);
    }
  }, [items, saveItemChanges]);

  useEffect(() => {
    if (!closingStackId) return;
    if (!closingStack) {
      setClosingStackId(null);
      return;
    }

    const finishClosing = () => {
      const stack = closingStack;
      if (isLockedFolder(stack) && editingItemId && items.some((item) => item.id === editingItemId && item.parentStackId === stack.id)) setEditorItemId(null);
      void closeFolder(stack.id);
    };

    const collapseDuration = stackCollapseTotalDuration(closingChildCount, window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    if (collapseDuration === 0) {
      finishClosing();
      return;
    }

    const timeout = window.setTimeout(finishClosing, collapseDuration);
    return () => window.clearTimeout(timeout);
  }, [closeFolder, closingChildCount, closingStack, closingStackId, editingItemId, items, setEditorItemId]);

  const undo = useCallback(async () => {
    const entry = history.current.pop();
    if (!entry) return;
    future.current.push(entry);
    setHistoryAvailability({ canUndo: history.current.length > 0, canRedo: true });
    await persistSet(entry.after, entry.before, false);
  }, [persistSet]);

  const redo = useCallback(async () => {
    const entry = future.current.pop();
    if (!entry) return;
    history.current.push(entry);
    setHistoryAvailability({ canUndo: true, canRedo: future.current.length > 0 });
    await persistSet(entry.before, entry.after, false);
  }, [persistSet]);

  const duplicate = useCallback(async (source = selected) => {
    const created: CanvasItem[] = [];
    for (const item of source) {
      // A copy of something decrypted from an open folder stays encrypted and inside that folder.
      const stored = storedItem(item.id);
      const sealed = stored && isSealedItem(stored) ? stored : undefined;
      const props = structuredClone(sealed?.props ?? item.props);
      const clone = await createItem(item.spaceId, props, { x: item.x + 28, y: item.y + 28 });
      await saveItemChanges(clone, { width: item.width, height: item.height, rotation: item.rotation, ...(sealed ? { parentStackId: sealed.parentStackId } : {}) });
      created.push(clone);
    }
    setSelectedIds(created.map((item) => item.id));
  }, [createItem, saveItemChanges, selected, setSelectedIds, storedItem]);

  const removeSelected = useCallback(async () => {
    const after = selected.map((item) => ({ ...item, deletedAt: Date.now() }));
    await persistSet(selected, after);
    setSelectedIds([]);
  }, [persistSet, selected, setSelectedIds]);

  const arrange = useCallback(async (direction: "front" | "back") => {
    const extreme = direction === "front" ? Math.max(...activeItems.map((item) => item.zIndex), 0) + 1 : Math.min(...activeItems.map((item) => item.zIndex), 0) - selected.length;
    const after = selected.map((item, index) => ({ ...item, zIndex: extreme + index }));
    await persistSet(selected, after);
  }, [activeItems, persistSet, selected]);

  const toggleStack = useCallback(async (stack: CanvasItem, unlocked = false) => {
    if (stack.props.kind !== "stack") return;
    const opening = unlocked || stack.props.collapsed;
    const lockedFolder = isLockedFolder(stack);
    if (opening && lockedFolder && !unlocked && !openFolderIds.has(stack.id)) {
      setLockRequest({ itemId: stack.id, mode: "unlock" });
      return;
    }
    const children = orderedStackChildren(activeItems, stack.id);

    setRevealingStackId(opening ? stack.id : null);
    if (!opening) {
      const previousViewport = stackFocusViewports.current.get(stack.id);
      stackFocusViewports.current.delete(stack.id);
      if (previousViewport) transitionViewport(previousViewport);
      setClosingStackId(stack.id);
      return;
    }

    setClosingStackId((current) => current === stack.id ? null : current);
    stackFocusViewports.current.set(stack.id, viewport);
    const arrangedChildren = arrangeStackChildren(stack, children);
    if (!searchTargetItemId && surfaceSize.width > 10 && surfaceSize.height > 10) {
      const focusedViewport = viewportForStackFocus(
        viewport,
        stack,
        arrangedChildren,
        surfaceSize.width,
        surfaceSize.height,
        surfaceSize.width < 600 ? 36 : 96
      );
      if (focusedViewport) transitionViewport(focusedViewport);
    }
    // Whether the folder is open stays on this device, so only children that actually moved are stored.
    const moved = arrangedChildren.filter((child, index) => child.x !== children[index]!.x || child.y !== children[index]!.y);
    try {
      if (moved.length > 0) await persistSet(children.filter((child) => moved.some((candidate) => candidate.id === child.id)), moved);
    } finally {
      if (!lockedFolder) expandFolder(stack.id);
    }
  }, [activeItems, expandFolder, openFolderIds, persistSet, searchTargetItemId, surfaceSize, transitionViewport, viewport]);

  useEffect(() => {
    if (!searchTargetItemId) return;
    const item = activeItems.find((candidate) => candidate.id === searchTargetItemId);
    if (!item) {
      setSearchTargetItemId(null);
      return;
    }
    const parent = item.parentStackId ? activeItems.find((candidate) => candidate.id === item.parentStackId) : undefined;
    if (parent?.props.kind === "stack" && parent.props.collapsed) {
      if (revealingStackId !== parent.id) void toggleStack(parent);
      return;
    }
    if (surfaceSize.width <= 10 || surfaceSize.height <= 10) return;
    setViewport(viewportForSearchTarget(item, surfaceSize.width, surfaceSize.height));
    setReady(true);
    if (item.props.kind === "arrow" || item.props.kind === "frame" || item.props.kind === "stack") {
      setSelectedIds([item.id]);
    } else {
      setSelectedIds([]);
      setEditorItemId(item.id);
    }
    setSearchTargetItemId(null);
  }, [activeItems, revealingStackId, searchTargetItemId, setEditorItemId, setSearchTargetItemId, setSelectedIds, surfaceSize, toggleStack]);

  const openItem = useCallback((item: CanvasItem) => {
    setSelectedIds([]);
    if (item.props.kind === "stack") void toggleStack(item);
    else if (item.props.kind === "arrow" || item.props.kind === "frame") setSelectedIds([item.id]);
    // A locked item asks for its PIN first, so there is no editor yet for the card to morph into.
    else if (item.props.kind === "label" || item.props.kind === "sticky-note" || isLockedItem(item)) setEditorItemId(item.id);
    else runViewTransition(() => setEditorItemId(item.id), item.id);
  }, [setEditorItemId, setSelectedIds, toggleStack]);

  const focusItemInCanvas = useCallback((id: string) => {
    const item = activeItems.find((candidate) => candidate.id === id);
    if (!item || surfaceSize.width <= 10 || surfaceSize.height <= 10) return;
    transitionViewport(viewportForSearchTarget(item, surfaceSize.width, surfaceSize.height));
  }, [activeItems, surfaceSize, transitionViewport]);

  const importFile = useCallback(async (file: File, point: Point) => {
    if (!space || file.size > 500 * 1024 * 1024) return;
    if (file.type === "text/markdown" || file.type === "text/plain" || /\.(md|markdown|txt)$/i.test(file.name)) {
      const markdown = await file.text();
      await createItem(space.id, { kind: "document", title: file.name.replace(/\.(md|markdown|txt)$/i, "") || "Imported document", markdown, plainText: markdownToPlainText(markdown), color: "paper" }, point);
      return;
    }
    const assetId = makeId("asset");
    const localUrl = URL.createObjectURL(file);
    const props = canvasAssetPropsForFile(file, assetId, uploadAsset ? "uploading" : "local", localUrl);
    if (!props) {
      URL.revokeObjectURL(localUrl);
      return;
    }
    await cacheAsset(assetId, file);
    const preferredSize = props.kind === "image" ? await canvasSizeForImageFile(file) ?? undefined : undefined;
    await createItem(space.id, props, point, preferredSize);
  }, [cacheAsset, createItem, space, uploadAsset]);

  const openFilePicker = useCallback((mediaOnly: boolean) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = !mediaOnly;
    input.accept = mediaOnly ? ASSET_FILE_ACCEPT : IMPORTABLE_FILE_ACCEPT;
    input.addEventListener("change", () => {
      const origin = screenToWorld({ x: surfaceSize.width / 2, y: surfaceSize.height / 2 }, viewport);
      [...(input.files ?? [])].forEach((file, index) => void importFile(file, { x: origin.x + index * 28, y: origin.y + index * 28 }));
    }, { once: true });
    input.click();
  }, [importFile, surfaceSize.height, surfaceSize.width, viewport]);

  const createWebClip = useCallback(async (url: URL, point?: Point) => {
    if (!space) throw new Error("This space is unavailable.");
    const clipId = makeId("clip");
    return createItem(space.id, { kind: "web-clip", clipId, url: url.toString(), title: "Capturing page…", description: "A static preview is being prepared.", siteName: url.hostname.toUpperCase(), status: "pending", previewAssetId: null }, point);
  }, [createItem, space]);

  const fitCanvas = useCallback(() => {
    transitionViewport(fitBounds(boundsForItems(canvasItems), surfaceSize.width, surfaceSize.height, 96));
  }, [canvasItems, surfaceSize.height, surfaceSize.width, transitionViewport]);

  const createShortcutItem = useCallback((kind: "document" | "sticky-note" | "arrow" | "frame" | "stack" | "label") => {
    if (!space) return;
    const point = screenToWorld({ x: surfaceSize.width / 2, y: surfaceSize.height / 2 }, viewport);
    if (kind === "document") void createItem(space.id, { kind: "document", title: "Untitled document", markdown: "", plainText: "", color: "paper" }, point);
    if (kind === "sticky-note") void createItem(space.id, { kind: "sticky-note", markdown: "", plainText: "", accent: "paper" }, point);
    if (kind === "arrow") void createItem(space.id, { kind: "arrow", path: "straight", color: "ink", stroke: "solid", head: "filled" }, point);
    if (kind === "frame") void createItem(space.id, { kind: "frame", title: "New frame", color: "clear", backgroundStyle: "soft", borderStyle: "solid", borderColor: "match" }, point);
    if (kind === "stack") void createItem(space.id, { kind: "stack", title: "New folder", collapsed: true, color: "lilac" }, point);
    if (kind === "label") void createItem(space.id, { kind: "label", text: "New thought", size: "large" }, point);
  }, [createItem, space, surfaceSize.height, surfaceSize.width, viewport]);

  const performCanvasShortcut = useCallback((shortcut: CanvasShortcutId): boolean => {
    if (isMarkdownEditorShortcutAction(shortcut)) return false;
    const needsSelection = shortcut === "open" || shortcut === "copy" || shortcut === "duplicate" || shortcut === "bring-front" || shortcut === "send-back" || shortcut === "delete";
    if (needsSelection && selected.length === 0) return false;
    if (shortcut === "open" && selected.length !== 1) return false;
    setContextMenu(null);
    if (shortcut !== "new-web-clip" && shortcut !== "new-date-event") setAddOpen(false);
    switch (shortcut) {
      case "new-document": createShortcutItem("document"); break;
      case "new-note": createShortcutItem("sticky-note"); break;
      case "new-date-event": setAddMenuView("date-event"); setAddOpen(true); break;
      case "new-arrow": createShortcutItem("arrow"); break;
      case "new-frame": createShortcutItem("frame"); break;
      case "new-folder": createShortcutItem("stack"); break;
      case "new-web-clip": setAddMenuView("web-clip"); setAddOpen(true); break;
      case "new-media": openFilePicker(true); break;
      case "new-label": createShortcutItem("label"); break;
      case "open": {
        const item = selected[0];
        if (item) openItem(item);
        break;
      }
      case "select-all": setSelectedIds(activeItems.map((item) => item.id)); break;
      case "undo": if (!historyAvailability.canUndo) return false; void undo(); break;
      case "redo": if (!historyAvailability.canRedo) return false; void redo(); break;
      case "copy": internalClipboard.current = selected.map((item) => structuredClone(item)); break;
      case "paste": if (internalClipboard.current.length > 0) void duplicate(internalClipboard.current); break;
      case "duplicate": void duplicate(); break;
      case "bring-front": void arrange("front"); break;
      case "send-back": void arrange("back"); break;
      case "delete": void removeSelected(); break;
      case "fit": fitCanvas(); break;
    }
    return true;
  }, [activeItems, arrange, createShortcutItem, duplicate, fitCanvas, historyAvailability, openFilePicker, openItem, redo, removeSelected, selected, setAddOpen, setSelectedIds, undo]);

  const applyClipboardPayload = useCallback(async (payload: ClipboardPastePayload) => {
    if (!space) return;
    const point = screenToWorld({ x: surfaceSize.width / 2, y: surfaceSize.height / 2 }, viewport);
    if (payload.kind === "files") {
      for (const [index, file] of payload.files.entries()) {
        try {
          await importFile(file, { x: point.x + index * 28, y: point.y + index * 28 });
        } catch { /* keep importing the remaining clipboard files */ }
      }
      return;
    }
    if (payload.kind === "url") await createWebClip(payload.url, point);
  }, [createWebClip, importFile, space, surfaceSize.height, surfaceSize.width, viewport]);

  const pasteFromSystemClipboard = useCallback(() => {
    if (systemClipboardPasteInFlight.current) return systemClipboardPasteInFlight.current;
    const paste = (async () => {
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (pasteSystemClipboard && isTextEditingTarget(activeElement)) {
        try {
          await pasteSystemClipboard();
        } catch (error) {
          console.error("Native clipboard paste failed", error);
        }
        return;
      }
      if (pasteSystemClipboard) {
        const previousFocus = activeElement;
        const target = document.createElement("div");
        target.contentEditable = "true";
        target.tabIndex = -1;
        target.setAttribute("aria-hidden", "true");
        Object.assign(target.style, { position: "fixed", width: "1px", height: "1px", opacity: "0", pointerEvents: "none" });
        let imported: Promise<void> | undefined;
        let didPaste = false;
        const onPaste = (event: ClipboardEvent) => {
          didPaste = true;
          event.preventDefault();
          const payload = classifyClipboardPaste(event.clipboardData);
          if (payload) imported = applyClipboardPayload(payload);
        };
        target.addEventListener("paste", onPaste, { once: true });
        document.body.append(target);
        target.focus();
        try {
          await pasteSystemClipboard();
          await new Promise((resolve) => setTimeout(resolve, 0));
          if (imported) {
            await imported;
            return;
          }
          if (didPaste) return;
        } catch (error) {
          console.error("Native clipboard paste failed", error);
        } finally {
          target.remove();
          if (previousFocus?.isConnected) previousFocus.focus();
        }
      }
      if (!readSystemClipboard) return;
      try {
        const payload = classifySystemClipboardContents(await readSystemClipboard());
        if (payload) await applyClipboardPayload(payload);
      } catch (error) {
        console.error("Clipboard read fallback failed", error);
      }
    })();
    systemClipboardPasteInFlight.current = paste;
    void paste.finally(() => {
      if (systemClipboardPasteInFlight.current === paste) systemClipboardPasteInFlight.current = null;
    });
    return paste;
  }, [applyClipboardPayload, pasteSystemClipboard, readSystemClipboard]);

  useEffect(() => {
    if (!space) return;
    const onPaste = (event: ClipboardEvent) => {
      if (isTextEditingTarget(event.target)) return;
      const payload = classifyClipboardPaste(event.clipboardData);
      if (!payload) return;
      event.preventDefault();
      void applyClipboardPayload(payload);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [applyClipboardPayload, space]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing || event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedIds([]);
        if (addOpen) closeAddMenu();
        setEditorItemId(null);
        return;
      }
      if (event.repeat) return;
      if (isSystemClipboardPasteShortcut(event)) {
        if (readSystemClipboard) {
          event.preventDefault();
          void pasteFromSystemClipboard();
        }
        return;
      }
      if (isTextEditingTarget(event.target)) return;
      const shortcut = canvasShortcutForEvent(event, undefined, shortcutBindings);
      if (shortcut && performCanvasShortcut(shortcut)) event.preventDefault();
    };
    const onCommand = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      if (detail === "paste" && isTextEditingTarget(document.activeElement)) return;
      const shortcut = CANVAS_SHORTCUT_ACTIONS.find((action) => action.id === detail);
      if (shortcut) performCanvasShortcut(shortcut.id);
      if (detail === "import") openFilePicker(false);
      if (detail === "paste-from-os") void pasteFromSystemClipboard();
    };
    const onImportFiles = (event: Event) => {
      const files = (event as CustomEvent<File[]>).detail;
      if (!Array.isArray(files)) return;
      const origin = screenToWorld({ x: surfaceSize.width / 2, y: surfaceSize.height / 2 }, viewport);
      files.forEach((file, index) => void importFile(file, { x: origin.x + index * 28, y: origin.y + index * 28 }));
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("bedrock:command", onCommand);
    window.addEventListener("bedrock:import-files", onImportFiles);
    return () => { window.removeEventListener("keydown", onKeyDown); window.removeEventListener("bedrock:command", onCommand); window.removeEventListener("bedrock:import-files", onImportFiles); };
  }, [addOpen, closeAddMenu, importFile, openFilePicker, pasteFromSystemClipboard, performCanvasShortcut, readSystemClipboard, setEditorItemId, setSelectedIds, shortcutBindings, surfaceSize.height, surfaceSize.width, viewport]);

  if (!space) return <div className="missing-space"><p>This space is unavailable.</p><Button onClick={() => setScreen({ kind: "library" })}>Back to library</Button></div>;

  const centerWorld = screenToWorld({ x: surfaceSize.width / 2, y: surfaceSize.height / 2 }, viewport);

  return <section className="space-screen">
    <CanvasSurface ref={surfaceRef} items={canvasItems} allItems={activeItems} revealingStack={revealingStack} closingStack={closingStack} viewport={viewport} setViewport={setViewport} viewportTransitioning={viewportTransitioning} onViewportInteraction={cancelViewportTransition} controlScheme={controlScheme} surfaceSize={surfaceSize} selectedIds={selectedIds} editingItemId={editingItemId} setSelectedIds={setSelectedIds} saveItemChanges={saveItemChanges} persistSet={persistSet} onCloseStack={toggleStack} onDropFile={importFile} onClearEditing={() => setEditorItemId(null)} onCanvasMenu={(anchor) => {
      setEditorItemId(null);
      setAddOpen(false);
      setSelectedIds([]);
      setContextMenu({ kind: "canvas", anchor });
    }} onItemMenu={(id, anchor) => {
      setEditorItemId(null);
      setAddOpen(false);
      setSelectedIds([id]);
      setContextMenu({ kind: "item", anchor, itemId: id });
    }} onFocusItem={focusItemInCanvas} onOpenItem={(id) => {
      const item = activeItems.find((candidate) => candidate.id === id);
      if (!item) return;
      openItem(item);
    }}/>

    <h1 className="space-enter-title">{space.title}</h1>
    <FloatingPanel className="space-top-left"><IconButton label={focusedStack ? "Close folder" : "Back to spaces"} icon="arrow-left" onClick={() => {
      if (focusedStack) {
        void toggleStack(focusedStack);
        return;
      }
      runViewTransition(() => setScreen({ kind: "library" }));
    }}/></FloatingPanel>
    <FloatingPanel className="space-top-right">
      {updateState && onInstallUpdate && isActionableUpdateState(updateState) && <DashboardUpdateButton state={updateState} onInstall={onInstallUpdate} className="workspace-update-button"/>}
      {updateState?.phase === "error" && !updateState.update && onCheckUpdate && <button type="button" className="library-update-button workspace-update-button library-update-button--error" aria-label="Retry update check" title={updateState.message} onClick={() => void Promise.resolve(onCheckUpdate())}><Icon name="download"/><span>Retry update check</span></button>}
      <IconButton label="Search" icon="search" onClick={() => setSearchOpen(true)}/>
    </FloatingPanel>

    <FloatingPanel className="space-bottom-left">
      {syncBadge}
      <IconButton label="Settings" icon="sliders" onClick={() => setSettingsOpen(true)}/>
      <div className="history-controls" aria-label="History controls"><IconButton label={`Undo (${canvasShortcutLabel("undo", undefined, shortcutBindings)})`} tooltip="Undo" tooltipShortcut={canvasShortcutLabel("undo", undefined, shortcutBindings)} aria-keyshortcuts={canvasShortcutAria("undo", undefined, shortcutBindings)} icon="undo" disabled={!historyAvailability.canUndo} onClick={() => void undo()}/><IconButton label={`Redo (${canvasShortcutLabel("redo", undefined, shortcutBindings)})`} tooltip="Redo" tooltipShortcut={canvasShortcutLabel("redo", undefined, shortcutBindings)} aria-keyshortcuts={canvasShortcutAria("redo", undefined, shortcutBindings)} icon="redo" disabled={!historyAvailability.canRedo} onClick={() => void redo()}/></div>
    </FloatingPanel>
    <div className="space-bottom-right">
      <Tooltip label="Center canvas" shortcut={canvasShortcutLabel("fit", undefined, shortcutBindings)}><button className="fit-elements-button" type="button" onClick={fitCanvas} aria-label="Center canvas on elements" aria-keyshortcuts={canvasShortcutAria("fit", undefined, shortcutBindings)}><Icon name="fit-view"/></button></Tooltip>
      <Tooltip label="Add to space"><button className={`add-button ${addOpen ? "is-open" : ""}`} aria-label="Add to space" onClick={() => { setContextMenu(null); if (addOpen) closeAddMenu(); else { setAddMenuView("menu"); setAddOpen(true); } }}><Icon name="plus"/></button></Tooltip>
    </div>
    {addOpen && <AddMenu spaceId={space.id} point={centerWorld} initialView={addMenuView} createItem={createItem} createWebClip={createWebClip} uploadAsset={uploadAsset} cacheAsset={cacheAsset} closing={addMenuExiting} onClose={closeAddMenu}/>}
    {contextMenu?.kind === "canvas" && <AddMenu key={`canvas-${contextMenu.anchor.x}-${contextMenu.anchor.y}`} spaceId={space.id} point={screenToWorld(contextMenu.anchor, viewport)} anchor={contextMenu.anchor} createItem={createItem} createWebClip={createWebClip} uploadAsset={uploadAsset} cacheAsset={cacheAsset} onClose={() => setContextMenu(null)}/>}
    {menuItem && contextMenu?.kind === "item" && <ItemActionMenu key={`item-${menuItem.id}-${contextMenu.anchor.x}-${contextMenu.anchor.y}`} anchor={contextMenu.anchor} item={menuItem} allItems={activeItems} onClose={() => setContextMenu(null)} onEdit={() => openItem(menuItem)} onRenameFolder={(title) => { if (menuItem.props.kind === "stack") void saveItemChanges(menuItem, { props: { ...menuItem.props, title } }); }} onChangeFolderColor={(color) => { if (menuItem.props.kind === "stack") void saveItemChanges(menuItem, { props: { ...menuItem.props, color } }); }} onChangeStickyColor={(accent) => { if (menuItem.props.kind === "sticky-note") void saveItemChanges(menuItem, { props: { ...menuItem.props, accent } }); }} onChangeDocumentColor={(color) => { if (menuItem.props.kind === "document") void saveItemChanges(menuItem, { props: { ...menuItem.props, color } }); }} onChangeDateEventColor={(color) => { if (menuItem.props.kind === "date-event") void saveItemChanges(menuItem, { props: { ...menuItem.props, color } }); }} onChangeArrow={(props) => { if (menuItem.props.kind === "arrow") void saveItemChanges(menuItem, { props }); }} onChangeFrame={(props) => { if (menuItem.props.kind === "frame") void saveItemChanges(menuItem, { props }); }} onDuplicate={() => void duplicate([menuItem])} onMoveLayer={(direction) => { const zIndex = direction === "front" ? Math.max(...activeItems.map((item) => item.zIndex), 0) + 1 : Math.min(...activeItems.map((item) => item.zIndex), 0) - 1; void saveItemChanges(menuItem, { zIndex }); }} onUnstack={() => void saveItemChanges(menuItem, { parentStackId: null })} onLock={() => setLockRequest({ itemId: menuItem.id, mode: "lock" })} onRemoveLock={() => setLockRequest({ itemId: menuItem.id, mode: "remove" })} onDelete={(withContents) => {
      void (async () => {
        if (menuItem.props.kind === "stack" || menuItem.props.kind === "frame") {
          for (const child of activeItems.filter((item) => item.parentStackId === menuItem.id)) {
            await saveItemChanges(child, withContents ? { deletedAt: Date.now() } : { parentStackId: null });
          }
        }
        await saveItemChanges(menuItem, { deletedAt: Date.now() });
      })();
    }}/>} 
    {lockRequest && lockRequestItem && <NoteLockDialog key={`${lockRequest.mode}-${lockRequestItem.id}`} mode={lockRequest.mode} itemTitle={lockedItemTitle(lockRequestItem)} folder={lockRequestItem.props.kind === "stack"} onCancel={() => setLockRequest(null)} onSubmit={async (pin) => {
      if (lockRequestItem.props.kind === "stack") {
        const failure = lockRequest.mode === "lock" ? await folderLocks.lockFolder(lockRequestItem, pin)
          : lockRequest.mode === "remove" ? await folderLocks.removeFolderLock(lockRequestItem, pin)
            : await folderLocks.openFolder(lockRequestItem, pin);
        if (failure === WRONG_PIN_MESSAGE) return failure;
        setLockRequest(null);
        if (lockRequest.mode === "unlock" && !failure) void toggleStack(lockRequestItem, true);
        if (failure) setLockNotice(failure);
        return null;
      }
      const props = noteProps(lockRequestItem);
      if (!props) return null;
      if (lockRequest.mode === "lock") {
        const locked = await lockNote(pin, props.markdown);
        await saveItemChanges(lockRequestItem, { props: withLockedBody(props, locked.lock) });
      } else {
        if (!props.lock) return null;
        const opened = await unlockNote(pin, props.lock);
        if (!opened) return WRONG_PIN_MESSAGE;
        await saveItemChanges(lockRequestItem, { props: withReadableBody(props, opened.markdown) }, { removeLock: true });
      }
      setLockRequest(null);
      return null;
    }}/>}
    {lockNotice && <div className="lock-notice" role="status"><Icon name="lock"/><span>{lockNotice}</span><button type="button" onClick={() => setLockNotice(null)}>OK</button></div>}
  </section>;
}

interface CanvasSurfaceProps {
  items: CanvasItem[];
  allItems: CanvasItem[];
  revealingStack: CanvasItem | undefined;
  closingStack: CanvasItem | undefined;
  viewport: Viewport;
  setViewport: Dispatch<SetStateAction<Viewport>>;
  viewportTransitioning: boolean;
  onViewportInteraction: () => void;
  controlScheme: CanvasControlScheme;
  surfaceSize: { width: number; height: number };
  selectedIds: string[];
  editingItemId: string | null;
  setSelectedIds: (ids: string[]) => void;
  saveItemChanges: SpaceScreenProps["saveItemChanges"];
  persistSet: (before: CanvasItem[], after: CanvasItem[], record?: boolean) => Promise<void>;
  onCloseStack: (stack: CanvasItem) => Promise<void>;
  onFocusItem: (id: string) => void;
  onOpenItem: (id: string) => void;
  onCanvasMenu: (anchor: Point) => void;
  onItemMenu: (id: string, anchor: Point) => void;
  onClearEditing: () => void;
  onDropFile: (file: File, point: Point) => Promise<void>;
}

const CanvasSurface = function CanvasSurface({ items, allItems, revealingStack, closingStack, viewport, setViewport, viewportTransitioning, onViewportInteraction, controlScheme, surfaceSize, selectedIds, editingItemId, setSelectedIds, saveItemChanges, persistSet, onFocusItem, onOpenItem, onCanvasMenu, onItemMenu, onCloseStack, onClearEditing, onDropFile, ref: externalRef }: CanvasSurfaceProps & { ref?: Ref<HTMLDivElement> }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const setRefs = (node: HTMLDivElement | null) => {
    ref.current = node;
    if (typeof externalRef === "function") externalRef(node);
    else if (externalRef) externalRef.current = node;
  };
  const pan = useRef<{ pointerId: number; last: Point; time: number; velocity: Point } | null>(null);
  const inertiaFrame = useRef<number | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const pinch = useRef<{ distance: number; viewport: Viewport; centroid: Point } | null>(null);
  const marquee = useRef<{ pointerId: number; start: Point; selectionBefore: string[] } | null>(null);
  const [marqueeBox, setMarqueeBox] = useState<{ start: Point; current: Point } | null>(null);
  const [folderDropFeedback, setFolderDropFeedback] = useState<(CanvasFolderDropIntent & { point: Point }) | null>(null);
  const [livePreviewGroups, setLivePreviewGroups] = useState(new Map<string, CanvasItem[]>());
  const setLivePreviewGroup = useCallback((ownerId: string, previews: CanvasItem[] | null) => {
    setLivePreviewGroups((current) => {
      if (previews?.length) {
        const previewIds = new Set(previews.map((preview) => preview.id));
        const next = new Map(current);
        for (const [existingOwnerId, existingPreviews] of next) {
          if (existingPreviews.some((preview) => previewIds.has(preview.id))) next.delete(existingOwnerId);
        }
        next.set(ownerId, previews);
        return next;
      }
      if (!previews && !current.has(ownerId)) return current;
      const next = new Map(current);
      next.delete(ownerId);
      return next;
    });
  }, []);
  useEffect(() => {
    const persistedById = new Map(allItems.map((item) => [item.id, item]));
    setLivePreviewGroups((current) => {
      let changed = false;
      const next = new Map(current);
      for (const [ownerId, previews] of current) {
        if (!previews.every((preview) => {
          const persisted = persistedById.get(preview.id);
          return persisted ? hasSameCanvasLayout(persisted, preview) : false;
        })) continue;
        next.delete(ownerId);
        changed = true;
      }
      return changed ? next : current;
    });
  }, [allItems]);
  const livePreviews = useMemo(() => {
    const previews = new Map<string, CanvasItem>();
    for (const group of livePreviewGroups.values()) {
      for (const item of group) previews.set(item.id, item);
    }
    return previews;
  }, [livePreviewGroups]);
  const optimisticItems = useMemo(
    () => allItems.map((item) => livePreviews.get(item.id) ?? item),
    [allItems, livePreviews]
  );
  const index = useMemo(() => {
    const tree = new ItemSpatialIndex();
    tree.rebuild(items.map((item) => ({ id: item.id, ...boundsForRect(item) })));
    return tree;
  }, [items]);
  const visibleIds = useMemo(() => new Set(index.visible(viewportWorldBounds(viewport, surfaceSize.width, surfaceSize.height, 260))), [index, surfaceSize, viewport]);
  const visibleItems = items.filter((item) => visibleIds.has(item.id));
  const focusedStack = items.find((item) => item.props.kind === "stack" && !item.props.collapsed);
  const focusedStackId = focusedStack?.id ?? null;
  const closingStackId = closingStack?.id ?? null;
  const dimmingStack = closingStack?.id === focusedStack?.id ? undefined : focusedStack;
  const isDimmedByFocusedStack = (item: CanvasItem) => Boolean(dimmingStack && item.id !== dimmingStack.id && item.parentStackId !== dimmingStack.id);
  const focusedStackItemIsOpen = focusedStack ? isStackFocusTarget(focusedStack.id, editingItemId, items) : false;
  const revealingChildren = revealingStack ? orderedStackChildren(items, revealingStack.id) : [];
  const revealIndexById = new Map(revealingChildren.map((item, index) => [item.id, index]));
  const closingChildren = closingStack ? orderedStackChildren(items, closingStack.id) : [];
  const closeIndexById = new Map(closingChildren.map((item, index) => [item.id, index]));
  const dropContainer = folderDropFeedback ? allItems.find((item) => item.id === folderDropFeedback.stackId) : undefined;
  const dropContainerTitle = dropContainer?.props.kind === "stack"
    ? dropContainer.props.title || "Untitled folder"
    : dropContainer?.props.kind === "frame" ? dropContainer.props.title || "Untitled frame" : "group";
  const dropContainerKind = dropContainer?.props.kind === "frame" ? "frame" : "folder";
  const folderDropStatus = folderDropFeedback
    ? folderDropFeedback.kind === "inside"
      ? folderDropFeedback.itemCount === 1 ? `Add to ${dropContainerTitle}` : `Add ${folderDropFeedback.itemCount} items to ${dropContainerTitle}`
      : folderDropFeedback.itemCount === 1 ? `Remove from ${dropContainerTitle}` : `Remove ${folderDropFeedback.itemCount} items from ${dropContainerTitle}`
    : null;

  const focusItemsRef = useRef(items);
  focusItemsRef.current = items;
  const onCloseStackRef = useRef(onCloseStack);
  onCloseStackRef.current = onCloseStack;

  useEffect(() => {
    if (!focusedStackId || closingStackId === focusedStackId || focusedStackItemIsOpen) return;

    const closeFocusedStack = () => {
      const currentStack = focusItemsRef.current.find((item) => item.id === focusedStackId);
      if (currentStack?.props.kind === "stack" && !currentStack.props.collapsed) void onCloseStackRef.current(currentStack);
    };
    return registerStackFocusDismissal(focusedStackId, () => focusItemsRef.current, closeFocusedStack);
  }, [closingStackId, focusedStackId, focusedStackItemIsOpen]);

  const stopInertia = useCallback(() => {
    if (inertiaFrame.current !== null) cancelAnimationFrame(inertiaFrame.current);
    inertiaFrame.current = null;
  }, []);

  useEffect(() => () => stopInertia(), [stopInertia]);

  useEffect(() => {
    const surface = ref.current;
    if (!surface) return;
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      onViewportInteraction();
      stopInertia();
      if (controlScheme === "sebsoto" || event.ctrlKey || event.metaKey) {
        setViewport((current) => zoomAt(current, { x: event.clientX, y: event.clientY }, current.scale * Math.exp(-event.deltaY * .003)));
      } else {
        setViewport((current) => panBy(current, { x: -event.deltaX, y: -event.deltaY }));
      }
    };
    surface.addEventListener("wheel", handleWheel, { passive: false });
    return () => surface.removeEventListener("wheel", handleWheel);
  }, [controlScheme, onViewportInteraction, setViewport, stopInertia]);

  const updateMarquee = (current: Point) => {
    const activeMarquee = marquee.current;
    if (!activeMarquee) return;
    setMarqueeBox({ start: activeMarquee.start, current });
    const worldStart = screenToWorld({
      x: Math.min(activeMarquee.start.x, current.x),
      y: Math.min(activeMarquee.start.y, current.y)
    }, viewport);
    const worldEnd = screenToWorld({
      x: Math.max(activeMarquee.start.x, current.x),
      y: Math.max(activeMarquee.start.y, current.y)
    }, viewport);
    const hitIds = new Set(index.visible({
      minX: worldStart.x,
      minY: worldStart.y,
      maxX: worldEnd.x,
      maxY: worldEnd.y
    }));
    setSelectedIds([...new Set([
      ...activeMarquee.selectionBefore,
      ...items.filter((item) => hitIds.has(item.id)).map((item) => item.id)
    ])]);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const mouse = event.pointerType === "mouse";
    if (mouse && controlScheme === "classic" && event.button === 1 && (event.target as HTMLElement).closest("[data-canvas-item-id]")) {
      event.preventDefault();
      event.stopPropagation();
      onViewportInteraction();
      stopInertia();
      return;
    }
    const middlePan = mouse && controlScheme === "sebsoto" && event.button === 1;
    const backgroundTarget = event.target === event.currentTarget || (event.target as HTMLElement).classList.contains("canvas-world");
    if (!backgroundTarget && !middlePan) return;
    if (mouse && event.button !== 0 && !middlePan) return;
    onViewportInteraction();
    stopInertia();
    if (middlePan) {
      event.preventDefault();
      event.stopPropagation();
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = { x: event.clientX, y: event.clientY };
    if (middlePan) {
      pointers.current.set(event.pointerId, point);
      pan.current = { pointerId: event.pointerId, last: point, time: event.timeStamp, velocity: { x: 0, y: 0 } };
      return;
    }
    onClearEditing();
    if (event.pointerType === "mouse" && event.shiftKey) {
      event.preventDefault();
      const surfaceBounds = event.currentTarget.getBoundingClientRect();
      const start = { x: point.x - surfaceBounds.left, y: point.y - surfaceBounds.top };
      marquee.current = { pointerId: event.pointerId, start, selectionBefore: [...selectedIds] };
      setMarqueeBox({ start, current: start });
      return;
    }
    pointers.current.set(event.pointerId, point);
    setSelectedIds([]);
    if (pointers.current.size === 1 && (!mouse || controlScheme === "classic")) pan.current = { pointerId: event.pointerId, last: point, time: event.timeStamp, velocity: { x: 0, y: 0 } };
    if (pointers.current.size === 2) {
      const values = [...pointers.current.values()];
      const first = values[0]; const second = values[1];
      if (first && second) pinch.current = { distance: Math.hypot(second.x - first.x, second.y - first.y), viewport, centroid: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 } };
    }
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (marquee.current?.pointerId === event.pointerId) {
      const surfaceBounds = event.currentTarget.getBoundingClientRect();
      updateMarquee({ x: event.clientX - surfaceBounds.left, y: event.clientY - surfaceBounds.top });
      return;
    }
    if (!pointers.current.has(event.pointerId)) return;
    const nextPoint = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, nextPoint);
    if (pointers.current.size >= 2 && pinch.current) {
      const values = [...pointers.current.values()];
      const first = values[0]; const second = values[1];
      if (!first || !second) return;
      const centroid = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
      const distance = Math.hypot(second.x - first.x, second.y - first.y);
      const zoomed = zoomAt(pinch.current.viewport, pinch.current.centroid, pinch.current.viewport.scale * distance / Math.max(1, pinch.current.distance));
      setViewport(panBy(zoomed, { x: centroid.x - pinch.current.centroid.x, y: centroid.y - pinch.current.centroid.y }));
    } else if (pan.current?.pointerId === event.pointerId) {
      const delta = { x: nextPoint.x - pan.current.last.x, y: nextPoint.y - pan.current.last.y };
      const elapsed = Math.max(8, event.timeStamp - pan.current.time);
      pan.current.velocity = { x: pan.current.velocity.x * .6 + (delta.x / elapsed) * .4, y: pan.current.velocity.y * .6 + (delta.y / elapsed) * .4 };
      pan.current.last = nextPoint;
      pan.current.time = event.timeStamp;
      setViewport((current) => panBy(current, delta));
    }
  };

  const endPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (marquee.current?.pointerId === event.pointerId) {
      const activeMarquee = marquee.current;
      if (event.type === "pointercancel") setSelectedIds(activeMarquee.selectionBefore);
      else {
        const surfaceBounds = event.currentTarget.getBoundingClientRect();
        updateMarquee({ x: event.clientX - surfaceBounds.left, y: event.clientY - surfaceBounds.top });
      }
      marquee.current = null;
      setMarqueeBox(null);
      return;
    }
    pointers.current.delete(event.pointerId);
    if (pan.current?.pointerId === event.pointerId) {
      let velocity = { ...pan.current.velocity };
      pan.current = null;
      if (Math.hypot(velocity.x, velocity.y) > .08 && pointers.current.size === 0) {
        const coast = () => {
          velocity = { x: velocity.x * .9, y: velocity.y * .9 };
          if (Math.hypot(velocity.x, velocity.y) < .012) { inertiaFrame.current = null; return; }
          setViewport((current) => panBy(current, { x: velocity.x * 16, y: velocity.y * 16 }));
          inertiaFrame.current = requestAnimationFrame(coast);
        };
        inertiaFrame.current = requestAnimationFrame(coast);
      }
    }
    if (pointers.current.size < 2) pinch.current = null;
  };

  return <div ref={setRefs} className={`canvas-surface canvas-surface--${controlScheme} ${marqueeBox ? "is-selecting" : ""} ${viewportTransitioning ? "is-viewport-transitioning" : ""}`} data-control-scheme={controlScheme} tabIndex={0}
    onPointerDownCapture={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={endPointer} onPointerCancel={endPointer}
    onAuxClickCapture={(event) => {
      if (event.button !== 1) return;
      if (controlScheme === "sebsoto") {
        event.preventDefault();
        return;
      }
      const itemId = (event.target as HTMLElement).closest<HTMLElement>("[data-canvas-item-id]")?.dataset["canvasItemId"];
      if (!itemId) return;
      event.preventDefault();
      event.stopPropagation();
      if (editingItemId && editingItemId !== itemId) onClearEditing();
      setSelectedIds([itemId]);
      onFocusItem(itemId);
    }}
    onDragOver={(event) => { if (event.dataTransfer.types.includes("Files")) event.preventDefault(); }}
    onDrop={(event) => {
      event.preventDefault();
      const origin = screenToWorld({ x: event.clientX, y: event.clientY }, viewport);
      [...event.dataTransfer.files].forEach((file, index) => void onDropFile(file, { x: origin.x + index * 28, y: origin.y + index * 28 }));
    }}
    onContextMenu={(event) => {
      if (event.target !== event.currentTarget && !(event.target as HTMLElement).classList.contains("canvas-world")) return;
      event.preventDefault();
      event.stopPropagation();
      stopInertia();
      const anchor = { x: event.clientX, y: event.clientY };
      onCanvasMenu(anchor);
    }}>
    <div className="canvas-world" style={{ transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})` }}>
      {visibleItems.map((item) => <CanvasCard key={item.id} item={livePreviews.get(item.id) ?? item} folderDropKind={folderDropFeedback?.stackId === item.id ? folderDropFeedback.kind : undefined} folderTransitionPhase={revealingStack?.id === item.id
        ? "revealing"
        : closingStack?.id === item.id ? "collapsing" : undefined} stackTransition={closingStack && closeIndexById.has(item.id)
        ? { stack: closingStack, index: closeIndexById.get(item.id) ?? 0, count: closingChildren.length, phase: "collapsing" }
        : revealingStack && revealIndexById.has(item.id)
          ? { stack: revealingStack, index: revealIndexById.get(item.id) ?? 0, count: revealingChildren.length, phase: "revealing" }
          : undefined} viewport={viewport} selected={selectedIds.includes(item.id)} dimmed={isDimmedByFocusedStack(item)} editingItemId={editingItemId} selection={selectedIds} setSelection={setSelectedIds} saveItemChanges={saveItemChanges} persistSet={persistSet} allItems={optimisticItems} onLivePreviewGroup={setLivePreviewGroup} onFolderDropFeedback={setFolderDropFeedback} onClearEditing={onClearEditing} openOnDoubleClick={controlScheme === "sebsoto"} onFocus={() => onFocusItem(item.id)} onOpen={() => onOpenItem(item.id)} onMenu={(anchor) => onItemMenu(item.id, anchor)}/>) }
      {visibleItems.map((item) => {
        const shown = livePreviews.get(item.id) ?? item;
        return shown.props.kind === "frame" ? <div
          key={`frame-label-${item.id}`}
          data-frame-label-for={item.id}
          className={`canvas-frame-label-overlay item-frame--${shown.props.color} item-frame--border-color-${shown.props.borderColor ?? "match"} ${isDimmedByFocusedStack(item) ? "is-dimmed" : ""}`}
          aria-hidden="true"
          style={{
            width: shown.width,
            height: shown.height,
            transform: `translate3d(${shown.x}px, ${shown.y}px, 0) rotate(${shown.rotation}deg)`,
            "--canvas-scale": viewport.scale
          } as CSSProperties}
        ><CanvasFrameLabel props={shown.props}/></div> : null;
      })}
    </div>
    {marqueeBox && <div
      className="canvas-selection-marquee"
      aria-hidden="true"
      style={{
        left: Math.min(marqueeBox.start.x, marqueeBox.current.x),
        top: Math.min(marqueeBox.start.y, marqueeBox.current.y),
        width: Math.abs(marqueeBox.current.x - marqueeBox.start.x),
        height: Math.abs(marqueeBox.current.y - marqueeBox.start.y)
      }}
    />}
    {folderDropFeedback && folderDropStatus && <div
      className={`canvas-folder-drop-status canvas-folder-drop-status--${folderDropFeedback.kind}`}
      role="status"
      aria-live="polite"
      style={{
        left: Math.max(12, Math.min(folderDropFeedback.point.x + 18, window.innerWidth - 276)),
        top: Math.max(12, Math.min(folderDropFeedback.point.y + 18, window.innerHeight - 52))
      }}
    ><Icon name={dropContainerKind === "frame" ? "frame" : folderDropFeedback.kind === "inside" ? "folder" : "folder-out"}/><span>{folderDropStatus}</span></div>}
    <div className="canvas-grain" aria-hidden="true"/>
  </div>;
};

function CanvasCard({ item, stackTransition, folderTransitionPhase, folderDropKind, viewport, selected, dimmed, editingItemId, selection, setSelection, saveItemChanges, persistSet, allItems, onLivePreviewGroup, onFolderDropFeedback, onClearEditing, openOnDoubleClick, onFocus, onOpen, onMenu }: {
  item: CanvasItem; viewport: Viewport; selected: boolean; dimmed: boolean; editingItemId: string | null; selection: string[]; setSelection: (ids: string[]) => void;
  stackTransition?: { stack: CanvasItem; index: number; count: number; phase: "revealing" | "collapsing" } | undefined;
  folderTransitionPhase?: "revealing" | "collapsing" | undefined;
  folderDropKind?: CanvasFolderDropIntent["kind"] | undefined;
  saveItemChanges: SpaceScreenProps["saveItemChanges"]; persistSet: (before: CanvasItem[], after: CanvasItem[]) => Promise<void>; allItems: CanvasItem[]; onLivePreviewGroup: (ownerId: string, previews: CanvasItem[] | null) => void; onFolderDropFeedback: (feedback: (CanvasFolderDropIntent & { point: Point }) | null) => void; onClearEditing: () => void; openOnDoubleClick: boolean; onFocus: () => void; onOpen: () => void; onMenu: (anchor: Point) => void;
}) {
  const drag = useRef<{
    start: Point;
    before: CanvasItem[];
    allItems: CanvasItem[];
    activeIds: string[];
    selectionBefore: string[];
    toggleSelection: boolean;
    moving: boolean;
  } | null>(null);
  const resize = useRef<{ start: Point; before: CanvasItem; allItems: CanvasItem[] } | null>(null);
  const rotationGesture = useRef<{
    before: CanvasItem;
    beforeGroup: CanvasItem[];
    allItems: CanvasItem[];
    center: Point;
    previousPointerAngle: number;
    accumulatedRotation: number;
  } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [preview, setPreview] = useState<CanvasItem | null>(null);
  const [committing, setCommitting] = useState(false);
  const commitSequence = useRef(0);
  const [focusPoint, setFocusPoint] = useState<Point | null>(null);
  const updatePreview = useCallback((next: CanvasItem | null, group: CanvasItem[] | null = null) => {
    setPreview(group?.length ? null : next);
    onLivePreviewGroup(item.id, group);
  }, [item.id, onLivePreviewGroup]);
  const shown = preview ?? item;
  const gestureItems = allItems.map((candidate) => candidate.id === shown.id ? shown : candidate);
  const stackItems = orderedStackChildren(allItems, item.id);
  const inlineEditing = editingItemId === item.id && (item.props.kind === "sticky-note" || item.props.kind === "label") && !isLockedItem(item);
  // Locked cards stay on the canvas behind the PIN sheet; the editor covers them once unlocked.
  const modalEditing = editingItemId === item.id && !inlineEditing && !isLockedItem(item);
  const revealOrigin = stackTransition ? stackRevealOrigin(shown, stackTransition.stack) : undefined;
  const transitionDelay = stackTransition?.phase === "collapsing"
    ? stackCollapseDelay(stackTransition.index, stackTransition.count)
    : stackTransition ? stackRevealDelay(stackTransition.index, stackTransition.count) : 0;
  const transitionDuration = stackTransition?.phase === "collapsing" ? STACK_COLLAPSE_DURATION_MS : STACK_REVEAL_DURATION_MS;
  const folderTransitionDuration = folderTransitionPhase === "collapsing"
    ? stackCollapseTotalDuration(stackItems.length)
    : stackRevealTotalDuration(stackItems.length);
  const shownZIndex = folderTransitionPhase
    ? Math.max(shown.zIndex, ...stackItems.map((stackItem) => stackItem.zIndex)) + 1
    : shown.zIndex;

  useEffect(() => {
    if (preview && !drag.current && !resize.current && !rotationGesture.current && !committing && hasSameCanvasLayout(item, preview)) updatePreview(null);
  }, [committing, item, preview, updatePreview]);

  useEffect(() => () => onLivePreviewGroup(item.id, null), [item.id, onLivePreviewGroup]);

  const commitPreview = useCallback((before: CanvasItem[], after: CanvasItem[]) => {
    const sequence = ++commitSequence.current;
    setCommitting(true);
    void persistSet(before, after).then(() => {
      if (commitSequence.current === sequence) setCommitting(false);
    }).catch(() => {
      if (commitSequence.current !== sequence) return;
      setCommitting(false);
      updatePreview(null);
    });
  }, [persistSet, updatePreview]);

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!canStartCanvasItemDrag(event.target as Element)) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.stopPropagation();
    if (editingItemId && editingItemId !== item.id) onClearEditing();
    event.currentTarget.setPointerCapture(event.pointerId);
    const selectionBefore = [...selection];
    const activeIds = [...new Set(selection.includes(item.id) ? selection : event.shiftKey ? [...selection, item.id] : [item.id])];
    const allItemsAtStart = gestureItems.map((value) => structuredClone(value));
    const before = canvasDragItems(allItemsAtStart, activeIds).map((value) => structuredClone(value));
    drag.current = {
      start: { x: event.clientX, y: event.clientY },
      before,
      allItems: allItemsAtStart,
      activeIds,
      selectionBefore,
      toggleSelection: event.shiftKey,
      moving: false
    };
    onFolderDropFeedback(null);
    if (event.pointerType === "touch") longPressTimer.current = setTimeout(() => { const anchor = drag.current?.start; drag.current = null; if (anchor) onMenu(anchor); }, 520);
  };
  const moveDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const screenDistance = Math.hypot(event.clientX - drag.current.start.x, event.clientY - drag.current.start.y);
    if (screenDistance < 5) return;
    if (!drag.current.moving) {
      drag.current.moving = true;
      setSelection(drag.current.activeIds);
    }
    const dx = (event.clientX - drag.current.start.x) / viewport.scale;
    const dy = (event.clientY - drag.current.start.y) / viewport.scale;
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = undefined; }
    const after = moveCanvasDragItems(drag.current.before, drag.current.allItems, dx, dy, item.id);
    const nextPreview = after.find((candidate) => candidate.id === item.id);
    updatePreview(nextPreview ?? null, after.length > 1 || item.props.kind === "frame" ? after : null);
    const intent = canvasFolderDropIntent(drag.current.before, after, item.id);
    onFolderDropFeedback(intent ? { ...intent, point: { x: event.clientX, y: event.clientY } } : null);
  };
  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = undefined;
    if (!drag.current) return;
    const dragState = drag.current;
    const dx = (event.clientX - dragState.start.x) / viewport.scale;
    const dy = (event.clientY - dragState.start.y) / viewport.scale;
    const before = dragState.before;
    drag.current = null;
    onFolderDropFeedback(null);
    if (!dragState.moving) {
      const restoredPreview = dragState.before.find((candidate) => candidate.id === item.id) ?? null;
      updatePreview(restoredPreview, dragState.before.length > 1 || item.props.kind === "frame" ? dragState.before : null);
      if (dragState.toggleSelection) {
        setSelection(dragState.selectionBefore.includes(item.id)
          ? dragState.selectionBefore.filter((id) => id !== item.id)
          : [...dragState.selectionBefore, item.id]);
        return;
      }
      if (openOnDoubleClick) {
        setSelection([item.id]);
        onFocus();
      } else {
        setFocusPoint({ x: event.clientX, y: event.clientY });
        onOpen();
      }
      return;
    }
    const moved = moveCanvasDragItems(before, dragState.allItems, dx, dy, item.id);
    const changeSet = item.props.kind === "frame"
      ? includeFrameMembershipChanges(before, moved, dragState.allItems, item.id)
      : { before, after: moved };
    const committedPreview = changeSet.after.find((value) => value.id === item.id);
    updatePreview(committedPreview ?? null, changeSet.after.length > 1 || item.props.kind === "frame" ? changeSet.after : null);
    commitPreview(changeSet.before, changeSet.after);
  };
  const cancelDrag = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = undefined;
    const dragState = drag.current;
    drag.current = null;
    const restoredPreview = dragState?.before.find((candidate) => candidate.id === item.id) ?? null;
    updatePreview(restoredPreview, dragState && (dragState.before.length > 1 || item.props.kind === "frame") ? dragState.before : null);
    onFolderDropFeedback(null);
  };
  const startResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation(); event.currentTarget.setPointerCapture(event.pointerId);
    resize.current = {
      start: { x: event.clientX, y: event.clientY },
      before: structuredClone(shown),
      allItems: gestureItems.map((value) => structuredClone(value))
    };
  };
  const moveResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!resize.current) return;
    const next = resizeCanvasItemFromScreenDelta(resize.current.before, {
      x: event.clientX - resize.current.start.x,
      y: event.clientY - resize.current.start.y
    }, viewport.scale);
    updatePreview(next, item.props.kind === "frame" ? [next] : null);
  };
  const endResize = () => {
    const resizeState = resize.current;
    resize.current = null;
    if (!resizeState || hasSameCanvasLayout(resizeState.before, shown)) return;
    const before = [resizeState.before]; const after = [shown];
    const changeSet = shown.props.kind === "frame"
      ? includeFrameMembershipChanges(before, after, resizeState.allItems, shown.id)
      : { before, after };
    updatePreview({ ...shown }, shown.props.kind === "frame" ? changeSet.after : null);
    commitPreview(changeSet.before, changeSet.after);
  };
  const resetSize = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resize.current = null;
    const before = structuredClone(shown);
    const after = before.props.kind === "frame"
      ? fitCanvasFrameToContents(before, gestureItems)
      : resetCanvasItemSize(before);
    if (hasSameCanvasLayout(before, after)) return;
    const changeSet = after.props.kind === "frame"
      ? includeFrameMembershipChanges([before], [after], gestureItems, after.id)
      : { before: [before], after: [after] };
    updatePreview(after, after.props.kind === "frame" ? changeSet.after : null);
    commitPreview(changeSet.before, changeSet.after);
  };

  const startRotation = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const before = structuredClone(shown);
    const beforeGroup = before.props.kind === "frame"
      ? rotateCanvasFrameItems(before, before, gestureItems).map((candidate) => structuredClone(candidate))
      : [before];
    const worldCenter = worldToScreen({ x: before.x + before.width / 2, y: before.y + before.height / 2 }, viewport);
    const surfaceBounds = event.currentTarget.closest(".canvas-surface")?.getBoundingClientRect();
    const center = {
      x: worldCenter.x + (surfaceBounds?.left ?? 0),
      y: worldCenter.y + (surfaceBounds?.top ?? 0)
    };
    rotationGesture.current = {
      before,
      beforeGroup,
      allItems: gestureItems.map((value) => structuredClone(value)),
      center,
      previousPointerAngle: canvasRotationPointerAngle(center, { x: event.clientX, y: event.clientY }),
      accumulatedRotation: before.rotation
    };
  };
  const advanceRotation = (event: ReactPointerEvent<HTMLButtonElement>): CanvasItem[] | null => {
    const gesture = rotationGesture.current;
    if (!gesture) return null;
    const update = updateCanvasRotation(
      gesture.accumulatedRotation,
      gesture.previousPointerAngle,
      gesture.center,
      { x: event.clientX, y: event.clientY },
      event.shiftKey
    );
    const after = { ...gesture.before, rotation: update.rotation };
    rotationGesture.current = {
      ...gesture,
      previousPointerAngle: update.pointerAngle,
      accumulatedRotation: update.accumulatedRotation
    };
    return after.props.kind === "frame"
      ? rotateCanvasFrameItems(gesture.before, after, gesture.beforeGroup)
      : [after];
  };
  const moveRotation = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const nextGroup = advanceRotation(event);
    const next = nextGroup?.find((candidate) => candidate.id === item.id);
    if (next) updatePreview(next, next.props.kind === "frame" ? nextGroup : null);
  };
  const endRotation = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const finalGroup = advanceRotation(event);
    const finalPreview = finalGroup?.find((candidate) => candidate.id === item.id);
    const gesture = rotationGesture.current;
    rotationGesture.current = null;
    if (!gesture || !finalGroup || !finalPreview || hasSameCanvasLayout(gesture.before, finalPreview)) {
      const restoredPreview = gesture?.before ?? null;
      updatePreview(restoredPreview, gesture?.before.props.kind === "frame" ? gesture.beforeGroup : null);
      return;
    }
    const changeSet = finalPreview.props.kind === "frame"
      ? includeFrameMembershipChanges(gesture.beforeGroup, finalGroup, gesture.allItems, finalPreview.id)
      : { before: [gesture.before], after: [finalPreview] };
    updatePreview(finalPreview, finalPreview.props.kind === "frame" ? changeSet.after : null);
    commitPreview(changeSet.before, changeSet.after);
  };
  const cancelRotation = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const gesture = rotationGesture.current;
    rotationGesture.current = null;
    updatePreview(gesture?.before ?? null, gesture?.before.props.kind === "frame" ? gesture.beforeGroup : null);
  };
  const resetRotation = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    rotationGesture.current = null;
    const before = structuredClone(shown);
    const after = { ...before, rotation: 0 };
    if (hasSameCanvasLayout(before, after)) return;
    const beforeGroup = before.props.kind === "frame"
      ? rotateCanvasFrameItems(before, before, gestureItems).map((candidate) => structuredClone(candidate))
      : [before];
    const afterGroup = after.props.kind === "frame"
      ? rotateCanvasFrameItems(before, after, beforeGroup)
      : [after];
    const changeSet = after.props.kind === "frame"
      ? includeFrameMembershipChanges(beforeGroup, afterGroup, gestureItems, after.id)
      : { before: [before], after: [after] };
    updatePreview(after, after.props.kind === "frame" ? changeSet.after : null);
    commitPreview(changeSet.before, changeSet.after);
  };

  const moving = Boolean(drag.current?.moving || resize.current || rotationGesture.current);

  return <article data-canvas-item-id={item.id} className={`canvas-item canvas-item--${item.type} ${selected ? "is-selected" : ""} ${dimmed ? "is-dimmed" : ""} ${moving ? "is-moving" : ""} ${inlineEditing ? "is-inline-editing" : ""} ${modalEditing ? "is-editor-source" : ""} ${folderDropKind ? `is-folder-drop-${folderDropKind}` : ""} ${stackTransition ? `is-stack-${stackTransition.phase}` : ""}`} style={{ left: 0, top: 0, width: shown.width, height: shown.height, zIndex: shownZIndex, pointerEvents: stackTransition?.phase === "collapsing" ? "none" : stackTransition?.phase === "revealing" ? "auto" : undefined, transform: `translate3d(${shown.x}px, ${shown.y}px, 0) rotate(${shown.rotation}deg)`, "--canvas-scale": viewport.scale, "--stack-reveal-from-x": `${revealOrigin?.x ?? shown.x}px`, "--stack-reveal-from-y": `${revealOrigin?.y ?? shown.y}px`, "--stack-reveal-from-rotation": `${revealOrigin?.rotation ?? shown.rotation}deg`, "--stack-reveal-to-x": `${shown.x}px`, "--stack-reveal-to-y": `${shown.y}px`, "--stack-reveal-to-rotation": `${shown.rotation}deg`, "--stack-reveal-delay": `${transitionDelay}ms`, "--stack-transition-duration": `${transitionDuration}ms`, "--folder-transition-duration": `${folderTransitionDuration}ms` } as CSSProperties}
    onPointerDown={startDrag} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={cancelDrag} onDoubleClick={(event) => {
      if (!openOnDoubleClick || !canStartCanvasItemDrag(event.target as Element)) return;
      event.preventDefault();
      event.stopPropagation();
      onOpen();
    }} onDragStart={(event) => event.preventDefault()} onContextMenu={(event) => { event.preventDefault(); event.stopPropagation(); onMenu({ x: event.clientX, y: event.clientY }); }}>
    <ItemContent item={shown} editing={inlineEditing} focusPoint={focusPoint} saveItemChanges={saveItemChanges} stackItems={stackItems} folderTransitionPhase={folderTransitionPhase} onOpen={onOpen} onFinishEditing={onClearEditing}/>
    {selected && <Tooltip label="Drag to rotate · Hold Shift to snap · Double-click to reset"><button className="rotation-handle" aria-label="Rotate item" onPointerDown={startRotation} onPointerMove={moveRotation} onPointerUp={endRotation} onPointerCancel={cancelRotation} onDoubleClick={resetRotation}/></Tooltip>}
    {selected && <Tooltip label={`Drag to resize · Double-click to ${shown.props.kind === "frame" ? "fit contents" : "reset"}`}><button className="resize-handle" aria-label="Resize item" onPointerDown={startResize} onPointerMove={moveResize} onPointerUp={endResize} onPointerCancel={endResize} onDoubleClick={resetSize}/></Tooltip>}
  </article>;
}

function ItemActionMenu({ anchor, item, allItems, onClose, onEdit, onRenameFolder, onChangeFolderColor, onChangeStickyColor, onChangeDocumentColor, onChangeDateEventColor, onChangeArrow, onChangeFrame, onDuplicate, onMoveLayer, onUnstack, onLock, onRemoveLock, onDelete }: {
  anchor: Point; item: CanvasItem; allItems: CanvasItem[]; onClose: () => void; onEdit: () => void; onRenameFolder: (title: string) => void; onChangeFolderColor: (color: FolderColor) => void; onChangeStickyColor: (color: StickyColor) => void; onChangeDocumentColor: (color: DocumentColor) => void; onChangeDateEventColor: (color: DateEventColor) => void; onChangeArrow: (props: Extract<ItemProps, { kind: "arrow" }>) => void; onChangeFrame: (props: Extract<ItemProps, { kind: "frame" }>) => void; onDuplicate: () => void;
  onMoveLayer: (direction: "front" | "back") => void; onUnstack: () => void; onLock: () => void; onRemoveLock: () => void; onDelete: (withContents: boolean) => void;
}) {
  const initialArrowProps = item.props.kind === "arrow" ? item.props : null;
  const arrowDraft = useRef(initialArrowProps);
  const initialFrameProps = item.props.kind === "frame" ? item.props : null;
  const frameDraft = useRef(initialFrameProps);
  const shortcutBindings = useShell((state) => state.shortcutBindings);
  const childCount = allItems.filter((candidate) => candidate.parentStackId === item.id).length;
  const parent = item.parentStackId ? allItems.find((candidate) => candidate.id === item.parentStackId) : undefined;
  const parentKind = parent?.props.kind === "frame" ? "frame" : "folder";
  const isContainer = item.props.kind === "stack" || item.props.kind === "frame";
  const locked = isLockedItem(item);
  // What sits inside a locked folder is protected by the folder, not a lock of its own.
  const inLockedFolder = parent !== undefined && isLockedFolder(parent);
  const lockable = !inLockedFolder && (item.props.kind === "sticky-note" || item.props.kind === "document" || item.props.kind === "stack");
  const closedLockedFolder = isClosedLockedFolder(item);
  // A locked body is ciphertext; only what has been opened with its PIN can be downloaded.
  const textProps = !locked && (item.props.kind === "document" || item.props.kind === "sticky-note") ? item.props : null;
  return <ContextMenu anchor={anchor} label="Item actions" className="item-context-menu" onClose={onClose}>{(close) => <>
      <ContextMenuHeader
        eyebrow={item.type === "stack" ? "FOLDER" : item.type.replace("-", " ").toLocaleUpperCase()}
        closeLabel="Close item menu"
        onClose={close}
      />
      {(item.props.kind === "sticky-note" || item.props.kind === "document" || item.props.kind === "label" || item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" || item.props.kind === "spreadsheet" || item.props.kind === "web-clip" || item.props.kind === "date-event" || item.props.kind === "stack") && <ContextMenuItem
        icon={canvasItemIcon(item)}
        label={item.props.kind === "stack" ? item.props.collapsed ? "Open folder" : "Close folder" : item.props.kind === "web-clip" ? "Open preview" : item.props.kind === "date-event" ? "Edit event" : item.props.kind === "label" ? "Edit label" : item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" || item.props.kind === "spreadsheet" ? "Open viewer" : "Open editor"}
        description={item.props.kind === "stack" ? closedLockedFolder ? "Enter the PIN to see what's inside" : item.props.collapsed ? "Show the items in this folder" : locked ? "Close and lock it again" : "Gather the items back together" : item.props.kind === "web-clip" ? "Review the saved capture before visiting" : item.props.kind === "date-event" ? "Change its name, date, or optional time" : item.props.kind === "label" ? "Update the text shown on the canvas" : item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" || item.props.kind === "spreadsheet" ? "Review the file and its details" : locked ? "Enter the PIN to see its contents" : "Edit this item’s contents"}
        {...canvasShortcutMenuProps("open", shortcutBindings)}
        onClick={() => close(onEdit)}
      />}
      {item.props.kind === "stack" && <>
        <ContextNameEditor inputId={`folder-name-${item.id}`} label="Folder name" title={item.props.title} allowEmpty onRename={onRenameFolder}/>
        <FolderColorPicker color={item.props.color ?? "lilac"} onChange={onChangeFolderColor}/>
      </>}
      {item.props.kind === "sticky-note" && <StickyColorPicker color={item.props.accent} onChange={onChangeStickyColor}/>}
      {item.props.kind === "document" && <DocumentColorPicker color={item.props.color ?? "paper"} onChange={onChangeDocumentColor}/>}
      {item.props.kind === "date-event" && <DateEventColorPicker color={item.props.color ?? "lilac"} onChange={onChangeDateEventColor}/>}
      {lockable && <ContextMenuItem
        icon={locked ? "unlock" : "lock"}
        label={locked ? "Remove lock" : "Lock with PIN"}
        description={item.props.kind === "stack"
          ? locked ? "Save everything inside without encryption" : "Encrypt everything inside behind a PIN"
          : locked ? "Save the contents without encryption" : "Encrypt the contents behind a PIN"}
        onClick={() => close(locked ? onRemoveLock : onLock)}
      />}
      {item.props.kind === "arrow" && <ArrowStylePicker props={arrowDraft.current ?? item.props} onChange={(props) => {
        arrowDraft.current = props;
        onChangeArrow(props);
      }}/>}
      {item.props.kind === "frame" && <>
        <ContextNameEditor inputId={`frame-name-${item.id}`} label="Frame name" title={frameDraft.current?.title ?? item.props.title} allowEmpty onRename={(title) => {
          const props: Extract<ItemProps, { kind: "frame" }> = { ...(frameDraft.current ?? item.props as Extract<ItemProps, { kind: "frame" }>), title };
          frameDraft.current = props;
          onChangeFrame(props);
        }}/>
        <FrameStylePicker props={frameDraft.current ?? item.props} onChange={(props) => {
          frameDraft.current = props;
          onChangeFrame(props);
        }}/>
      </>}
      {textProps && <ContextMenuItem
        icon="download"
        label={textProps.kind === "document" ? "Download as Markdown" : "Download as text"}
        description={textProps.kind === "document" ? "Save a .md file" : "Save a .txt file"}
        onClick={() => close(() => downloadTextFile(textDownloadForItem(textProps)))}
      />}
      <ContextMenuItem
        icon="copy"
        label="Duplicate"
        description="Create a copy nearby"
        {...canvasShortcutMenuProps("duplicate", shortcutBindings)}
        onClick={() => close(onDuplicate)}
      />
      <ContextMenuItem
        icon="bring-front"
        label="Bring to front"
        description="Place above other items"
        {...canvasShortcutMenuProps("bring-front", shortcutBindings)}
        onClick={() => close(() => onMoveLayer("front"))}
      />
      <ContextMenuItem
        icon="send-back"
        label="Send to back"
        description="Place behind other items"
        {...canvasShortcutMenuProps("send-back", shortcutBindings)}
        onClick={() => close(() => onMoveLayer("back"))}
      />
      {item.parentStackId && <ContextMenuItem
        icon={parentKind === "frame" ? "frame" : "folder-out"}
        label={`Remove from ${parentKind}`}
        description={`Move this item out of its ${parentKind}`}
        onClick={() => close(onUnstack)}
      />}
      <ContextMenuDivider/>
      {isContainer && childCount > 0 && !closedLockedFolder ? <>
        <ContextMenuItem
          icon={item.props.kind === "frame" ? "frame" : "folder-minus"}
          label={`Delete ${item.props.kind === "frame" ? "frame" : "folder"}, keep ${childCount} ${childCount === 1 ? "item" : "items"}`}
          description="Leave the contents on the canvas"
          {...canvasShortcutMenuProps("delete", shortcutBindings)}
          onClick={() => close(() => onDelete(false))}
        />
        <ContextMenuItem
          icon="trash"
          label="Move everything to Trash"
          description="Delete the folder and its contents"
          tone="danger"
          onClick={() => close(() => onDelete(true))}
        />
      </> : <ContextMenuItem
        icon="trash"
        label={closedLockedFolder && childCount > 0 ? "Move everything to Trash" : "Move to Trash"}
        description={closedLockedFolder && childCount > 0 ? "Delete the locked folder and its contents" : "Remove this item from the space"}
        tone="danger"
        {...canvasShortcutMenuProps("delete", shortcutBindings)}
        onClick={() => close(() => onDelete(closedLockedFolder))}
      />}
    </>}
  </ContextMenu>;
}

function ContextNameEditor({ inputId, label, title, allowEmpty = false, onRename }: { inputId: string; label: string; title: string; allowEmpty?: boolean; onRename: (title: string) => void }) {
  const [draft, setDraft] = useState(title);
  const latestTitle = useRef(title);
  latestTitle.current = title;

  const commit = () => {
    const nextTitle = draft.trim();
    if ((nextTitle || allowEmpty) && nextTitle !== latestTitle.current) {
      latestTitle.current = nextTitle;
      onRename(nextTitle);
    }
  };

  return <form className="context-name-editor" onSubmit={(event) => { event.preventDefault(); commit(); }}>
    <label htmlFor={inputId}>{label}</label>
    <input
      id={inputId}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.stopPropagation();
          commit();
          event.currentTarget.blur();
          return;
        }
        if (event.key === "Escape") setDraft(latestTitle.current);
        if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Escape") event.stopPropagation();
      }}
      aria-label={label}
    />
  </form>;
}

function FolderColorPicker({ color, onChange }: { color: FolderColor; onChange: (color: FolderColor) => void }) {
  return <section className="folder-color-picker" aria-label="Folder color">
    <header><span>Folder color</span><strong>{folderColorLabel(color)}</strong></header>
    <div>
      {FOLDER_COLORS.map((option) => <button
        key={option.value}
        type="button"
        role="menuitemradio"
        aria-checked={option.value === color}
        aria-label={`${option.label} folder`}
        className={option.value === color ? "is-selected" : ""}
        onClick={() => onChange(option.value)}
      >
        <span className={`folder-color-swatch folder-color-swatch--${option.value}`}>{option.value === color && <Icon name="check"/>}</span>
        <span>{option.label}</span>
      </button>)}
    </div>
  </section>;
}

function StickyColorPicker({ color, onChange }: { color: StickyColor; onChange: (color: StickyColor) => void }) {
  return <section className="sticky-color-picker" aria-label="Note color">
    <header><span>Note color</span><strong>{stickyColorLabel(color)}</strong></header>
    <div>
      {STICKY_COLORS.map((option) => <button
        key={option.value}
        type="button"
        role="menuitemradio"
        aria-checked={option.value === color}
        aria-label={`${option.label} note`}
        className={option.value === color ? "is-selected" : ""}
        onClick={() => onChange(option.value)}
      >
        <span className={`sticky-color-swatch sticky-color-swatch--${option.value}`}>{option.value === color && <Icon name="check"/>}</span>
        <span>{option.label}</span>
      </button>)}
    </div>
  </section>;
}

function DocumentColorPicker({ color, onChange }: { color: DocumentColor; onChange: (color: DocumentColor) => void }) {
  return <section className="document-color-picker" aria-label="Document color">
    <header><span>Document color</span><strong>{documentColorLabel(color)}</strong></header>
    <div>
      {DOCUMENT_COLORS.map((option) => <button
        key={option.value}
        type="button"
        role="menuitemradio"
        aria-checked={option.value === color}
        aria-label={`${option.label} document`}
        className={option.value === color ? "is-selected" : ""}
        onClick={() => onChange(option.value)}
      >
        <span className={`document-color-swatch document-color--${option.value}`}>{option.value === color && <Icon name="check"/>}</span>
        <span>{option.label}</span>
      </button>)}
    </div>
  </section>;
}

function DateEventColorPicker({ color, onChange }: { color: DateEventColor; onChange: (color: DateEventColor) => void }) {
  return <section className="date-event-color-picker" aria-label="Event color">
    <header><span>Event color</span><strong>{dateEventColorLabel(color)}</strong></header>
    <div>
      {DATE_EVENT_COLORS.map((option) => <button
        key={option.value}
        type="button"
        role="menuitemradio"
        aria-checked={option.value === color}
        aria-label={`${option.label} event`}
        className={option.value === color ? "is-selected" : ""}
        onClick={() => onChange(option.value)}
      >
        <span className={`date-event-color-swatch date-event-color--${option.value}`}>{option.value === color && <Icon name="check"/>}</span>
        <span>{option.label}</span>
      </button>)}
    </div>
  </section>;
}

function ArrowStylePicker({ props, onChange }: { props: Extract<ItemProps, { kind: "arrow" }>; onChange: (props: Extract<ItemProps, { kind: "arrow" }>) => void }) {
  const choices = [
    { label: "Path", value: props.path, options: [{ value: "straight", label: "Straight" }, { value: "curved", label: "Curved" }] },
    { label: "Line", value: props.stroke, options: [{ value: "solid", label: "Solid" }, { value: "dashed", label: "Dashed" }, { value: "dotted", label: "Dotted" }] },
    { label: "Arrowhead", value: props.head, options: [{ value: "filled", label: "Filled" }, { value: "outline", label: "Outline" }, { value: "none", label: "None" }] }
  ] as const;
  return <section className="element-style-picker arrow-style-picker" aria-label="Arrow style">
    {choices.map((choice) => <div className="element-style-picker__row" key={choice.label}>
      <span>{choice.label}</span>
      <div role="group" aria-label={choice.label}>
        {choice.options.map((option) => <button
          key={option.value}
          type="button"
          className={option.value === choice.value ? "is-selected" : ""}
          aria-pressed={option.value === choice.value}
          onClick={() => {
            if (choice.label === "Path") onChange({ ...props, path: option.value as typeof props.path });
            if (choice.label === "Line") onChange({ ...props, stroke: option.value as typeof props.stroke });
            if (choice.label === "Arrowhead") onChange({ ...props, head: option.value as typeof props.head });
          }}
        >{option.label}</button>)}
      </div>
    </div>)}
    <div className="element-color-picker" role="group" aria-label="Arrow color">
      <header><span>Color</span><strong>{arrowColorLabel(props.color)}</strong></header>
      <div>{ARROW_COLORS.map((option) => <button
        key={option.value}
        type="button"
        aria-label={`${option.label} arrow`}
        aria-pressed={option.value === props.color}
        className={option.value === props.color ? "is-selected" : ""}
        onClick={() => onChange({ ...props, color: option.value })}
      ><span className={`element-color-swatch arrow-color-swatch--${option.value}`}>{option.value === props.color && <Icon name="check"/>}</span></button>)}</div>
    </div>
  </section>;
}

function FrameStylePicker({ props, onChange }: { props: Extract<ItemProps, { kind: "frame" }>; onChange: (props: Extract<ItemProps, { kind: "frame" }>) => void }) {
  const backgroundStyle = props.backgroundStyle ?? "soft";
  const borderStyle = props.borderStyle ?? "solid";
  const borderColor = props.borderColor ?? "match";
  return <section className="element-style-picker frame-style-picker" aria-label="Frame appearance">
    <div className="element-style-picker__row">
      <span>Background</span>
      <div role="group" aria-label="Background style">
        {([
          { value: "none", label: "None" },
          { value: "subtle", label: "Subtle" },
          { value: "soft", label: "Soft" }
        ] as const).map((option) => <button
          key={option.value}
          type="button"
          className={option.value === backgroundStyle ? "is-selected" : ""}
          aria-pressed={option.value === backgroundStyle}
          onClick={() => onChange({ ...props, backgroundStyle: option.value })}
        >{option.label}</button>)}
      </div>
    </div>
    <div className="element-style-picker__row">
      <span>Border</span>
      <div role="group" aria-label="Border style">
        {([
          { value: "solid", label: "Solid" },
          { value: "dashed", label: "Dashed" },
          { value: "dotted", label: "Dotted" }
        ] as const).map((option) => <button
          key={option.value}
          type="button"
          className={option.value === borderStyle ? "is-selected" : ""}
          aria-pressed={option.value === borderStyle}
          onClick={() => onChange({ ...props, borderStyle: option.value })}
        >{option.label}</button>)}
      </div>
    </div>
    <div className="element-color-picker" role="group" aria-label="Frame background">
      <header><span>Background color</span><strong>{frameColorLabel(props.color)}</strong></header>
      <div>{FRAME_COLORS.map((option) => <button
        key={option.value}
        type="button"
        aria-label={`${option.label} frame background`}
        aria-pressed={option.value === props.color}
        className={option.value === props.color ? "is-selected" : ""}
        onClick={() => onChange({ ...props, color: option.value })}
      ><span className={`element-color-swatch frame-color-swatch--${option.value}`}>{option.value === props.color && <Icon name="check"/>}</span></button>)}</div>
    </div>
    <div className="element-color-picker frame-border-color-picker" role="group" aria-label="Frame border color">
      <header><span>Border color</span><strong>{frameBorderColorLabel(borderColor)}</strong></header>
      <div>{FRAME_BORDER_COLORS.map((option) => <button
        key={option.value}
        type="button"
        aria-label={`${option.label} frame border`}
        aria-pressed={option.value === borderColor}
        className={option.value === borderColor ? "is-selected" : ""}
        onClick={() => onChange({ ...props, borderColor: option.value })}
      ><span className={`element-color-swatch frame-border-swatch frame-border-swatch--${option.value}`}>{option.value === borderColor && <Icon name="check"/>}</span></button>)}</div>
    </div>
  </section>;
}

const markdownEditorShortcutActions: readonly MarkdownEditorShortcutAction[] = [
  "text-bold",
  "text-italic",
  "text-underline",
  "text-paragraph",
  "text-heading-one",
  "text-heading-two",
  "text-heading-three",
  "text-heading-four",
  "text-heading-five",
  "text-align-left",
  "text-align-center",
  "text-align-right",
  "text-align-justify",
  "text-bullet-list",
  "text-ordered-list",
  "text-checklist",
  "text-quote",
  "text-code-block",
  "text-highlight"
];
const defaultMarkdownEditorShortcutBindings = defaultCanvasShortcutBindings();

function isMarkdownEditorShortcutAction(value: CanvasShortcutId | null): value is MarkdownEditorShortcutAction {
  return value !== null && markdownEditorShortcutActions.includes(value as MarkdownEditorShortcutAction);
}

function useMarkdownEditorShortcuts(): MarkdownEditorShortcuts {
  const shortcutBindings = useShell((state) => state.shortcutBindings);
  return useMemo(() => ({
    actions: Object.fromEntries(markdownEditorShortcutActions.map((action) => [action, {
      display: canvasShortcutLabel(action, undefined, shortcutBindings),
      aria: canvasShortcutAria(action, undefined, shortcutBindings)
    }])) as MarkdownEditorShortcuts["actions"],
    actionForEvent: (event: KeyboardEvent) => {
      const action = canvasShortcutForEvent(event, undefined, shortcutBindings);
      return isMarkdownEditorShortcutAction(action) ? action : null;
    },
    defaultActionForEvent: (event: KeyboardEvent) => {
      const action = canvasShortcutForEvent(event, undefined, defaultMarkdownEditorShortcutBindings);
      return isMarkdownEditorShortcutAction(action) ? action : null;
    }
  }), [shortcutBindings]);
}

function InlineStickyEditor({ item, focusPoint, saveItemChanges }: { item: CanvasItem; focusPoint: Point | null; saveItemChanges: SpaceScreenProps["saveItemChanges"] }) {
  const props = item.props.kind === "sticky-note" ? item.props : null;
  const shortcuts = useMarkdownEditorShortcuts();
  const [markdown, setMarkdown] = useState(props?.markdown ?? "");
  const latestItem = useRef(item);
  latestItem.current = item;

  const commit = (nextMarkdown: string) => {
    const current = latestItem.current;
    if (current.props.kind !== "sticky-note" || current.props.lock || current.props.markdown === nextMarkdown) return;
    void saveItemChanges(current, { props: { ...current.props, markdown: nextMarkdown, plainText: markdownToPlainText(nextMarkdown) } });
  };

  if (!props) return null;
  return <MarkdownEditor markdown={markdown} onChange={setMarkdown} onCommit={commit} shortcuts={shortcuts} compact showToolbar floatingToolbar autofocus={!focusPoint} {...(focusPoint ? { focusPoint } : {})}/>;
}

function FolderPreviewCard({ item }: { item: CanvasItem }) {
  const props = item.props;
  // A sealed card peeks out of its folder as frosted paper; nothing about it is readable.
  if (isSealedItem(item)) return <div className={`folder-preview-card folder-preview-card--sealed${props.kind === "sticky-note" ? ` accent-${props.accent}` : ""}`} aria-hidden="true">
    <strong>{LOCKED_FILLER[0]}</strong>
    <i/><i/>
  </div>;
  const text = props.kind === "label" ? props.text
    : props.kind === "sticky-note" ? props.lock ? "Locked note" : props.plainText
      : "title" in props ? props.title
        : "filename" in props ? props.filename
          : "Untitled";
  const accent = props.kind === "sticky-note" ? ` accent-${props.accent}` : "";
  const eventColor = props.kind === "date-event" ? ` date-event-color--${props.color ?? "lilac"}` : "";
  const documentColor = props.kind === "document" ? ` document-color--${props.color ?? "paper"}` : "";
  return <div className={`folder-preview-card folder-preview-card--${props.kind}${accent}${eventColor}${documentColor}`}>
    {props.kind === "image" && props.localUrl && <img src={props.localUrl} alt=""/>}
    <strong>{text || "Untitled"}</strong>
    <i/><i/>
  </div>;
}

function InlineLabelEditor({ item, saveItemChanges, onFinish }: { item: CanvasItem; saveItemChanges: SpaceScreenProps["saveItemChanges"]; onFinish: () => void }) {
  const props = item.props.kind === "label" ? item.props : null;
  const [text, setText] = useState(props?.text ?? "");
  const latestItem = useRef(item);
  const latestText = useRef(text);
  const lastCommitted = useRef(props?.text ?? "");
  const cancelled = useRef(false);
  latestItem.current = item;
  latestText.current = text;

  const commit = useCallback((nextText: string) => {
    const current = latestItem.current;
    if (current.props.kind !== "label" || lastCommitted.current === nextText) return;
    lastCommitted.current = nextText;
    void saveItemChanges(current, { props: { ...current.props, text: nextText } });
  }, [saveItemChanges]);

  useEffect(() => () => {
    if (!cancelled.current) commit(latestText.current);
  }, [commit]);

  if (!props) return null;
  return <input
    className="item-label-input"
    aria-label="Label text"
    autoFocus
    value={text}
    onChange={(event) => setText(event.target.value)}
    onBlur={() => {
      commit(latestText.current);
      onFinish();
    }}
    onKeyDown={(event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.currentTarget.blur();
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        cancelled.current = true;
        onFinish();
      }
    }}
  />;
}

function ItemContent({ item, editing, focusPoint, saveItemChanges, stackItems, folderTransitionPhase, onOpen, onFinishEditing }: { item: CanvasItem; editing: boolean; focusPoint: Point | null; saveItemChanges: SpaceScreenProps["saveItemChanges"]; stackItems: CanvasItem[]; folderTransitionPhase?: "revealing" | "collapsing" | undefined; onOpen: () => void; onFinishEditing: () => void }) {
  const props = item.props;
  const savePreviewTask = (markdown: string) => {
    if ((props.kind !== "sticky-note" && props.kind !== "document") || props.markdown === markdown) return;
    void saveItemChanges(item, { props: { ...props, markdown, plainText: markdownToPlainText(markdown) } });
  };
  if (props.kind === "sticky-note" && props.lock) return <div className={`item-sticky accent-${props.accent} is-locked`}><LockedNoteFace itemId={item.id}/></div>;
  if (props.kind === "sticky-note") return <div className={`item-sticky accent-${props.accent} ${editing ? "is-editing" : ""}`}>{editing ? <InlineStickyEditor item={item} focusPoint={focusPoint} saveItemChanges={saveItemChanges}/> : <MarkdownPreview markdown={props.markdown} onTaskToggle={savePreviewTask}/>}</div>;
  if (props.kind === "document") {
    const title = props.title || "Untitled document";
    return <div className={`item-document document-color--${props.color ?? "paper"}${props.lock ? " is-locked" : ""}`}><span className="document-kicker">BEDROCK NOTE</span><h2>{title}</h2>{props.lock ? <LockedNoteFace itemId={item.id}/> : <MarkdownPreview markdown={props.markdown} hiddenHeading={[title, "Untitled document"]} onTaskToggle={savePreviewTask}/>}<span className="document-page">01</span></div>;
  }
  if (props.kind === "label") return <div className={`item-label item-label--${props.size}`}>{editing ? <InlineLabelEditor item={item} saveItemChanges={saveItemChanges} onFinish={onFinishEditing}/> : props.text}</div>;
  if (props.kind === "image" || props.kind === "video" || props.kind === "pdf" || props.kind === "spreadsheet") return <CanvasMedia props={props}/>;
  if (props.kind === "web-clip") return <CanvasWebClip props={props} onOpen={onOpen}/>;
  if (props.kind === "date-event") return <CanvasDateEvent props={props}/>;
  if (props.kind === "arrow") return <CanvasArrow props={props} width={item.width} height={item.height}/>;
  if (props.kind === "frame") return <CanvasFrame props={props}/>;
  const previews = props.collapsed || folderTransitionPhase ? stackItems.slice(-3) : [];
  return <div className={`item-stack item-stack--${props.color ?? "lilac"} ${props.collapsed ? "is-collapsed" : "is-expanded"} ${folderTransitionPhase ? `is-${folderTransitionPhase}` : ""}`}>
    <div className="item-stack__back" aria-hidden="true"/>
    {previews.length > 0 && <div className="item-stack__previews" aria-hidden="true">{previews.map((preview) => <FolderPreviewCard key={preview.id} item={preview}/>)}</div>}
    <div className="item-stack__front">
      <p>{folderStatusText(props.collapsed, stackItems.length)}</p>
      <h3>{props.title || "Untitled folder"}</h3>
      {props.lock && <span className="item-stack__lock" role="img" aria-label={props.collapsed ? "Locked folder" : "Unlocked for now"}><Icon name={props.collapsed ? "lock" : "unlock"}/></span>}
    </div>
  </div>;
}

function AddMenu({ spaceId, point, anchor, initialView = "menu", createItem, createWebClip, uploadAsset, cacheAsset, closing = false, onClose }: { spaceId: string; point: Point; anchor?: Point; initialView?: "menu" | "web-clip" | "date-event"; createItem: SpaceScreenProps["createItem"]; createWebClip: (url: URL, point?: Point) => Promise<CanvasItem>; uploadAsset?: ((input: { assetId: string; filename: string; mimeType: string; blob: Blob }) => Promise<void>) | undefined; cacheAsset: (assetId: string, blob: Blob) => Promise<void>; closing?: boolean; onClose: () => void }) {
  const shortcutBindings = useShell((state) => state.shortcutBindings);
  const fileInput = useRef<HTMLInputElement>(null);
  const [webClipOpen, setWebClipOpen] = useState(initialView === "web-clip");
  const [dateEventOpen, setDateEventOpen] = useState(initialView === "date-event");
  useEffect(() => {
    if (initialView === "web-clip") setWebClipOpen(true);
    if (initialView === "date-event") setDateEventOpen(true);
  }, [initialView]);
  const { exiting: webClipExiting, requestExit: closeWebClip } = useExitAnimation(() => setWebClipOpen(false));
  const { exiting: anchoredMenuExiting, requestExit: closeAnchoredMenu } = useExitAnimation(onClose);
  const closeMenu = anchor ? closeAnchoredMenu : onClose;
  const add = async (props: ItemProps, close: () => void, preferredSize?: { width: number; height: number }) => { const item = await createItem(spaceId, props, point, preferredSize); close(); return item; };
  const addFile = async (file: File, close: () => void) => {
    if (file.size > 500 * 1024 * 1024) throw new Error("Files must be 500 MB or smaller");
    if (file.type === "text/markdown" || file.type === "text/plain" || /\.(md|markdown|txt)$/i.test(file.name)) {
      const markdown = await file.text();
      const title = file.name.replace(/\.(md|markdown|txt)$/i, "") || "Imported document";
      await add({ kind: "document", title, markdown, plainText: markdownToPlainText(markdown), color: "paper" }, close);
      return;
    }
    const assetId = makeId("asset");
    const localUrl = URL.createObjectURL(file);
    const props = canvasAssetPropsForFile(file, assetId, uploadAsset ? "uploading" : "local", localUrl);
    if (!props) {
      URL.revokeObjectURL(localUrl);
      return;
    }
    await cacheAsset(assetId, file);
    const preferredSize = props.kind === "image" ? await canvasSizeForImageFile(file) ?? undefined : undefined;
    await add(props, close, preferredSize);
  };
  if (webClipOpen) return <div className={`web-clip-entry-layer ${closing || anchoredMenuExiting || webClipExiting ? "is-closing" : ""}`} role="presentation" onMouseDown={() => closeMenu()}>
    <div className="web-clip-entry-dialog" role="dialog" aria-modal="true" aria-label="New web clip" onMouseDown={(event) => event.stopPropagation()}>
      <WebClipForm onSubmit={async (url) => { await createWebClip(url, point); closeMenu(); }} onCancel={closeWebClip}/>
    </div>
  </div>;
  if (dateEventOpen) return <Modal title="New event" className="date-event-modal" onClose={() => setDateEventOpen(false)}>
    <DateEventForm submitLabel="Create event" onSubmit={async (props) => {
      await createItem(spaceId, props, point);
      setDateEventOpen(false);
      closeMenu();
    }} onCancel={() => setDateEventOpen(false)}/>
  </Modal>;
  const contents = (close: () => void) => <>
    <ContextMenuHeader title="New item" closeLabel="Close add menu" onClose={close}/>
    <ContextMenuItem icon="note" label="Document" description="A page for longer thoughts" {...canvasShortcutMenuProps("new-document", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => void add({ kind: "document", title: "Untitled document", markdown: "", plainText: "", color: "paper" }, close)}/>
    <ContextMenuItem icon="sticky-note" label="Note" description="A quick thought" {...canvasShortcutMenuProps("new-note", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => void add({ kind: "sticky-note", markdown: "", plainText: "", accent: "paper" }, close)}/>
    <ContextMenuItem icon="calendar" label="Event" description="A named date with an optional time" {...canvasShortcutMenuProps("new-date-event", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => setDateEventOpen(true)}/>
    <ContextMenuItem icon="arrow" label="Arrow" description="Connect thoughts and show direction" {...canvasShortcutMenuProps("new-arrow", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => void add({ kind: "arrow", path: "straight", color: "ink", stroke: "solid", head: "filled" }, close)}/>
    <ContextMenuItem icon="frame" label="Frame" description="Name and move an area together" {...canvasShortcutMenuProps("new-frame", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => void add({ kind: "frame", title: "New frame", color: "clear", backgroundStyle: "soft", borderStyle: "solid", borderColor: "match" }, close)}/>
    <ContextMenuItem icon="folder" label="Folder" description="Gather related items" {...canvasShortcutMenuProps("new-folder", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => void add({ kind: "stack", title: "New folder", collapsed: true, color: "lilac" }, close)}/>
    <ContextMenuItem icon="browser" label="Web clip" description="Save a public page" {...canvasShortcutMenuProps("new-web-clip", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => setWebClipOpen(true)}/>
    <ContextMenuItem icon="media" label="File" description="Choose an image, video, PDF, or Excel workbook." {...canvasShortcutMenuProps("new-media", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => fileInput.current?.click()}/>
    <ContextMenuItem icon="label" label="Label" description="Name an area" {...canvasShortcutMenuProps("new-label", shortcutBindings)} menuItem={Boolean(anchor)} onClick={() => void add({ kind: "label", text: "New thought", size: "large" }, close)}/>
    <input ref={fileInput} type="file" hidden accept={IMPORTABLE_FILE_ACCEPT} onChange={(event) => { const file = event.target.files?.[0]; if (file) void addFile(file, close); }}/>
  </>;
  if (anchor) return <ContextMenu anchor={anchor} label="Create new item" className="add-menu add-menu--context" onClose={onClose}>{(close) => contents(close)}</ContextMenu>;
  return <div className={`add-menu-layer ${closing ? "is-closing" : ""}`} role="presentation" onMouseDown={onClose}><FloatingPanel className={`add-menu ${closing ? "is-closing" : ""}`} role="dialog" aria-modal="true" aria-label="Add to space" onMouseDown={(event) => event.stopPropagation()}>{contents(onClose)}</FloatingPanel></div>;
}

type NoteProps = Extract<ItemProps, { kind: "sticky-note" | "document" }>;

function noteProps(item: CanvasItem): NoteProps | null {
  return item.props.kind === "sticky-note" || item.props.kind === "document" ? item.props : null;
}

function withLockedBody<T extends NoteProps>(props: T, lock: NoteLock): T {
  return { ...props, markdown: "", plainText: "", lock };
}

function withReadableBody<T extends NoteProps>(props: T, markdown: string): T {
  const { lock: _lock, ...readable } = props;
  return { ...readable, markdown, plainText: markdownToPlainText(markdown) } as T;
}

function lockedItemTitle(item: CanvasItem): string | null {
  if (item.props.kind === "stack") return item.props.title || "Untitled folder";
  return item.props.kind === "document" ? item.props.title || "Untitled document" : null;
}

/** A locked folder that hasn't been opened with its PIN on this device. */
function isClosedLockedFolder(item: CanvasItem): boolean {
  return item.props.kind === "stack" && isLockedFolder(item) && item.props.collapsed;
}

// Stand-in text for the frosted face. The real body is encrypted and never rendered here.
const LOCKED_FILLER = [
  "Start with the account that matters most and note where each recovery code lives, which device holds the second factor, and who to call first.",
  "Keep the spare key details, the alarm code for the side door, and the numbers to call when something at the house needs attention.",
  "Write down the passphrase hints, where the backups are kept, and the order to restore things in when a device has to be replaced."
];

function LockedNoteFace({ itemId }: { itemId: string }) {
  const filler = LOCKED_FILLER[[...itemId].reduce((sum, character) => sum + character.charCodeAt(0), 0) % LOCKED_FILLER.length];
  return <div className="locked-note-face" role="img" aria-label="Locked contents">
    <p aria-hidden="true">{filler}</p>
    <span className="locked-note-face__badge" aria-hidden="true"><Icon name="lock"/></span>
  </div>;
}

/** Asks for the PIN, then edits the decrypted body; the key is forgotten when the editor closes. */
function LockedNoteEditor({ item, store, saveItemChanges }: { item: CanvasItem; store: LocalStore; saveItemChanges: SpaceScreenProps["saveItemChanges"] }) {
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const [session, setSession] = useState<UnlockedNote | null>(null);
  const lock = noteProps(item)?.lock;
  if (!lock) return null;
  if (!session) return <NoteLockDialog mode="unlock" itemTitle={lockedItemTitle(item)} onCancel={() => setEditorItemId(null)} onSubmit={async (pin) => {
    const opened = await unlockNote(pin, lock);
    if (!opened) return WRONG_PIN_MESSAGE;
    setSession(opened);
    return null;
  }}/>;
  return <EditorDialog item={item} store={store} saveItemChanges={saveItemChanges} position={null} onPositionChange={ignorePositionChange} unlocked={session}/>;
}

function ignorePositionChange(): void {}

function EditorDialog({ item, store, saveItemChanges, position, onPositionChange, selectTitle = false, unlocked }: { position: WorkingSession | null; onPositionChange: (item: CanvasItem, cursor: number, scrollTop: number) => void; item: CanvasItem; store: LocalStore; saveItemChanges: SpaceScreenProps["saveItemChanges"]; selectTitle?: boolean; unlocked?: UnlockedNote }) {
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const documentColorInEditor = useShell((state) => state.documentColorInEditor);
  const shortcuts = useMarkdownEditorShortcuts();
  const bodyEditor = useRef<MarkdownEditorHandle>(null);
  const titleInput = useRef<HTMLInputElement>(null);
  const writingSheet = useRef<HTMLDivElement>(null);
  const initialPosition = useRef(position);
  const cursor = useRef(position?.cursor ?? 0);
  const latestPositionChange = useRef(onPositionChange);
  latestPositionChange.current = onPositionChange;
  useEffect(() => {
    const body = writingSheet.current?.closest<HTMLElement>(".br-modal__body");
    if (!body) return;
    const frame = requestAnimationFrame(() => { body.scrollTop = initialPosition.current?.scrollTop ?? 0; });
    const remember = () => latestPositionChange.current(item, cursor.current, body.scrollTop);
    body.addEventListener("scroll", remember, { passive: true });
    return () => { cancelAnimationFrame(frame); body.removeEventListener("scroll", remember); remember(); };
  }, [item.id]);

  const [markdown, setMarkdown] = useState(unlocked ? unlocked.markdown : "markdown" in item.props ? item.props.markdown : "");
  const [title, setTitle] = useState(item.props.kind === "document" ? item.props.title : "");
  const [recovered, setRecovered] = useState<RecoveredVersion[]>([]);
  const [showRecovered, setShowRecovered] = useState(false);
  const lockSession = useRef(unlocked ?? null);
  const lockedSaves = useRef(Promise.resolve());
  const lastLockedSave = useRef({ markdown, title });
  useEffect(() => { if (!unlocked) void store.listRecoveredVersions(item.id).then(setRecovered); }, [item.id, store, unlocked]);
  useEffect(() => {
    const openDocumentSearch = (event: KeyboardEvent) => {
      const insideEditorDialog = event.target instanceof Element && event.target.closest(".editor-modal");
      const findShortcut = !event.altKey && !event.shiftKey && event.code === "KeyF" && event.metaKey !== event.ctrlKey && (event.metaKey || event.ctrlKey);
      if (!insideEditorDialog || !findShortcut) return;
      event.preventDefault();
      event.stopPropagation();
      bodyEditor.current?.openSearch();
    };
    window.addEventListener("keydown", openDocumentSearch);
    return () => window.removeEventListener("keydown", openDocumentSearch);
  }, []);
  useEffect(() => {
    if (!selectTitle) return;
    titleInput.current?.focus();
    titleInput.current?.select();
  }, [selectTitle]);
  const commitLocked = (nextMarkdown: string) => {
    const props = noteProps(item);
    const nextTitle = title;
    // Saves are chained so an older ciphertext can never land after a newer one.
    const save = lockedSaves.current.then(async () => {
      const session = lockSession.current;
      if (!session || !props || (lastLockedSave.current.markdown === nextMarkdown && lastLockedSave.current.title === nextTitle)) return;
      const relocked = await relockNote(session, nextMarkdown);
      lockSession.current = relocked;
      lastLockedSave.current = { markdown: nextMarkdown, title: nextTitle };
      await saveItemChanges(item, { props: withLockedBody(props.kind === "document" ? { ...props, title: nextTitle } : props, relocked.lock) });
    });
    lockedSaves.current = save.catch(() => undefined);
    return save;
  };
  const commit = async (nextMarkdown = markdown) => {
    if (lockSession.current) return commitLocked(nextMarkdown);
    if (item.props.kind === "sticky-note") await saveItemChanges(item, { props: { ...item.props, markdown: nextMarkdown, plainText: markdownToPlainText(nextMarkdown) } });
    if (item.props.kind === "document") await saveItemChanges(item, { props: { ...item.props, title, markdown: nextMarkdown, plainText: markdownToPlainText(nextMarkdown) } });
  };
  const close = () => { void commit(); runViewTransition(() => setEditorItemId(null), item.id, "out"); };
  const editorDocumentColor = documentColorInEditor && item.props.kind === "document" && item.props.color && item.props.color !== "paper" ? item.props.color : null;
  return <Modal title={item.props.kind === "document" ? "Document" : "Note"} onClose={close} wide immersive className={`editor-modal editor-modal--${item.props.kind}${editorDocumentColor ? ` editor-modal--tinted document-color--${editorDocumentColor}` : ""}`}>
    <div ref={writingSheet} className={`writing-sheet ${item.props.kind === "sticky-note" ? `writing-sheet--sticky accent-${item.props.accent}` : ""}`} style={{ viewTransitionName: itemTransitionName(item.id) }}>
      {item.props.kind === "document" && <input
        ref={titleInput}
        className="document-title-input"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
          event.preventDefault();
          bodyEditor.current?.focusFirstParagraph();
        }}
        aria-label="Document title"
      />}
      <MarkdownEditor ref={bodyEditor} initialCursor={initialPosition.current?.cursor} onCursorChange={(next) => { cursor.current = next; const body = writingSheet.current?.closest<HTMLElement>(".br-modal__body"); onPositionChange(item, next, body?.scrollTop ?? 0); }} markdown={markdown} onChange={setMarkdown} onCommit={(value: string) => void commit(value)} hiddenFirstHeading={item.props.kind === "document" ? [title, "Untitled document"] : undefined} shortcuts={shortcuts} autofocus={!selectTitle && !initialPosition.current} bottomScrollClearance={EDITOR_TOOLBAR_SCROLL_CLEARANCE} enableTables={item.props.kind === "document"}/>
    </div>
    {showRecovered && <div className="recovered-versions"><header><span>RECOVERED VERSIONS</span><button onClick={() => setShowRecovered(false)}>Hide</button></header>{recovered.map((version) => <button key={version.id} onClick={() => { setMarkdown(version.markdown); setShowRecovered(false); }}><strong>{new Date(version.recoveredAt).toLocaleString()}</strong><small>{markdownToPlainText(version.markdown).slice(0, 110) || "Empty note"}</small></button>)}</div>}
    <div className="editor-footer"><span>Markdown · saved locally</span><div><Button onClick={() => { if (item.props.kind === "document") downloadTextFile(textDownloadForItem({ ...item.props, title }, markdown)); else if (item.props.kind === "sticky-note") downloadTextFile(textDownloadForItem(item.props, markdown)); }}>{item.props.kind === "document" ? "Download .md" : "Download .txt"}</Button>{recovered.length > 0 && <Button onClick={() => setShowRecovered(!showRecovered)}>Recovered · {recovered.length}</Button>}<Button tone="primary" onClick={close}>Done</Button></div></div>
  </Modal>;
}

function DateEventDialog({ item, saveItemChanges }: { item: CanvasItem; saveItemChanges: SpaceScreenProps["saveItemChanges"] }) {
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const props = item.props.kind === "date-event" ? item.props : null;
  if (!props) return null;
  const close = () => runViewTransition(() => setEditorItemId(null), item.id, "out");
  return <Modal title="Edit event" className="date-event-modal" onClose={close}>
    <DateEventForm initialValue={props} submitLabel="Save changes" onSubmit={async (nextProps) => {
      await saveItemChanges(item, { props: nextProps });
      close();
    }} onCancel={close}/>
  </Modal>;
}

const fallbackPalette = ["#f3f4ef", "#a8bbb8", "#4fa3b5", "#24778a", "#806c58", "#4d2b20", "#080a09"];

function useImagePalette(source: string | undefined): string[] {
  const [palette, setPalette] = useState(fallbackPalette);
  useEffect(() => {
    if (!source) return;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 48; canvas.height = 48;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) return;
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        const counts = new Map<string, number>();
        for (let index = 0; index < pixels.length; index += 16) {
          if ((pixels[index + 3] ?? 0) < 180) continue;
          const red = Math.min(255, Math.round((pixels[index] ?? 0) / 32) * 32);
          const green = Math.min(255, Math.round((pixels[index + 1] ?? 0) / 32) * 32);
          const blue = Math.min(255, Math.round((pixels[index + 2] ?? 0) / 32) * 32);
          const key = `${red},${green},${blue}`;
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }
        const selected: number[][] = [];
        for (const [key] of [...counts.entries()].sort((left, right) => right[1] - left[1])) {
          const color = key.split(",").map(Number);
          if (selected.every((existing) => Math.hypot((existing[0] ?? 0) - (color[0] ?? 0), (existing[1] ?? 0) - (color[1] ?? 0), (existing[2] ?? 0) - (color[2] ?? 0)) > 58)) selected.push(color);
          if (selected.length === 7) break;
        }
        if (selected.length >= 4) setPalette(selected.map((color) => `rgb(${color.join(" ")})`));
      } catch { /* A remote image without CORS keeps the calm fallback palette. */ }
    };
    image.src = source;
    return () => { image.onload = null; };
  }, [source]);
  return palette;
}

function MediaDialog({ item, saveItemChanges, copyFileToClipboard }: { item: CanvasItem; saveItemChanges: SpaceScreenProps["saveItemChanges"]; copyFileToClipboard: CopyFileToClipboard | undefined }) {
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const props = item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" ? item.props : null;
  const imageFrameRef = useRef<HTMLElement>(null);
  const imageZoomRef = useRef(1);
  const imageDrag = useRef<{ pointerId: number; start: ImagePreviewOffset; offset: ImagePreviewOffset } | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [duration, setDuration] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffset, setImageOffset] = useState<ImagePreviewOffset>({ x: 0, y: 0 });
  const [imagePanning, setImagePanning] = useState(false);
  const [note, setNote] = useState(props?.kind === "image" ? props.alt : "");
  const palette = useImagePalette(props?.kind === "image" ? props.localUrl : undefined);
  const clampImageOffset = (offset: ImagePreviewOffset, zoom: number) => {
    const frame = imageFrameRef.current;
    const image = frame?.querySelector("img");
    if (!frame || !image) return { x: 0, y: 0 };
    return clampImagePreviewOffset(
      offset,
      zoom,
      { width: image.clientWidth, height: image.clientHeight },
      { width: frame.clientWidth, height: frame.clientHeight }
    );
  };
  useEffect(() => {
    if (props?.kind !== "image") return;
    const frame = imageFrameRef.current;
    if (!frame) return;
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const nextZoom = imagePreviewZoomFromWheel(imageZoomRef.current, event.deltaY);
      imageZoomRef.current = nextZoom;
      setImageZoom(nextZoom);
      setImageOffset((current) => clampImageOffset(current, nextZoom));
    };
    frame.addEventListener("wheel", handleWheel, { passive: false });
    return () => frame.removeEventListener("wheel", handleWheel);
  }, [props?.kind]);
  if (!props) return null;
  const close = () => runViewTransition(() => setEditorItemId(null), item.id, "out");
  const download = () => {
    if (!props.localUrl) return;
    const anchor = document.createElement("a");
    anchor.href = props.localUrl; anchor.download = props.filename; anchor.click();
  };
  const remove = () => { void saveItemChanges(item, { deletedAt: Date.now() }); close(); };
  const commitNote = () => { if (props.kind === "image" && note !== props.alt) void saveItemChanges(item, { props: { ...props, alt: note } }); };
  const format = props.kind === "pdf" ? "PDF" : props.mimeType.split("/")[1]?.split(";")[0]?.toUpperCase() || props.kind.toUpperCase();
  const videoAspect = dimensions.width && dimensions.height ? dimensions.width / dimensions.height : 16 / 10;
  const title = props.kind === "image" ? "Image" : props.kind === "video" ? "Video" : "PDF";
  const startImagePan = (event: ReactPointerEvent<HTMLElement>) => {
    if (props.kind !== "image" || event.button !== 0 || imageZoomRef.current <= 1) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    imageDrag.current = { pointerId: event.pointerId, start: { x: event.clientX, y: event.clientY }, offset: imageOffset };
    setImagePanning(true);
  };
  const moveImagePan = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = imageDrag.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    setImageOffset(clampImageOffset({
      x: drag.offset.x + event.clientX - drag.start.x,
      y: drag.offset.y + event.clientY - drag.start.y
    }, imageZoomRef.current));
  };
  const endImagePan = (event: ReactPointerEvent<HTMLElement>) => {
    if (imageDrag.current?.pointerId !== event.pointerId) return;
    imageDrag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setImagePanning(false);
  };
  return <Modal title={title} onClose={close} wide immersive className={`media-modal media-modal--${props.kind}`}>
    <div className={`media-detail media-detail--${props.kind}`}>
      {props.kind === "image" && <MediaPalette colors={palette}/>}
      <div className="media-focus-column">
        <figure
          ref={imageFrameRef}
          className={`media-focus-frame media-focus-frame--${props.kind} ${props.kind === "image" && imageZoom > 1 ? "is-pannable" : ""} ${imagePanning ? "is-panning" : ""}`}
          style={{ viewTransitionName: itemTransitionName(item.id), "--media-aspect": videoAspect } as CSSProperties}
          onPointerDown={startImagePan}
          onPointerMove={moveImagePan}
          onPointerUp={endImagePan}
          onPointerCancel={endImagePan}
        >
          {props.kind === "image"
            ? props.localUrl
              ? <img src={props.localUrl} alt={props.alt || props.filename} draggable={false} style={{ transform: `translate3d(${imageOffset.x}px, ${imageOffset.y}px, 0) scale(${imageZoom})` }} onLoad={(event) => setDimensions({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}/>
              : <div className="media-placeholder"><Icon name="image"/></div>
            : props.kind === "video"
              ? <VideoPlayer src={props.localUrl} filename={props.filename} variant="detail" autoPlay onMetadata={(metadata) => { setDimensions({ width: metadata.width, height: metadata.height }); setDuration(metadata.duration); }}/>
              : <PdfViewer props={props} variant="detail"/>
          }
        </figure>
        {props.kind === "image" && <textarea value={note} onChange={(event) => setNote(event.target.value)} onBlur={commitNote} placeholder="Write a note…" aria-label="Image note"/>}
      </div>
      <aside className="media-metadata">
        {(props.kind === "video" || props.kind === "pdf") && <><span>Format</span><strong>{format}</strong></>}
        {props.kind !== "pdf" && <><span>Resolution</span><strong>{dimensions.width && dimensions.height ? `${dimensions.width} × ${dimensions.height}` : "—"}</strong></>}
        {props.kind === "video" && <><span>Duration</span><strong>{duration > 0 ? formatVideoTime(duration) : "—"}</strong></>}
        <span>Filename</span><strong>{props.filename}</strong>
        <span>Added</span><strong>{new Date(item.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</strong>
      </aside>
    </div>
    <FloatingPanel className="media-actions"><AssetCopyButton props={props} copyFileToClipboard={copyFileToClipboard}/><IconButton label="Download" icon="download" onClick={download} disabled={!props.localUrl}/><IconButton label="Move to Trash" icon="trash" onClick={remove}/></FloatingPanel>
  </Modal>;
}

function SpreadsheetDialog({ item, saveItemChanges, copyFileToClipboard }: { item: CanvasItem; saveItemChanges: SpaceScreenProps["saveItemChanges"]; copyFileToClipboard: CopyFileToClipboard | undefined }) {
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const props = item.props.kind === "spreadsheet" ? item.props : null;
  if (!props) return null;
  const close = () => runViewTransition(() => setEditorItemId(null), item.id, "out");
  const download = () => {
    if (!props.localUrl) return;
    const anchor = document.createElement("a");
    anchor.href = props.localUrl;
    anchor.download = props.filename;
    anchor.click();
  };
  const remove = () => { void saveItemChanges(item, { deletedAt: Date.now() }); close(); };
  return <Modal title="Workbook" onClose={close} wide immersive className="spreadsheet-modal">
    <div className="spreadsheet-dialog">
      <div className="spreadsheet-dialog__frame" style={{ viewTransitionName: itemTransitionName(item.id) }}>
        <SpreadsheetViewer props={props} variant="detail" addedAt={item.createdAt}/>
      </div>
    </div>
    <FloatingPanel className="media-actions spreadsheet-actions" aria-label="Workbook actions">
      <AssetCopyButton props={props} copyFileToClipboard={copyFileToClipboard}/>
      <IconButton label="Download" icon="download" onClick={download} disabled={!props.localUrl}/>
      <IconButton label="Move to Trash" icon="trash" onClick={remove}/>
    </FloatingPanel>
  </Modal>;
}

function WebClipDialog({ item }: { item: CanvasItem }) {
  const setEditorItemId = useShell((state) => state.setEditorItemId);
  const props = item.props.kind === "web-clip" ? item.props : null;
  const [copied, setCopied] = useState(false);
  const copyResetTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
  }, []);

  if (!props) return null;
  const close = () => runViewTransition(() => setEditorItemId(null), item.id, "out");
  const statusLabel = props.status === "ready" ? "Static capture" : props.status === "pending" ? "Capture in progress" : "Capture unavailable";
  const capturedAt = new Date(item.createdAt);
  const capturedDate = capturedAt.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  const capturedTime = capturedAt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const hostname = new URL(props.url).hostname.replace(/^www\./, "");
  const copyAddress = async () => {
    let copySucceeded = false;
    try {
      if (!navigator.clipboard) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(props.url);
      copySucceeded = true;
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = props.url;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.append(textarea);
      textarea.select();
      try { copySucceeded = document.execCommand("copy"); } catch { /* Clipboard access can be denied by the browser. */ }
      textarea.remove();
    }
    if (!copySucceeded) return;
    setCopied(true);
    if (copyResetTimer.current !== null) window.clearTimeout(copyResetTimer.current);
    copyResetTimer.current = window.setTimeout(() => setCopied(false), 1_600);
  };

  return <Modal title={`Website preview: ${props.title || hostname}`} onClose={close} wide immersive className="web-clip-modal">
    <div className={`web-clip-detail web-clip-detail--${props.status}`}>
      <div className="web-clip-detail__content">
        <header className="web-clip-detail__metadata">
          <span>{capturedDate} · {capturedTime} · {statusLabel}</span>
          <strong>{props.title || hostname}</strong>
          <code>{props.url}</code>
        </header>
        <figure className="web-clip-detail__capture" style={{ viewTransitionName: itemTransitionName(item.id) }}>
          {props.previewUrl ? <img src={props.previewUrl} alt={`Static preview of ${props.title || hostname}`}/> : <div className="web-clip-detail__fallback">
            <span>{props.siteName || hostname}</span>
            <div><Icon name={props.status === "failed" ? "cloud" : "browser"}/><small>{statusLabel}</small></div>
            <h1>{props.title || hostname}</h1>
            <p>{props.description || (props.status === "failed" ? "The page details were saved, but its visual preview could not be captured." : "The page details are saved while Bedrock prepares its visual preview.")}</p>
            <code>{hostname}</code>
          </div>}
        </figure>
      </div>
    </div>
    <FloatingPanel className="web-clip-actions" aria-label="Website actions">
      <IconButton label={copied ? "Website address copied" : "Copy website address"} icon={copied ? "check" : "copy"} onClick={() => void copyAddress()}/>
      <span aria-hidden="true"/>
      <IconButton label="Open website" icon="external-link" onClick={() => window.open(props.url, "_blank", "noopener,noreferrer")}/>
    </FloatingPanel>
  </Modal>;
}

const searchItemIcons: Record<ItemType, IconName> = {
  "sticky-note": "sticky-note",
  document: "document",
  label: "label",
  image: "image",
  video: "video",
  pdf: "pdf",
  spreadsheet: "spreadsheet",
  "web-clip": "browser",
  "date-event": "calendar",
  stack: "stack",
  arrow: "arrow",
  frame: "frame"
};

const searchItemLabels: Record<ItemType, string> = {
  "sticky-note": "Note",
  document: "Document",
  label: "Label",
  image: "Image",
  video: "Video",
  pdf: "PDF",
  spreadsheet: "Workbook",
  "web-clip": "Web clip",
  "date-event": "Event",
  stack: "Folder",
  arrow: "Arrow",
  frame: "Frame"
};

function searchResultPresentation(result: SearchResult): { title: string; excerpt: string; context: string; kind: string; icon: IconName } {
  if (result.entityType === "space") return { title: result.title, excerpt: "", context: "", kind: "Space", icon: "grid" };
  if (result.itemType) return {
    title: result.title,
    excerpt: result.excerpt,
    context: result.spaceTitle ? `In ${result.spaceTitle}` : "",
    kind: searchItemLabels[result.itemType],
    icon: searchItemIcons[result.itemType]
  };
  const genericTitle = result.title.toLocaleLowerCase();
  const legacyType = (genericTitle === "sticky note" ? "sticky-note" : genericTitle.replace(" ", "-")) as ItemType;
  const genericIcon = searchItemIcons[legacyType];
  if (genericIcon) {
    return {
      title: result.excerpt,
      excerpt: "",
      context: "",
      kind: searchItemLabels[legacyType],
      icon: genericIcon
    };
  }
  return { title: result.title, excerpt: result.excerpt, context: "", kind: "Item", icon: "note" };
}

function SearchDialog({ store }: { store: LocalStore }) {
  const setSearchOpen = useShell((state) => state.setSearchOpen);
  const setScreen = useShell((state) => state.setScreen);
  const setSearchTargetItemId = useShell((state) => state.setSearchTargetItemId);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const { exiting, requestExit } = useExitAnimation(() => setSearchOpen(false));
  const normalizedQuery = query.trim();
  const openResult = useCallback((result: SearchResult) => {
    requestExit(() => {
      setScreen({ kind: "space", spaceId: result.spaceId });
      if (result.entityType === "item") setSearchTargetItemId(result.entityId);
    });
  }, [requestExit, setScreen, setSearchTargetItemId]);
  const handleSearchKeys = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0 || searching) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + direction + results.length) % results.length);
    }
    if (event.key === "Enter") {
      const result = results[activeIndex];
      if (!result) return;
      event.preventDefault();
      openResult(result);
    }
  };
  useEffect(() => {
    let active = true;
    if (!normalizedQuery) {
      setResults([]);
      setActiveIndex(0);
      setSearching(false);
      setSearchError("");
      return;
    }
    setResults([]);
    setSearching(true);
    setSearchError("");
    const timer = setTimeout(() => {
      void store.search(normalizedQuery).then((nextResults) => {
        if (active) {
          setResults(nextResults);
          setActiveIndex(0);
        }
      }).catch(() => {
        if (active) setSearchError("Search couldn't finish. Try again.");
      }).finally(() => {
        if (active) setSearching(false);
      });
    }, 160);
    return () => { active = false; clearTimeout(timer); };
  }, [normalizedQuery, store]);
  useEffect(() => {
    document.getElementById(`bedrock-search-result-${activeIndex}`)?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);
  return <Modal title="Search Bedrock" className={`search-modal ${exiting ? "is-closing" : ""}`} onClose={requestExit}>
    <div className="search-command">
      <div className="search-dialog-input" role="search">
        <span className="search-dialog-input__icon"><Icon name="search"/></span>
        <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={handleSearchKeys} placeholder="Search titles and contents" aria-label="Search your Bedrock library" aria-describedby="search-library-scope" role="combobox" aria-autocomplete="list" aria-expanded={results.length > 0 && !searching} aria-controls="bedrock-search-results" aria-activedescendant={results.length > 0 && !searching ? `bedrock-search-result-${activeIndex}` : undefined}/>
        {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search"><Icon name="close"/></button>}
      </div>
      <p id="search-library-scope">Every space, note, label, folder, and clip.</p>
    </div>
    <div className="search-results" aria-busy={searching}>
      {!normalizedQuery && <div className="search-state search-state--prompt">
        <span className="search-state__mark"><StrataMark size={48}/></span>
        <div><strong>Find anything in your field</strong><p>Search across your whole library, including the words inside notes and saved clips.</p></div>
      </div>}
      {normalizedQuery && searching && <div className="search-skeleton" role="status" aria-label="Searching your library">
        {[0, 1, 2].map((index) => <div key={index}><i/><span><b/><small/></span></div>)}
      </div>}
      {normalizedQuery && !searching && searchError && <div className="search-state search-state--empty" role="alert">
        <span className="search-state__icon"><Icon name="search"/></span>
        <div><strong>Search is unavailable</strong><p>{searchError}</p></div>
      </div>}
      {normalizedQuery && !searching && !searchError && results.length === 0 && <div className="search-state search-state--empty" role="status">
        <span className="search-state__icon"><Icon name="search"/></span>
        <div><strong>No matches for “{normalizedQuery}”</strong><p>Try fewer words or search for text from inside a note.</p></div>
      </div>}
      {normalizedQuery && !searching && !searchError && results.length > 0 && <>
        <div className="search-results__heading" role="status"><span>Best matches</span><small>{results.length} {results.length === 1 ? "result" : "results"}</small></div>
        <div className="search-results__list" id="bedrock-search-results" role="listbox" aria-label="Search results">
          {results.map((result, index) => {
            const presentation = searchResultPresentation(result);
            const description = [presentation.excerpt, presentation.context].filter(Boolean).join(" · ");
            return <button id={`bedrock-search-result-${index}`} role="option" aria-selected={index === activeIndex} className={`search-result search-result--${result.entityType} ${index === activeIndex ? "is-active" : ""}`} key={`${result.entityType}-${result.entityId}`} onMouseEnter={() => setActiveIndex(index)} onClick={() => openResult(result)}>
              <span className="search-result__icon"><Icon name={presentation.icon}/></span>
              <span className="search-result__copy"><span className="search-result__kind">{presentation.kind}</span><strong>{presentation.title}</strong>{description && <small>{description}</small>}</span>
              <Icon className="search-result__arrow" name="arrow-right"/>
            </button>;
          })}
        </div>
      </>}
    </div>
    <footer className="search-dialog-footer"><span><i/>Private, local search</span><span><kbd>↑↓</kbd> navigate <kbd>Enter</kbd> open <kbd>Esc</kbd> close</span></footer>
  </Modal>;
}

function SettingsDialog({ authMode, store, spaces, itemsBySpace, saveSpaceChanges, saveItemChanges, permanentlyDelete, getAccountStorageUsage, storageRefreshVersion, onSignOut }: {
  authMode: "local" | "authenticated";
  store: LocalStore;
  spaces: Space[];
  itemsBySpace: Map<string, CanvasItem[]>;
  saveSpaceChanges: (space: Space, patch: Partial<Pick<Space, "title" | "theme" | "starred" | "deletedAt">>) => Promise<void>;
  saveItemChanges: SpaceScreenProps["saveItemChanges"];
  permanentlyDelete: (entityType: "space" | "item", entityId: string, baseRevision: number) => Promise<void>;
  getAccountStorageUsage?: (() => Promise<AccountStorageUsage>) | undefined;
  storageRefreshVersion: number;
  onSignOut?: (() => Promise<void> | void) | undefined;
}) {
  const setSettingsOpen = useShell((state) => state.setSettingsOpen);
  const syncState = useShell((state) => state.syncState);
  const theme = useShell((state) => state.theme);
  const setTheme = useShell((state) => state.setTheme);
  const startupMode = useShell((state) => state.startupMode);
  const setStartupMode = useShell((state) => state.setStartupMode);
  const documentColorInEditor = useShell((state) => state.documentColorInEditor);
  const setDocumentColorInEditor = useShell((state) => state.setDocumentColorInEditor);
  const controlScheme = useShell((state) => state.controlScheme);
  const setControlScheme = useShell((state) => state.setControlScheme);
  const [exporting, setExporting] = useState(false);
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const [recentlyDeletedOpen, setRecentlyDeletedOpen] = useState(false);
  const [storageUsage, setStorageUsage] = useState<AccountStorageUsage | null>(null);
  const [storageError, setStorageError] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { exiting: settingsExiting, requestExit: requestSettingsExit } = useExitAnimation(() => setSettingsOpen(false));
  const { exiting: keyboardShortcutsExiting, requestExit: requestKeyboardShortcutsExit } = useExitAnimation(() => setKeyboardShortcutsOpen(false));
  const { exiting: recentlyDeletedExiting, requestExit: requestRecentlyDeletedExit } = useExitAnimation(() => setRecentlyDeletedOpen(false));

  useEffect(() => {
    if (!getAccountStorageUsage) return;
    let active = true;
    setStorageError(false);
    void getAccountStorageUsage().then((usage) => { if (active) setStorageUsage(usage); }).catch(() => { if (active) setStorageError(true); });
    return () => { active = false; };
  }, [getAccountStorageUsage, storageRefreshVersion]);

  const cloudUsagePercent = storageUsage?.limitBytes
    ? Math.min(100, (storageUsage.usedBytes / storageUsage.limitBytes) * 100)
    : null;
  const deletedSpaceCount = spaces.filter((space) => space.deletedAt !== null).length;
  const deletedItemCount = [...itemsBySpace.values()].flat().filter((item) => item.deletedAt !== null).length;
  const recentlyDeletedCount = deletedSpaceCount + deletedItemCount;
  const childDialogOpen = keyboardShortcutsOpen || recentlyDeletedOpen;
  const signInRequired = syncState.error?.code === "sign_in_required" || syncState.error?.code === "unauthorized";
  const settingsSyncLabel = authMode === "local"
    ? "Local only"
    : signInRequired
      ? "Sign in required"
      : ({
          "local-only": "Local only",
          offline: "Offline",
          syncing: "Syncing",
          "up-to-date": "Synced",
          retrying: "Retrying",
          attention: "Needs attention"
        } satisfies Record<SyncStatus["phase"], string>)[syncState.phase];
  const settingsSyncTone = authMode === "authenticated" && syncState.phase === "up-to-date"
    ? "is-online"
    : authMode === "authenticated" && (signInRequired || syncState.phase === "attention")
      ? "is-attention"
      : "";

  return <>
    <div aria-hidden={childDialogOpen ? true : undefined} inert={childDialogOpen ? true : undefined}>
      <Modal title="Settings" wide className={`settings-modal ${settingsExiting ? "is-closing" : ""}`} onClose={() => keyboardShortcutsOpen ? requestKeyboardShortcutsExit() : recentlyDeletedOpen ? requestRecentlyDeletedExit() : requestSettingsExit()}>
        <div className="settings-workspace">
          <header className="settings-intro">
            <div>
              <span>WORKSPACE PREFERENCES</span>
              <h3>Make the canvas feel like yours.</h3>
              <p>Adjust how Bedrock looks, moves, and keeps your library safe.</p>
            </div>
            <StrataMark size={78}/>
          </header>

          <div className="settings-primary-grid">
            <section className="settings-panel settings-appearance" aria-labelledby="settings-appearance-title">
              <header className="settings-panel__header">
                <span className="settings-panel__number">01</span>
                <div><span>APPEARANCE</span><h4 id="settings-appearance-title">Canvas theme</h4></div>
              </header>
              <div className="theme-choices" role="radiogroup" aria-label="Canvas theme">
                <button type="button" role="radio" className={theme === "light" ? "is-active" : ""} aria-checked={theme === "light"} onClick={() => setTheme("light")}>
                  <span className="theme-preview theme-preview--light" aria-hidden="true"><i/><i/><i/><i/></span>
                  <span className="theme-choice__copy"><strong><Icon name="sun"/>Light</strong><small>Soft paper canvas</small></span>
                  <span className="theme-choice__check"><Icon name="check"/></span>
                </button>
                <button type="button" role="radio" className={theme === "dark" ? "is-active" : ""} aria-checked={theme === "dark"} onClick={() => setTheme("dark")}>
                  <span className="theme-preview theme-preview--dark" aria-hidden="true"><i/><i/><i/><i/></span>
                  <span className="theme-choice__copy"><strong><Icon name="moon"/>Dark</strong><small>Low-light workspace</small></span>
                  <span className="theme-choice__check"><Icon name="check"/></span>
                </button>
              </div>
              <p className="settings-panel__note">Applies throughout your library and every canvas.</p>
            </section>

            <section className="settings-panel settings-controls" aria-labelledby="settings-controls-title">
              <header className="settings-panel__header">
                <span className="settings-panel__number">02</span>
                <div><span>NAVIGATION</span><h4 id="settings-controls-title">Canvas controls</h4></div>
              </header>
              <div className="control-scheme-choices" role="radiogroup" aria-label="Canvas controls">
                <button type="button" role="radio" className={controlScheme === "classic" ? "is-active" : ""} aria-checked={controlScheme === "classic"} onClick={() => setControlScheme("classic")}>
                  <span><strong>Classic</strong><small>Drag to pan · Scroll to move · Ctrl/⌘ + scroll to zoom · Middle-click to focus</small></span>
                  <span className="control-scheme-choice__check"><Icon name="check"/></span>
                </button>
                <button type="button" role="radio" className={controlScheme === "sebsoto" ? "is-active" : ""} aria-checked={controlScheme === "sebsoto"} onClick={() => setControlScheme("sebsoto")}>
                  <span><strong>Sebsoto</strong><small>Middle-drag to pan · Scroll to zoom · Click to focus · Double-click to open</small></span>
                  <span className="control-scheme-choice__check"><Icon name="check"/></span>
                </button>
              </div>
              <p className="settings-panel__note">Touch gestures continue to pan and pinch-zoom in either mode.</p>
            </section>

            <section className="settings-panel settings-shortcuts" aria-labelledby="settings-shortcuts-title">
              <header className="settings-panel__header">
                <span className="settings-panel__number">03</span>
                <div><span>KEYBOARD</span><h4 id="settings-shortcuts-title">Shortcuts</h4></div>
              </header>
              <div className="settings-shortcuts__count" aria-hidden="true"><strong>{CANVAS_SHORTCUT_ACTIONS.length}</strong><span>actions</span></div>
              <p>Customize {CANVAS_SHORTCUT_ACTIONS.length} canvas and text actions, plus review the fixed OS paste shortcut.</p>
              <button type="button" className="settings-open-button" onClick={() => setKeyboardShortcutsOpen(true)}>Customize<Icon name="arrow-right"/></button>
            </section>
          </div>

          <section className="settings-panel settings-startup" aria-labelledby="settings-startup-title">
            <header className="settings-panel__header">
              <span className="settings-panel__number" aria-hidden="true">04</span>
              <div><span>ON STARTUP</span><h4 id="settings-startup-title">When Bedrock opens</h4></div>
              <small className="settings-startup__scope">Saved on this device</small>
            </header>
            <div className="settings-startup-options" role="radiogroup" aria-labelledby="settings-startup-title" aria-describedby="settings-startup-note">
              <label className="settings-startup-choice">
                <Icon name="grid" className="settings-startup-choice__icon" aria-hidden="true"/>
                <span className="settings-startup-choice__copy"><strong>Open the homepage</strong><small id="settings-startup-home-description">Start in your library of spaces.</small></span>
                <input type="radio" name="startup" aria-label="Open the homepage" aria-describedby="settings-startup-home-description" checked={startupMode === "library"} onChange={() => setStartupMode("library")}/>
              </label>
              <label className="settings-startup-choice">
                <Icon name="clock" className="settings-startup-choice__icon" aria-hidden="true"/>
                <span className="settings-startup-choice__copy"><strong>Continue where I left off</strong><small id="settings-startup-continue-description">Reopen your last space and document at your saved position.</small></span>
                <input type="radio" name="startup" aria-label="Continue where I left off" aria-describedby="settings-startup-continue-description" checked={startupMode === "continue"} onChange={() => setStartupMode("continue")}/>
              </label>
            </div>
            <p className="settings-panel__note" id="settings-startup-note">Your last space and document follow your account when cloud sync is available.</p>
          </section>

          <section className="settings-panel settings-preferences" aria-labelledby="settings-preferences-title">
            <header className="settings-panel__header">
              <span className="settings-panel__number" aria-hidden="true">05</span>
              <div><span>GENERAL</span><h4 id="settings-preferences-title">Preferences</h4></div>
              <small className="settings-startup__scope">Saved on this device</small>
            </header>
            <label className="settings-switch">
              <span className="settings-switch__copy"><strong>Use the document color</strong><small id="settings-document-color-description">Tint the full-screen editor with the color you chose for the document on the canvas.</small></span>
              <input type="checkbox" role="switch" aria-label="Use the document color in the editor" aria-describedby="settings-document-color-description" checked={documentColorInEditor} onChange={(event) => setDocumentColorInEditor(event.target.checked)}/>
            </label>
          </section>

          <section className="settings-data" aria-labelledby="settings-data-title">
            <header>
              <div><span>DATA &amp; SYNC</span><h4 id="settings-data-title">Where your work lives</h4></div>
              <span className={`settings-sync-state ${settingsSyncTone}`}><i/>{settingsSyncLabel}</span>
            </header>
            <div className="settings-data-grid">
              {authMode === "authenticated" && <article>
                <span className="settings-data-icon"><Icon name="cloud"/></span>
                <div><span>PRIVATE CLOUD</span><strong>{storageUsage ? `${formatStorage(storageUsage.usedBytes)} of ${formatStorage(storageUsage.limitBytes)}` : storageError ? "Usage unavailable" : "Checking usage…"}</strong><small>{storageUsage?.reservedBytes ? `${formatStorage(storageUsage.reservedBytes)} reserved by uploads.` : "Files and previews count toward this account."}</small></div>
                {cloudUsagePercent !== null && <span className="settings-usage" aria-label={`${cloudUsagePercent.toFixed(1)}% of cloud storage used`}><i style={{ width: `${cloudUsagePercent}%` }}/></span>}
              </article>}
              <button type="button" className="settings-data-entry settings-data-recovery" aria-label="Open recently deleted items" onClick={() => setRecentlyDeletedOpen(true)}>
                <span className="settings-data-icon"><Icon name="trash"/></span>
                <div><span>RECOVERY</span><strong>Recently deleted</strong><small>{recentlyDeletedCount === 0 ? "Nothing is waiting to be restored." : `${recentlyDeletedCount} ${recentlyDeletedCount === 1 ? "entry" : "entries"} available for 30 days.`}</small></div>
                <Icon name="arrow-right" className="settings-data-recovery__arrow"/>
              </button>
            </div>
          </section>

          <div className="settings-actions">
            <section className="settings-backup" aria-labelledby="settings-backup-title">
              <span className="settings-action-icon"><Icon name="download"/></span>
              <div><span>PORTABLE BACKUP</span><h4 id="settings-backup-title">Take your library with you.</h4><p>Export a ZIP with the manifest, Markdown notes, and clip metadata.</p></div>
              <button type="button" disabled={exporting} onClick={() => { setExporting(true); void downloadLibraryExport(store).finally(() => setExporting(false)); }}>{exporting ? "Preparing…" : "Export ZIP"}<Icon name="arrow-up-right"/></button>
            </section>

            {authMode === "authenticated" && onSignOut && <section className="settings-account">
              <div><span>ACCOUNT</span><strong>Finished here?</strong><small>Your inactive local profile stays on this device.</small></div>
              <Button tone="danger" disabled={signingOut} onClick={() => { setSigningOut(true); void Promise.resolve(onSignOut()).catch(() => setSigningOut(false)); }}>{signingOut ? "Signing out…" : "Sign out"}</Button>
            </section>}
          </div>
        </div>
      </Modal>
    </div>
    {keyboardShortcutsOpen && <KeyboardShortcutsDialog exiting={keyboardShortcutsExiting} onClose={requestKeyboardShortcutsExit}/>}
    {recentlyDeletedOpen && <RecentlyDeletedDialog exiting={recentlyDeletedExiting} spaces={spaces} itemsBySpace={itemsBySpace} saveSpaceChanges={saveSpaceChanges} saveItemChanges={saveItemChanges} permanentlyDelete={permanentlyDelete} onClose={requestRecentlyDeletedExit}/>}
  </>;
}

function formatStorage(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(0, bytes / 1024).toFixed(bytes > 0 ? 1 : 0)} KiB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`;
}

function KeyboardShortcutsDialog({ exiting, onClose }: { exiting: boolean; onClose: () => void }) {
  const shortcutBindings = useShell((state) => state.shortcutBindings);
  const setShortcutBinding = useShell((state) => state.setShortcutBinding);
  const resetShortcutBindings = useShell((state) => state.resetShortcutBindings);
  const [recordingShortcut, setRecordingShortcut] = useState<CanvasShortcutId | null>(null);
  const [shortcutError, setShortcutError] = useState("");

  useEffect(() => {
    if (!recordingShortcut) return;
    const record = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (event.key === "Escape") {
        setRecordingShortcut(null);
        setShortcutError("");
        return;
      }
      const binding = canvasShortcutBindingFromEvent(event);
      if (!binding) {
        setShortcutError("Press a letter, number, or action key. Escape cancels.");
        return;
      }
      if (isSystemClipboardPasteBinding(binding)) {
        setShortcutError("That shortcut is reserved for Paste from OS.");
        return;
      }
      const conflict = canvasShortcutConflict(shortcutBindings, recordingShortcut, binding);
      if (conflict) {
        const action = CANVAS_SHORTCUT_ACTIONS.find(({ id }) => id === conflict);
        setShortcutError(`That shortcut is already used by ${action?.label ?? "another action"}.`);
        return;
      }
      setShortcutBinding(recordingShortcut, binding);
      setRecordingShortcut(null);
      setShortcutError("");
    };
    window.addEventListener("keydown", record, { capture: true });
    return () => window.removeEventListener("keydown", record, { capture: true });
  }, [recordingShortcut, setShortcutBinding, shortcutBindings]);

  const groups = ["Create", "Edit", "Arrange", "View", "Text"] as const;
  const activeShortcutAction = recordingShortcut
    ? CANVAS_SHORTCUT_ACTIONS.find(({ id }) => id === recordingShortcut)
    : null;

  return <Modal title="Keyboard shortcuts" wide className={`shortcut-modal ${exiting ? "is-closing" : ""}`} onClose={onClose}>
    <div className={`shortcut-workspace ${recordingShortcut ? "is-recording" : ""}`} data-modal-escape-boundary={recordingShortcut ? "true" : undefined}>
      <aside className="shortcut-rail">
        <span className="shortcut-rail__eyebrow">COMMAND MAP</span>
        <h3>{recordingShortcut ? "Listening for a new shortcut." : "Tune the actions you use every day."}</h3>
        <p className="shortcut-rail__description">
          {recordingShortcut
            ? <>Press the new keys for <strong>{activeShortcutAction?.label ?? "this action"}</strong>. Escape cancels.</>
            : "Select any binding, then press its replacement. Your changes apply immediately."}
        </p>
        {shortcutError && <p className="shortcut-settings__error" role="status">{shortcutError}</p>}
        <div className="shortcut-rail__summary" aria-label="Shortcut summary">
          <span><strong>{CANVAS_SHORTCUT_ACTIONS.length}</strong><small>editable</small></span>
          <span><strong>1</strong><small>fixed</small></span>
        </div>
        <Button className="shortcut-rail__reset" onClick={() => { resetShortcutBindings(); setRecordingShortcut(null); setShortcutError(""); }}>Restore defaults</Button>
      </aside>

      <div className="shortcut-catalog">
        <header className="shortcut-catalog__header">
          <div><span>CANVAS CONTROLS</span><h3>Command atlas</h3></div>
          <p><kbd>Esc</kbd><span>cancels while recording</span></p>
        </header>
        <div className="shortcut-settings__groups">
          {groups.map((group) => {
            const groupActions = CANVAS_SHORTCUT_ACTIONS.filter((action) => action.group === group);
            const commandCount = groupActions.length + (group === "Edit" ? 1 : 0);
            return <section className="shortcut-settings__group" key={group}>
              <header><h3>{group}</h3><span>{commandCount} {commandCount === 1 ? "command" : "commands"}</span></header>
              <div>
                {groupActions.map((action) => {
                  const recording = recordingShortcut === action.id;
                  const current = canvasShortcutLabel(action.id, undefined, shortcutBindings);
                  return <Fragment key={action.id}>
                    <div className={`shortcut-setting ${recording ? "is-recording" : ""}`}>
                      <span><strong>{action.label}</strong><small>{action.description}</small></span>
                      <button
                        type="button"
                        className="shortcut-setting__recorder"
                        aria-label={`Change ${action.label} shortcut, current ${current}`}
                        aria-pressed={recording}
                        onClick={() => { setRecordingShortcut(action.id); setShortcutError(""); }}
                      >{recording ? <span>Press keys…</span> : <kbd>{current}</kbd>}</button>
                    </div>
                    {action.id === "paste" && <div className="shortcut-setting shortcut-setting--fixed" data-shortcut-id="paste-from-os">
                      <span><strong>{SYSTEM_CLIPBOARD_PASTE_ACTION.label}</strong><small>{SYSTEM_CLIPBOARD_PASTE_ACTION.description}</small></span>
                      <kbd className="shortcut-setting__fixed" aria-label={`${SYSTEM_CLIPBOARD_PASTE_ACTION.label}, fixed ${systemClipboardPasteShortcutLabel()}`} aria-keyshortcuts={systemClipboardPasteShortcutAria()}>{systemClipboardPasteShortcutLabel()}</kbd>
                    </div>}
                  </Fragment>;
                })}
              </div>
            </section>;
          })}
        </div>
      </div>
    </div>
  </Modal>;
}

function RecentlyDeletedDialog({ exiting, spaces, itemsBySpace, saveSpaceChanges, saveItemChanges, permanentlyDelete, onClose }: {
  exiting: boolean;
  spaces: Space[];
  itemsBySpace: Map<string, CanvasItem[]>;
  saveSpaceChanges: (space: Space, patch: Partial<Pick<Space, "title" | "theme" | "starred" | "deletedAt">>) => Promise<void>;
  saveItemChanges: SpaceScreenProps["saveItemChanges"];
  permanentlyDelete: (entityType: "space" | "item", entityId: string, baseRevision: number) => Promise<void>;
  onClose: () => void;
}) {
  const [confirmation, setConfirmation] = useState<{ entityType: "space" | "item"; entityId: string; title: string; baseRevision: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const trashedSpaces = spaces.filter((space) => space.deletedAt !== null).sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
  const trashed = [...itemsBySpace.values()].flat().filter((item) => item.deletedAt !== null).sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
  const total = trashedSpaces.length + trashed.length;

  const confirmDelete = () => {
    if (!confirmation || deleting) return;
    setDeleting(true);
    setDeleteError("");
    void permanentlyDelete(confirmation.entityType, confirmation.entityId, confirmation.baseRevision)
      .then(() => setConfirmation(null))
      .catch(() => setDeleteError("Bedrock could not permanently delete this entry. Please try again."))
      .finally(() => setDeleting(false));
  };

  return <>
    <div aria-hidden={confirmation ? true : undefined} inert={confirmation ? true : undefined}>
      <Modal title="Recently deleted" wide className={`trash-modal ${exiting ? "is-closing" : ""}`} onClose={() => confirmation ? setConfirmation(null) : onClose()}>
        <div className="trash-workspace">
      <aside className="trash-rail">
        <span>RECOVERY WINDOW</span>
        <h3>{total === 0 ? "Nothing is waiting." : `${total} ${total === 1 ? "entry is" : "entries are"} waiting.`}</h3>
        <p>Bedrock keeps deleted cards and spaces for 30 days. Restore anything you still need.</p>
        <div className="trash-rail__summary" aria-label="Recently deleted summary">
          <span><strong>{trashedSpaces.length}</strong><small>{trashedSpaces.length === 1 ? "space" : "spaces"}</small></span>
          <span><strong>{trashed.length}</strong><small>{trashed.length === 1 ? "item" : "items"}</small></span>
        </div>
      </aside>
      <section className="trash-catalog" aria-labelledby="trash-catalog-title">
        <header>
          <div><span>DELETED CONTENT</span><h3 id="trash-catalog-title">Recovery shelf</h3></div>
          {total > 0 && <small>Newest first</small>}
        </header>
        <div className="trash-list">
          {total === 0 && <div className="trash-empty"><StrataMark size={58}/><h2>Nothing buried here</h2><p>Deleted cards and spaces will wait here before permanent removal.</p></div>}
          {trashedSpaces.map((space) => <article key={space.id}><span className="trash-item-icon"><Icon name="grid"/></span><div><strong>{space.title}</strong><small>Space · deleted {new Date(space.deletedAt ?? 0).toLocaleDateString()}</small></div><span className="trash-item-actions"><Button onClick={() => void saveSpaceChanges(space, { deletedAt: null })}>Restore</Button><Button tone="danger" onClick={() => { setDeleteError(""); setConfirmation({ entityType: "space", entityId: space.id, title: space.title, baseRevision: space.updatedAt }); }}>Delete permanently</Button></span></article>)}
          {trashed.map((item) => {
            const title = "title" in item.props ? item.props.title : "filename" in item.props ? item.props.filename : "plainText" in item.props ? isLockedItem(item) ? "Locked note" : item.props.plainText.slice(0, 50) || "Untitled note" : item.type;
            const shownTitle = isSealedItem(item) ? "Locked item" : title;
            return <article key={item.id}><span className="trash-item-icon"><Icon name={canvasItemIcon(item)}/></span><div><strong>{shownTitle}</strong><small>{spaces.find((space) => space.id === item.spaceId)?.title ?? "Unknown space"} · deleted {new Date(item.deletedAt ?? 0).toLocaleDateString()}</small></div><span className="trash-item-actions"><Button onClick={() => void saveItemChanges(item, { deletedAt: null })}>Restore</Button><Button tone="danger" onClick={() => { setDeleteError(""); setConfirmation({ entityType: "item", entityId: item.id, title: shownTitle, baseRevision: item.updatedAt }); }}>Delete permanently</Button></span></article>;
          })}
        </div>
      </section>
        </div>
      </Modal>
    </div>
    {confirmation && <Modal title="Delete permanently?" className="permanent-delete-modal" overlayLayer={1020} onClose={() => { if (!deleting) setConfirmation(null); }}>
      <div className="permanent-delete-confirmation">
        <span className="permanent-delete-confirmation__icon"><Icon name="trash"/></span>
        <h3>Delete “{confirmation.title}” forever?</h3>
        <p>{confirmation.entityType === "space" ? "The space and everything inside it will be permanently deleted." : "This item will be permanently deleted."} This cannot be undone.</p>
        {deleteError && <p className="permanent-delete-confirmation__error" role="alert">{deleteError}</p>}
        <div><Button disabled={deleting} onClick={() => setConfirmation(null)}>Cancel</Button><Button tone="danger" disabled={deleting} onClick={confirmDelete}>{deleting ? "Deleting…" : "Delete permanently"}</Button></div>
      </div>
    </Modal>}
  </>;
}

async function downloadLibraryExport(store: LocalStore): Promise<void> {
  const blob = await exportLibrary(store);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `bedrock-library-${new Date().toISOString().slice(0, 10)}.bedrock.zip`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export type { CanvasShortcutAccelerators } from "./canvas-shortcuts";
